import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  Divider,
  Tooltip,
  Accordion,
  AccordionSummary,
  Collapse,
  AccordionDetails,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Backdrop,
  Snackbar,
  Alert,
  Switch,
  FormControlLabel,
  Badge,
  Link,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Description as ContractIcon,
  AttachMoney as MoneyIcon,
 
  ExpandMore as ExpandMoreIcon,
   Lock as LockIcon,
 
  PictureAsPdf as PdfIcon,
  FolderShared as DriveIcon,
  Share as ShareIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
// Firebase imports
import { db } from '../firebase/config';
import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  onSnapshot,
  serverTimestamp,
  where,
  writeBatch,
} from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { getClientAvailableBalance } from '../utils/clientBalanceService';
import { splitPartnerCompany } from '../utils/money';
import { useConfig } from '../contexts/ConfigContext';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import NumericField from './NumericField';
import { filterServiceOptions, frequencySearchText } from '../utils/serviceSearch';
import ucenaContractCoverBackground from '../assets/ucena-contract-cover-background.png';

// Import AP management utility for automatic AP record creation
import { postInvoicedVendorCredits, postPaidCredits, postPartialCredits, postCancellationReversals } from '../utils/contractsLedgerPosting';
import { applyMilestoneTransition } from '../utils/milestones';
import { useClientGrouping, groupByClient, GroupByClientToggle, ClientGroupHeaderRow } from './ClientGroups';

// Enhanced payment schedule statuses with immutability rules
const paymentStatuses = {
  PENDING: { label: 'Pending', color: 'default', immutable: false },
  INVOICED: { label: 'Invoiced', color: 'warning', immutable: true },
  PAID: { label: 'Paid', color: 'success', immutable: true },
  PARTIALLY_PAID: { label: 'Partially Paid', color: 'info', immutable: true },
  CANCELLED: { label: 'Cancelled', color: 'error', immutable: true },
};

// Allowed forward-only transitions for milestone payment statuses
const allowedMilestoneTransitions = {
  Scheduled: ['Invoiced', 'Skip'],
  Invoiced: ['Paid', 'Partial', 'Cancelled'],
  Paid: ['Cancelled'],
  Partial: ['Cancelled'],
  Cancelled: [],
  Skip: []
};

// Allowed forward-only transitions for payment schedule items (uppercase statuses)
const allowedPaymentScheduleTransitions = {
  PENDING: ['INVOICED'],
  INVOICED: ['PAID', 'PARTIALLY_PAID', 'CANCELLED'],
  PAID: ['CANCELLED'],
  PARTIALLY_PAID: ['CANCELLED'],
  CANCELLED: []
};

const normalizeScheduleStatus = (status) => {
  const s = String(status || '').toUpperCase();
  if (s === 'PARTIAL') return 'PARTIALLY_PAID';
  if (s === 'CANCELED') return 'CANCELLED';
  return s;
};

// Helper: compare monetary amounts by cents to avoid floating point equality issues
const toCentsLocal = (n) => Math.round((Number(n) || 0) * 100);
const amountsEqualByCents = (a, b) => toCentsLocal(a) === toCentsLocal(b);
const isPositiveByCents = (n) => toCentsLocal(n) > 0;
const lessThanByCents = (a, b) => toCentsLocal(a) < toCentsLocal(b);
// Round a number to 2 decimals in a consistent, cents-based way
const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

// Contract value = every month's invoice amount added up, regardless of milestone status
const getContractValue = (contract) => {
  const logs = Array.isArray(contract?.milestoneLogs) ? contract.milestoneLogs : [];
  if (logs.length === 0) return Number(contract?.totalAmount) || 0;
  return round2(logs.reduce((sum, m) => sum + (Number(m?.expectedAmount) || 0), 0));
};

// Derive expected commission for legacy milestones when missing.
// Uses existing milestone amounts to avoid changing expectedAmount.
const deriveExpectedCommission = (milestone) => {
  if (!milestone) return null;
  const expectedAmount = Number(milestone.expectedAmount);
  const expectedRevenueBase = Number(milestone.expectedRevenueBase);
  if (!Number.isFinite(expectedAmount) || !Number.isFinite(expectedRevenueBase)) return null;
  const expectedTransport = Number(milestone.expectedTransport || 0);
  const expectedProcessingFee = Number(milestone.expectedProcessingFee || 0);
  const baseAll = expectedAmount - expectedProcessingFee;
  if (!Number.isFinite(baseAll)) return null;
  const commission = baseAll - expectedRevenueBase - expectedTransport;
  if (!Number.isFinite(commission)) return null;
  return round2(Math.max(0, commission));
};

// (moved inside Contracts component to access state/config)

// Pricing helpers (unit price is stored INCLUSIVE of transport; UI edits show EX-transport)
const computeTransportComponent = (internalUnitCost, transportPercent) => {
  const internal = Math.max(0, Number(internalUnitCost) || 0);
  const pct = Number.isFinite(Number(transportPercent)) ? Number(transportPercent) : 0;
  return internal * (pct / 100);
};

const computeInclusiveUnitPrice = (internalUnitCost, marginPercent, transportPercent) => {
  const internal = Math.max(0, Number(internalUnitCost) || 0);
  const marginPct = Number.isFinite(Number(marginPercent)) ? Number(marginPercent) : 0;
  const transportComponent = computeTransportComponent(internal, transportPercent);
  const exTransport = internal * (1 + (marginPct / 100));
  return round2(Math.max(0, exTransport + transportComponent));
};

const computeInclusiveFromExTransport = (exTransportUnit, internalUnitCost, transportPercent) => {
  const base = Math.max(0, Number(exTransportUnit) || 0);
  const transportComponent = computeTransportComponent(internalUnitCost, transportPercent);
  return round2(Math.max(0, base + transportComponent));
};

const deriveExTransportUnitPrice = (unitPriceInclusive, internalUnitCost, transportPercent) => {
  const inclusive = Math.max(0, Number(unitPriceInclusive) || 0);
  const transportComponent = computeTransportComponent(internalUnitCost, transportPercent);
  return round2(Math.max(0, inclusive - transportComponent));
};

const computeMarginPctFromExTransport = (exTransportUnit, internalUnitCost) => {
  const internal = Math.max(0, Number(internalUnitCost) || 0);
  const base = Math.max(0, Number(exTransportUnit) || 0);
  if (internal <= 0) return 0;
  const raw = ((base / internal) - 1) * 100;
  return Math.round((Math.max(-100, Math.min(500, raw)) + Number.EPSILON) * 100) / 100;
};

// ========================================
// DATA CONSISTENCY HELPERS
// ========================================

/**
 * Check if a service is active for a given month.
 * 
 * A service is considered ACTIVE for a month if:
 * - No cancellation date is set, OR
 * - The month is BEFORE the cancellation effective month
 * 
 * Boundary behavior: If ym === cancellationEffectiveYm, the service is CANCELLED
 * (i.e., the cancellation takes effect AT THE START of that month)
 * 
 * @param {Object} service - Service object with cancellationEffectiveYm field
 * @param {string} ym - Year-month string in YYYY-MM format
 * @returns {boolean} True if service is active for this month
 */
const isServiceActiveForMonth = (service, ym) => {
  if (!service) {
    console.warn('[isServiceActiveForMonth] Service is null/undefined');
    return false;
  }
  
  // No cancellation date = always active
  if (!service.cancellationEffectiveYm) return true;
  
  // Service is active if month is BEFORE cancellation month
  // String comparison works for YYYY-MM format: "2026-01" < "2026-02" = true
  const isActive = String(ym) < String(service.cancellationEffectiveYm);
  
  console.log('[isServiceActiveForMonth]', {
    serviceName: service.name || service.serviceName,
    ym,
    cancellationEffectiveYm: service.cancellationEffectiveYm,
    isActive
  });
  
  return isActive;
};

/**
 * Validate contract data consistency before save.
 * 
 * Checks:
 * - Milestone amounts are valid after Invoiced
 * - One-time services have milestone IDs when applied
 * - Cancelled services don't have future invoiced milestones
 * - Sum of applied amounts is reasonable
 * 
 * @param {Object} contract - Contract object to validate
 * @returns {Object} { valid: boolean, errors: string[], warnings: string[] }
 */
const validateContractConsistency = (contract) => {
  const errors = [];
  const warnings = [];
  
  if (!contract) {
    errors.push('Contract is null/undefined');
    return { valid: false, errors, warnings };
  }
  
  const milestoneLogs = Array.isArray(contract.milestoneLogs) ? contract.milestoneLogs : [];
  const services = Array.isArray(contract.services) ? contract.services : [];
  
  // 1. Check milestone amounts for non-Scheduled milestones
  const nonScheduledMilestones = milestoneLogs.filter(m => 
    m && m.status && m.status !== 'Scheduled' && m.status !== 'Skip'
  );
  
  for (const m of nonScheduledMilestones) {
    if (typeof m.expectedAmount !== 'number' || m.expectedAmount <= 0) {
      errors.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) has invalid expectedAmount: ${m.expectedAmount}`);
    }
    const hasExpectedCommission = Object.prototype.hasOwnProperty.call(m, 'expectedCommission');
    if (!hasExpectedCommission || m.expectedCommission === null || m.expectedCommission === undefined) {
      warnings.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) missing expectedCommission (will default to 0 on save)`);
    } else if (typeof m.expectedCommission !== 'number' || !Number.isFinite(m.expectedCommission)) {
      errors.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) has invalid expectedCommission: ${m.expectedCommission}`);
    }
    if (typeof m.expectedRevenueBase !== 'number') {
      const hasExpectedAmount = Number.isFinite(Number(m.expectedAmount)) && Number(m.expectedAmount) > 0;
      if (hasExpectedAmount) {
        warnings.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) missing expectedRevenueBase (will derive on save if possible)`);
      } else {
        errors.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) missing expectedRevenueBase`);
      }
    }
    if (typeof m.expectedProcessingFee !== 'number') {
      warnings.push(`Milestone ${m.id} (${m.expectedDate}, ${m.status}) missing expectedProcessingFee`);
    }
  }
  
  // 2. Check one-time services have milestone IDs when applied
  const oneTimeApplied = services.filter(s => s && s.oneTimeApplied);
  for (const s of oneTimeApplied) {
    if (!s.oneTimeAppliedMilestoneId) {
      warnings.push(`One-time service "${s.name || s.serviceName}" marked applied but no milestone ID stored`);
    }
  }
  
  // 3. Check cancelled services don't contribute to future invoiced milestones
  const cancelledServices = services.filter(s => s && s.cancellationEffectiveYm);
  for (const s of cancelledServices) {
    const futureInvoiced = milestoneLogs.filter(m => 
      m && 
      ['Invoiced', 'Paid', 'Partial'].includes(m.status) && 
      m.expectedDate && 
      String(m.expectedDate) >= String(s.cancellationEffectiveYm)
    );
    
    if (futureInvoiced.length > 0) {
      warnings.push(
        `Service "${s.name || s.serviceName}" cancelled effective ${s.cancellationEffectiveYm} ` +
        `but has ${futureInvoiced.length} invoiced/paid milestones after that date. ` +
        `This may be intentional if the service was invoiced before cancellation.`
      );
    }
  }
  
  // 4. Check for duplicate milestone IDs
  const milestoneIds = milestoneLogs.map(m => m && m.id).filter(Boolean);
  const uniqueIds = new Set(milestoneIds);
  if (milestoneIds.length !== uniqueIds.size) {
    errors.push('Duplicate milestone IDs detected');
  }
  
  // 5. Check milestones have expected dates
  const milestonesWithoutDates = milestoneLogs.filter(m => m && !m.expectedDate);
  if (milestonesWithoutDates.length > 0) {
    errors.push(`${milestonesWithoutDates.length} milestones missing expectedDate`);
  }
  
  const result = {
    valid: errors.length === 0,
    errors,
    warnings
  };
  
  console.log('[validateContractConsistency]', {
    contractId: contract.id,
    ...result
  });
  
  return result;
};

// Transport fees by country (percentages)
const transportFees = {
  'AM': { rate: 2.0, name: 'Armenia' },
  'US': { rate: 0.0, name: 'United States' },
  'GB': { rate: 1.5, name: 'United Kingdom' },
  'DE': { rate: 1.0, name: 'Germany' },
  'FR': { rate: 1.0, name: 'France' },
  'IN': { rate: 3.0, name: 'India' },
  'CN': { rate: 2.5, name: 'China' },
  'default': { rate: 1.5, name: 'Other' }
};

// Entity types that can receive AP payments
const payableEntityTypes = ['vendor', 'company', 'partner'];

// Partner positions with enhanced validation (0-20% constraint)
const partnerPositions = [
  { id: 'lead', name: 'Lead', defaultPercent: 5, description: 'Brought in the lead', editable: false, maxPercent: 10 },
  { id: 'sales', name: 'Sales', defaultPercent: 5, description: 'Closed the deal, sales lead', editable: false, maxPercent: 10 },
  { id: 'support', name: 'Sales Support', defaultPercent: 3, description: 'Helped close the deal', editable: false, maxPercent: 10 },
  { id: 'client_pm', name: 'Client Relations', defaultPercent: 5, description: 'Manages the Client Relations', editable: false, maxPercent: 10 },
  { id: 'team_pm', name: 'Team Project Manager', defaultPercent: 5, description: 'Manages the team and the project', editable: false, maxPercent: 10 },
  { id: 'administration', name: 'Administration', defaultPercent: 5, description: 'Billing, management, detail flow, advice', editable: false, maxPercent: 10 },
  { id: 'company', name: 'Company', defaultPercent: 7, description: 'Company taxes and fees', editable: false, maxPercent: 10 },
];

const contractStatuses = ['Draft', 'Ready', 'Active', 'Inactive'];

// Allowed forward-only transitions between contract statuses
const allowedContractTransitions = {
  Draft: ['Ready'],
  Ready: ['Active'],
  Active: ['Inactive'],
  Inactive: []
};

const canTransitionContractStatus = (fromStatus, toStatus, isAdmin = false) => {
  // Always allow transitioning to Inactive from any status
  if (toStatus === 'Inactive') return true;
  const from = fromStatus || 'Draft';
  if (from === toStatus) return true;
  // Only admins may reactivate an inactive contract, either through Ready or directly to Active.
  if (from === 'Inactive' && (toStatus === 'Ready' || toStatus === 'Active')) return isAdmin;
  const allowed = allowedContractTransitions[from] || [];
  return allowed.includes(toStatus);
};

const compareTableValues = (left, right) => {
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  const leftText = String(left ?? '').trim();
  const rightText = String(right ?? '').trim();
  if (!leftText && rightText) return 1;
  if (leftText && !rightText) return -1;
  return leftText.localeCompare(rightText, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
};

const DEFAULT_SCHEDULED_RECEIVABLE_TIMING = 'current_month';
const scheduledReceivableTimingOptions = [
  { value: 'current_month', label: 'Start of service month' },
  { value: 'next_month', label: 'Start of following month' }
];

const normalizeScheduledReceivableTiming = (value) => (
  value === 'next_month' ? 'next_month' : DEFAULT_SCHEDULED_RECEIVABLE_TIMING
);

// Service modification types
const serviceModificationTypes = {
  ADD: 'add',
  REMOVE: 'remove',
  MODIFY: 'modify'
};

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Contracts() {
  const navigate = useNavigate();
  const location = useLocation();
  const { contractId: routeContractId } = useParams();
  const { currentUser, userProfile, isSuperAdmin, isMember } = useAuth();
  const isSa = (typeof isSuperAdmin === 'function') ? isSuperAdmin() : !!isSuperAdmin;
  const { getCountryOptions, getCurrencyOptions, getPdfTexts, getFinanceConfig, getBrandingConfig } = useConfig();
  const pdfTexts = getPdfTexts();
  const financeConfig = getFinanceConfig();
  const creditCardProcessingPercent = Math.max(0, Math.min(100, Number(financeConfig?.creditCardProcessingPercent || 0)));
  const checkProcessingPercent = Math.max(0, Math.min(100, Number(financeConfig?.checkProcessingPercent || 0)));
  const getProcessingPercentForMethod = (method) => {
    const m = String(method || 'Transfer').toLowerCase();
    if (m === 'credit card' || m === 'creditcard' || m === 'credit_card') return creditCardProcessingPercent;
    if (m === 'check') return checkProcessingPercent;
    return 0;
  };
  const backfillMissingExpectedRevenueBase = (milestoneLogs, defaultPaymentMethod) => {
    if (!Array.isArray(milestoneLogs)) return { milestones: [], changed: false };
    let changed = false;
    const pct = getProcessingPercentForMethod(defaultPaymentMethod);
    const milestones = milestoneLogs.map((m) => {
      if (!m || typeof m.expectedRevenueBase === 'number') return m;
      const expectedAmount = Number(m.expectedAmount);
      if (!Number.isFinite(expectedAmount) || expectedAmount <= 0) return m;
      const expectedCommission = Number.isFinite(Number(m.expectedCommission)) ? Number(m.expectedCommission) : 0;
      const expectedTransport = Number.isFinite(Number(m.expectedTransport)) ? Number(m.expectedTransport) : 0;
      const hasProcessingFee = Number.isFinite(Number(m.expectedProcessingFee));
      let baseAll = null;
      let processingFeeDerived = null;
      if (hasProcessingFee) {
        const fee = Number(m.expectedProcessingFee);
        baseAll = expectedAmount - fee;
      } else {
        baseAll = pct > 0 ? expectedAmount / (1 + (pct / 100)) : expectedAmount;
        processingFeeDerived = round2(expectedAmount - baseAll);
      }
      if (!Number.isFinite(baseAll) || baseAll <= 0) return m;
      const expectedRevenueBase = round2(baseAll - expectedCommission - expectedTransport);
      if (!Number.isFinite(expectedRevenueBase)) return m;
      const next = { ...m, expectedRevenueBase: Math.max(0, expectedRevenueBase) };
      if (!hasProcessingFee && Number.isFinite(processingFeeDerived)) {
        next.expectedProcessingFee = processingFeeDerived;
      }
      changed = true;
      return next;
    });
    return { milestones, changed };
  };

  // Get configuration options
  const countries = getCountryOptions();
  const currencies = getCurrencyOptions();

  const [contracts, setContracts] = useState([]);
  const [filteredContracts, setFilteredContracts] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [entities, setEntities] = useState([]);
  const [services, setServices] = useState([]); // Master Service List
  const [costs, setCosts] = useState([]); // Master Costs (services) from proposals
  const [openDialog, setOpenDialog] = useState(false);
  const [editingContract, setEditingContract] = useState(null);
  const [currentTab, setCurrentTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [operationInProgress, setOperationInProgress] = useState(false);
  const [showServiceModification, setShowServiceModification] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [filters, setFilters] = useState({
    status: '',
    client: '',
    search: ''
  });
  const [contractSort, setContractSort] = useState({ field: 'client', direction: 'asc' });
  const [clientAvailableBalance, setClientAvailableBalance] = useState(0);
  const openedContractIdRef = useRef(null);

  // Only show clients that have at least one Accepted proposal
  const clientsWithAcceptedProposals = useMemo(() => {
    try {
      const acceptedClientIds = new Set((Array.isArray(proposals) ? proposals : [])
        .map((p) => p?.client_id)
        .filter(Boolean));
      return (Array.isArray(clients) ? clients : []).filter((c) => acceptedClientIds.has(c.id));
    } catch {
      return clients;
    }
  }, [clients, proposals]);

  // Compute effective available for a client from current state:
  // ledger available minus sum of applied amounts across all open (Invoiced/Partial) milestones.
  const getEffectiveClientAvailable = (clientId, overrideMilestoneLogs) => {
    if (!clientId) return 0;
    const base = Number((clientId === formData.clientEntityId) ? (clientAvailableBalance || 0) : 0);
    let deduction = 0;
    const deductionDetails = [];
    const overrideArray = Array.isArray(overrideMilestoneLogs) ? overrideMilestoneLogs : null;
    const applyLogsDeduction = (logs = [], contractId) => {
      logs.forEach((m) => {
        const status = String(m?.status || '');
        if (status === 'Invoiced' || status === 'Partial') {
          const applied = Number(m?.appliedAmount || 0);
          deduction += applied;
          deductionDetails.push({ contractId, milestoneId: m?.id, status, amount: applied });
        } else if (status === 'Paid' && m.clientBalanceTracked === true) {
          const expected = Number(m?.expectedAmount || 0);
          const actual = Number(m?.actualAmount || 0);
          const paidBase = actual > 0 ? Math.min(actual, expected) : expected;
          deduction += paidBase;
          deductionDetails.push({ contractId, milestoneId: m?.id, status, amount: paidBase });
        }
      });
    };
    (Array.isArray(contracts) ? contracts : []).forEach((ct) => {
      if (ct.clientEntityId !== clientId) return;
      const logsForContract = (editingContract && ct.id === editingContract.id)
        ? (overrideArray || (Array.isArray(formData.milestoneLogs) ? formData.milestoneLogs : []))
        : (Array.isArray(ct.milestoneLogs) ? ct.milestoneLogs : []);
      applyLogsDeduction(logsForContract, ct.id);
    });
    if ((!editingContract || !(Array.isArray(contracts) && contracts.some((ct) => ct.id === editingContract.id))) &&
        clientId === formData.clientEntityId &&
        overrideArray) {
      applyLogsDeduction(overrideArray, 'new-contract');
    }
    const result = Math.max(0, Number((base - deduction).toFixed(2)));
    
    // 🔍 RACE CONDITION LOG: Track available balance calculation
    console.log('[Contracts][getEffectiveClientAvailable] CALCULATED:', {
      timestamp: new Date().toISOString(),
      clientId,
      base,
      deduction,
      result,
      hasOverride: !!overrideArray,
      editingContractId: editingContract?.id,
      deductionCount: deductionDetails.length,
      deductionDetails
    });
    
    return result;
  };

  const getContractDirectPath = (contractId) => `/contracts/${encodeURIComponent(contractId)}`;

  const buildContractDirectLink = (contractId) => {
    const origin = window.location.origin;
    return `${origin}${getContractDirectPath(contractId)}`;
  };

  // Auto-open when navigated with a contract id via route, state, or query string
  useEffect(() => {
    const params = new URLSearchParams(location?.search || '');
    const fromQuery = params.get('openContractId');
    const maybeId = routeContractId || (location?.state && location.state.openContractId) || fromQuery;
    if (!maybeId) {
      openedContractIdRef.current = null;
      return;
    }
    if (maybeId && Array.isArray(contracts) && contracts.length > 0) {
      if (openedContractIdRef.current === maybeId && openDialog && editingContract?.id === maybeId) {
        return;
      }
      const found = contracts.find(c => c.id === maybeId);
      // Members cannot open Inactive contracts, even from a direct link
      if (found && !(isMember() && found.status === 'Inactive')) {
        openedContractIdRef.current = maybeId;
        handleOpenDialog(found);
      } else if (!loading) {
        setSnackbar({ open: true, message: 'Contract link not found', severity: 'warning' });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeContractId, location?.state, location?.search, contracts, loading, openDialog, editingContract?.id]);
  const [formData, setFormData] = useState({
    proposalId: '',
    title: '',
    status: 'Draft',
    clientEntityId: '',
    beneficiaryCompanyId: '',
    currency: 'USD',
    bankingInfo: {
      accountNumber: '',
      routingNumber: '',
      bankName: '',
      swiftCode: ''
    },
    partnerRoles: [],
    paymentSchedule: [], // Enhanced payment schedule
    services: [], // Contract services with entity mapping
    transportFees: [], // Separate transport fee line items
    milestoneLogs: [],
    signedDate: '',
    startDate: '',
    endDate: '',
    notes: '',
    totalAmount: 0,
    totalTransportFees: 0,
    serviceModifications: [], // Track service changes
    financialTriggers: [], // AR/AP/Ledger integration points
    createdBy: '',
    beneficiaryLocked: false, // Immutability flag
    startDateLocked: false,
    // Signatory fields
    clientSignatoryName: '',
    clientSignatoryTitle: '',
    // Default payment method for processing fees: 'Check' | 'Transfer' | 'Credit Card'
    defaultPaymentMethod: 'Transfer',
    scheduledReceivableTiming: DEFAULT_SCHEDULED_RECEIVABLE_TIMING
  });

  useEffect(() => {
    const loadClientBalance = async () => {
      try {
        if (!formData.clientEntityId) {
          setClientAvailableBalance(0);
          return;
        }
        const bal = await getClientAvailableBalance(formData.clientEntityId);
        setClientAvailableBalance(bal);
      } catch (e) {
        // ignore
      }
    };
    loadClientBalance();
  }, [formData.clientEntityId]);

  // Live update Client Available when client balance entries change
  useEffect(() => {
    if (!formData.clientEntityId) return;
    try {
      const qcb = query(collection(db, 'client_balance_entries'), where('clientId', '==', formData.clientEntityId));
      const unsub = onSnapshot(qcb, (snap) => {
        let sum = 0;
        snap.forEach((d) => { sum += Number(d.data()?.amountUsd || 0); });
        setClientAvailableBalance(sum);
      });
      return () => { if (typeof unsub === 'function') unsub(); };
    } catch {}
  }, [formData.clientEntityId]);

  // Financial calculation engine
  const calculateTransportFee = (amount, countryCode) => {
    const feeConfig = transportFees[countryCode] || transportFees.default;
    return amount * (feeConfig.rate / 100);
  };

  // Normalize various date input formats to a JS Date for Firestore
  // Store at 12:00:00 UTC to avoid timezone day-shift for date-only values
  const toFirestoreDate = (value) => {
    if (!value) return null;
    // Firestore Timestamp-like
    if (typeof value?.toDate === 'function') {
      const d = value.toDate();
      if (!(d instanceof Date) || isNaN(d.getTime())) return null;
      return new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate(), 12, 0, 0));
    }
    // Already a Date
    if (value instanceof Date && !isNaN(value.getTime())) {
      return new Date(Date.UTC(value.getFullYear(), value.getMonth(), value.getDate(), 12, 0, 0));
    }
    // ISO date string from inputs (YYYY-MM or YYYY-MM-DD)
    if (typeof value === 'string') {
      try {
        const s = String(value).trim();
        if (s.length === 7) {
          const [y, m] = s.split('-').map((x) => Number(x));
          if (!Number.isFinite(y) || !Number.isFinite(m)) return null;
          return new Date(Date.UTC(y, m - 1, 1, 12, 0, 0));
        }
        if (s.length >= 10) {
          const [y, m, d] = s.slice(0, 10).split('-').map((x) => Number(x));
          if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
          return new Date(Date.UTC(y, m - 1, d, 12, 0, 0));
        }
      } catch {}
    }
    return null;
  };

  // Generate YYYY-MM strings for each month between start and end (inclusive)
  const listMonthlyPeriods = (startDateStr, endDateStr) => {
    if (!startDateStr || !endDateStr) return [];
    const start = new Date(startDateStr);
    const end = new Date(endDateStr);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    // Normalize to first day of month
    const current = new Date(Date.UTC(start.getUTCFullYear(), start.getUTCMonth(), 1));
    const last = new Date(Date.UTC(end.getUTCFullYear(), end.getUTCMonth(), 1));
    const months = [];
    while (current.getTime() <= last.getTime()) {
      const y = current.getUTCFullYear();
      const m = String(current.getUTCMonth() + 1).padStart(2, '0');
      months.push(`${y}-${m}`);
      // advance 1 month UTC-safe
      current.setUTCMonth(current.getUTCMonth() + 1);
    }
    return months;
  };

  // Format a Date to YYYY-MM (UTC)
  const formatYearMonth = (dateObj) => {
    if (!(dateObj instanceof Date) || isNaN(dateObj.getTime())) return '';
    const y = dateObj.getUTCFullYear();
    const m = String(dateObj.getUTCMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  };

  // Get next month as YYYY-MM (UTC), relative to now or a given date
  const getNextMonthYm = (fromDate = new Date()) => {
    const next = new Date(Date.UTC(fromDate.getUTCFullYear(), fromDate.getUTCMonth() + 1, 1));
    return formatYearMonth(next);
  };

  // Compute proposal-parity totals matching the proposal's calculateTotalAmount logic
  const computeProposalParityTotals = (base) => {
      const costs = Array.isArray(base?.proposalSnapshot?.costs) ? base.proposalSnapshot.costs : null;
      
      // Helper to get transport percent matching proposal logic (vendor country, not client)
      const getTransportPercentForCost = (cost) => {
        if (typeof cost.transport_percent === 'number') return cost.transport_percent;
        const vendor = entities.find(e => e.id === (cost.entity_id || cost.vendorEntityId || cost.vendorId));
        const country = vendor ? countries.find(c => c.code === vendor.country) : null;
        const pct = Number(country?.transportPercent) || 0;
        return pct < 0 ? 0 : pct;
      };

      const roles = Array.isArray(base?.partnerRoles) ? base.partnerRoles : [];
      const totalPartnerPercent = roles.reduce((sum, r) => sum + (Number(r?.percent) || 0), 0);

      const method = String(base?.defaultPaymentMethod || '').toLowerCase();
      const processingPercentLocal = (method === 'credit card' || method === 'creditcard' || method === 'credit_card')
        ? creditCardProcessingPercent
        : (method === 'check' ? checkProcessingPercent : 0);

      // Calculate exactly like proposal does - no intermediate rounding
      let monthlyBaseTotal = 0;
      let oneTimeBaseTotal = 0;
      let monthlyInternalTotal = 0;
      let oneTimeInternalTotal = 0;

      if (costs && costs.length > 0) {
        // Use proposal costs directly
        costs.forEach((cost) => {
          const internal = Number(cost.internal_cost) || 0;
          const marginPct = Number(cost.margin_percent) || 0;
          const transportPct = getTransportPercentForCost(cost);
          const quantity = Number(cost.quantity) || 1;
          
          // Match proposal calculation exactly
          const clientPrice = internal * (1 + (marginPct / 100)) + internal * (transportPct / 100);
          const lineTotal = clientPrice * quantity;
          const internalTotal = internal * quantity;
          
          if (normalizeFrequencyValue(cost.frequency || 'one_time') === 'monthly') {
            monthlyBaseTotal += lineTotal;
            monthlyInternalTotal += internalTotal;
          } else {
            oneTimeBaseTotal += lineTotal;
            oneTimeInternalTotal += internalTotal;
          }
        });
      } else if (Array.isArray(base?.services)) {
        // Fallback to services - respect oneTimeApplied flag
        base.services.forEach((s) => {
          const internal = Number(s?.internalUnitCost) || 0;
          const unit = Number(s?.unitPrice) || 0;
          const quantity = Number(s?.quantity) || 1;
          
          // For services, transport is already baked into unit price
          const lineTotal = unit * quantity;
          const internalTotal = internal * quantity;
          
          if (normalizeFrequencyValue(s?.frequency || 'one-time') === 'monthly') {
            monthlyBaseTotal += lineTotal;
            monthlyInternalTotal += internalTotal;
          } else {
            // Only include one-time services that haven't been applied yet
            if (!s?.oneTimeApplied) {
              oneTimeBaseTotal += lineTotal;
              oneTimeInternalTotal += internalTotal;
            }
          }
        });
      }

      // Calculate uplift
      const monthlyUplift = monthlyInternalTotal * (totalPartnerPercent / 100);
      const oneTimeUplift = oneTimeInternalTotal * (totalPartnerPercent / 100);

      // Proposal totals (no processing)
      const monthlyProposalTotal = monthlyBaseTotal + monthlyUplift;
      const oneTimeProposalTotal = oneTimeBaseTotal + oneTimeUplift;

      // Add processing for contract
      const monthlyProcessing = monthlyProposalTotal * (processingPercentLocal / 100);
      const oneTimeProcessing = oneTimeProposalTotal * (processingPercentLocal / 100);

      const monthlyAll = monthlyProposalTotal + monthlyProcessing;
      const oneTimeAll = oneTimeProposalTotal + oneTimeProcessing;

      return {
        monthlyBase: monthlyBaseTotal,
        oneTimeBase: oneTimeBaseTotal,
        monthlyInternal: monthlyInternalTotal,
        oneTimeInternal: oneTimeInternalTotal,
        monthlyCommission: monthlyUplift,
        oneTimeCommission: oneTimeUplift,
        monthlyTransport: 0, // Already included in base via unit prices
        oneTimeTransport: 0, // Already included in base via unit prices
        monthlyProcessing,
        oneTimeProcessing,
        monthlyAll,
        oneTimeAll,
      };
  };

  // Build client payment milestones by month from the payment schedule and dates
  // Respects per-service cancellationEffectiveYm: a monthly service contributes only while ym < cancellationEffectiveYm
  // ✅ FIX: servicesArg is now REQUIRED - no fallback to formData.services to avoid stale closures
  const generateMonthlyMilestonesFromSchedule = (schedule, partnerRoles = [], startDateStr, endDateStr, defaultPaymentMethodArg, servicesArg) => {
    const safeSchedule = Array.isArray(schedule) ? schedule : [];
    const months = listMonthlyPeriods(startDateStr, endDateStr);
    if (months.length === 0) return [];

    const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';

    // ✅ FIX: Validate services parameter is provided (no more fallback)
    if (!Array.isArray(servicesArg)) {
      console.error('[generateMonthlyMilestonesFromSchedule] ERROR - servicesArg not provided or not an array!', {
        timestamp: new Date().toISOString(),
        servicesArg,
        typeof: typeof servicesArg
      });
      throw new Error('servicesArg is required and must be an array. Do not rely on formData.services closure.');
    }
    const servicesArray = servicesArg;
    
    // Helpers for internal base lookup
    const findServiceById = (sid) => servicesArray.find((s) => s && s.id === sid) || null;
    const getItemInternalUnit = (item) => {
      const direct = Number(item?.serviceInternalUnitCost);
      if (Number.isFinite(direct)) return direct;
      const svc = findServiceById(item?.serviceId);
      return Number(svc?.internalUnitCost) || 0;
    };
    const getItemQuantity = (item) => {
      const direct = Number(item?.serviceQuantity);
      if (Number.isFinite(direct) && direct > 0) return direct;
      const svc = findServiceById(item?.serviceId);
      return Number(svc?.quantity) || 0;
    };
    const isItemOneTime = (item) => !isMonthly(item?.frequency);
    const isOneTimeAppliedForItem = (item) => {
      if (!isItemOneTime(item)) return false;
      const svc = findServiceById(item?.serviceId);
      return !!svc?.oneTimeApplied;
    };

    // One-time revenue and internal base billed in the first month
    const oneTimeRevenueAmount = safeSchedule
      .filter((i) => i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedForItem(i))
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);

    const oneTimeInternalAmount = safeSchedule
      .filter((i) => i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedForItem(i))
      .reduce((sum, i) => sum + ((getItemInternalUnit(i) || 0) * (getItemQuantity(i) || 0)), 0);

    const transportOneTimeAmount = 0; // transport included in service amounts

    // Partner commission percent (include company role)
    const partnerPercent = (Array.isArray(partnerRoles) ? partnerRoles : [])
      .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0);

    // If nothing to bill at all, return empty
    const anyMonthlyExists = safeSchedule.some((i) => i && i.type === 'Service' && isMonthly(i.frequency));
    if (!anyMonthlyExists && oneTimeRevenueAmount === 0 && transportOneTimeAmount === 0) return [];

    // Processing percent based on default payment method
    const methodLocal = String(defaultPaymentMethodArg || formData?.defaultPaymentMethod || '').toLowerCase();
    const processingPercentLocal = (methodLocal === 'credit card' || methodLocal === 'creditcard' || methodLocal === 'credit_card')
      ? creditCardProcessingPercent
      : (methodLocal === 'check' ? checkProcessingPercent : 0);

    const milestones = months.map((ym, idx) => {
      // ✅ FIX: Use isServiceActiveForMonth helper for consistent cancellation filtering
      const monthlyActive = safeSchedule
        .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency) && isServiceActiveForMonth(i, ym));
      
      // Debug logging to track cancellation filtering
      console.log('[generateMonthlyMilestonesFromSchedule] Milestone calculation:', {
        ym,
        allMonthlyServices: safeSchedule.filter((i) => i && i.type === 'Service' && isMonthly(i.frequency)).map(i => ({
          name: i.serviceName,
          amount: i.amount,
          cancellationEffectiveYm: i.cancellationEffectiveYm,
          included: isServiceActiveForMonth(i, ym)
        })),
        monthlyActiveCount: monthlyActive.length,
        monthlyActiveServices: monthlyActive.map(i => ({ name: i.serviceName, amount: i.amount }))
      });
      
      const expectedRevenueBaseMonthly = monthlyActive.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const monthlyInternal = monthlyActive.reduce((sum, i) => sum + ((getItemInternalUnit(i) || 0) * (getItemQuantity(i) || 0)), 0);
      const partnerPercent = (Array.isArray(partnerRoles) ? partnerRoles : [])
        .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0) / 100;
      let expectedRevenueBase = round2(expectedRevenueBaseMonthly);
      let expectedTransport = 0; // included in unit price
      let expectedCommission = round2(monthlyInternal * partnerPercent);
      // Add one-time components in first month only
      if (idx === 0) {
        expectedRevenueBase = round2(expectedRevenueBase + oneTimeRevenueAmount);
        expectedCommission = round2(expectedCommission + (oneTimeInternalAmount * partnerPercent));
      }
      
      // 🔧 SAFETY: Ensure expectedCommission is always a valid number (never NaN or undefined)
      if (!Number.isFinite(expectedCommission)) {
        console.warn('[generateMonthlyMilestonesFromSchedule] Invalid expectedCommission calculated, defaulting to 0:', {
          expectedCommission,
          monthlyInternal,
          oneTimeInternalAmount: idx === 0 ? oneTimeInternalAmount : 0,
          partnerPercent,
          partnerRoles
        });
        expectedCommission = 0;
      }
      
      const baseAll = expectedRevenueBase + expectedCommission + expectedTransport;
      const expectedProcessingFee = round2(baseAll * (processingPercentLocal / 100));
      const expectedAmount = round2(baseAll + expectedProcessingFee);

      return {
        id: Date.now() + idx,
        milestone: String(idx + 1),
        expectedDate: ym,
        actualDate: '',
        invoiceId: '',
        invoiceUrl: '',
        expectedRevenueBase,
        expectedTransport,
        expectedCommission,
        expectedAmount,
        expectedProcessingFee,
        actualAmount: 0,
        appliedAmount: 0,
        status: 'Scheduled',
        financialTriggers: []
      };
    });

    return milestones;
  };

  const calculateMargin = (internalCost, marginPercent) => {
    return internalCost * (marginPercent / 100);
  };

  const calculatePartnerCommission = (amount, commissionPercent) => {
    return amount * (Math.max(0, Math.min(20, commissionPercent)) / 100);
  };

  // Enhanced payment schedule generation with services (transport already in unit price)
  const generateEnhancedPaymentSchedule = (contractServices, partnerRoles, clientCountry) => {
    const schedule = [];
    let scheduleId = 1;
    
    contractServices.forEach((service, serviceIndex) => {
      const serviceAmountCents = toCentsLocal((Number(service.unitPrice) || 0) * (Number(service.quantity) || 0));
      const serviceAmount = serviceAmountCents / 100; // includes margin AND transport, cents-rounded
      // Transport is already included in unitPrice from proposal calculation
      const transportFee = 0; // No separate transport calculation needed
      const partnerRolesSnapshot = (partnerRoles || []).map(r => ({ entityId: r.entityId, percent: r.percent, positionId: r.positionId, subAccountId: r.subAccountId }));
      const totalPartnerPercent = partnerRolesSnapshot.reduce((s, r) => s + (Number(r.percent) || 0), 0) / 100;
      const targets = splitPartnerCompany({ lineAmountUsd: serviceAmount, totalPartnerPercent });
      
      // Main service payment line
      const serviceLineItem = {
        id: `service-${scheduleId++}`,
        type: 'Service',
        serviceId: service.id,
        serviceName: service.name,
        description: `${service.name} (Qty: ${service.quantity})`,
        amount: round2(serviceAmount),
        transportFeeUsd: round2(transportFee), // Always 0 now, kept for backward compatibility
        // Carry internal and quantity for downstream commission math
        serviceInternalUnitCost: Number(service.internalUnitCost) || 0,
        serviceQuantity: Number(service.quantity) || 0,
        partnerRolesSnapshot,
        partnerComponentTargetUsd: targets.partnerUsd,
        companyComponentTargetUsd: targets.companyUsd, // No separate transport to add
        frequency: service.frequency || 'one-time',
        status: 'PENDING',
        category: service.category,
        entityId: service.assignedEntityId,
        subAccountId: service.subAccountId,
        cancellationEffectiveYm: service.cancellationEffectiveYm || null,
        invoiced: false,
        paid: false,
        partiallyPaid: false,
        paidAmount: 0,
        invoiceDate: null,
        paymentDate: null,
        immutable: false,
        createdAt: new Date().toISOString(),
      };
      
      schedule.push(serviceLineItem);
      
      // No separate transport fee line; TF included in milestones and credited on Paid/Partial per spec
      
      // Partner commission line items (based on internal cost, non-accumulating)
      partnerRoles.forEach(role => {
        if (role.percent > 0 && role.percent <= 20 && role.entityId && role.positionId !== 'company') {
          const internalBaseAmount = ((Number(service.internalUnitCost) || 0) * (Number(service.quantity) || 1));
          const commissionCents = toCentsLocal(calculatePartnerCommission(internalBaseAmount, role.percent));
          const commissionAmount = commissionCents / 100;
          
          schedule.push({
            id: `commission-${scheduleId++}`,
            type: 'Partner Commission',
            serviceId: service.id,
            serviceName: service.name,
            description: `${role.positionName} commission for ${service.name}`,
            amount: round2(commissionAmount),
            frequency: 'one-time',
            status: 'PENDING',
            category: 'Commission',
            entityId: role.entityId,
            subAccountId: role.subAccountId,
            partnerRole: role.positionId,
            partnerPercent: role.percent,
            invoiced: false,
            paid: false,
            partiallyPaid: false,
            paidAmount: 0,
            invoiceDate: null,
            paymentDate: null,
            immutable: false,
            parentServiceId: service.id,
            createdAt: new Date().toISOString(),
          });
        }
      });
    });
    
    return schedule;
  };

  // Helper: transport percent for cost (matching proposal logic)
  const getTransportPercentForCost = (cost) => {
    if (typeof cost.transport_percent === 'number') return cost.transport_percent;
    const vendor = entities.find(e => e.id === (cost.entity_id || cost.vendorEntityId || cost.vendorId));
    const country = vendor ? countries.find(c => c.code === vendor.country) : null;
    const pct = Number(country?.transportPercent) || 0;
    return pct < 0 ? 0 : pct;
  };

  // Convert a master cost (as used in Proposals) to a contract service shape
  const convertCostToContractService = (cost, quantity = 1, assignedEntityId = '') => {
    const internal = Number(cost.internal_cost) || 0;
    const marginPct = Number(cost.margin_percent) || 0;
    const transportPct = getTransportPercentForCost(cost);
    // Mirror proposal calculation exactly: internal × (1 + margin%) + internal × transport%
    const unitPrice = Math.round((internal * (1 + (marginPct / 100)) + internal * (transportPct / 100)) * 100) / 100;
    const frequency = normalizeFrequencyValue(cost.frequency || 'one_time');
    const uniqueSuffix = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    return {
      id: `contract-service-${cost.id || 'cost'}-${uniqueSuffix}`,
      name: cost.name || '',
      description: cost.description || cost.name || '',
      category: cost.category || 'General',
      unitPrice,
      internalUnitCost: internal,
      marginPercent: marginPct,
      transportPercent: transportPct,
      // Preserve fractional quantities if provided
      quantity: Number(quantity) || 1,
      frequency,
      vendorEntityId: cost.vendorEntityId || cost.vendorId || cost.entity_id || (cost.vendor && (cost.vendor.id || cost.vendor.entity_id)) || null,
      assignedEntityId: assignedEntityId || '',
      subAccountId: null,
      originalCostId: cost.id || '',
      cancellationEffectiveYm: null,
      oneTimeApplied: false
    };
  };

  // Service modification functions
  const getServicePaymentScheduleItems = (serviceId) => {
    const schedule = Array.isArray(formData.paymentSchedule) ? formData.paymentSchedule : [];
    return schedule.filter((item) => item && item.type === 'Service' && item.serviceId === serviceId);
  };

  const isScheduleItemImmutable = (item) => {
    if (!item) return false;
    const statusKey = String(item.status || '').toUpperCase();
    if (paymentStatuses[statusKey]?.immutable) return true;
    if (item.invoiced || item.paid || item.partiallyPaid) return true;
    return false;
  };

  const serviceHasImmutablePayments = (serviceId) => {
    return getServicePaymentScheduleItems(serviceId).some((item) => isScheduleItemImmutable(item));
  };

  const removeServiceFromContract = async (serviceId) => {
    if (!canModifyServices(editingContract)) {
      setSnackbar({ 
        open: true, 
        message: 'Cannot remove service - invoiced items exist', 
        severity: 'error' 
      });
      return;
    }

    const serviceToRemove = formData.services.find(s => s.id === serviceId);
    if (!serviceToRemove) {
      return;
    }
    if (serviceToRemove?.cancellationEffectiveYm) {
      setSnackbar({ 
        open: true, 
        message: 'Cannot remove a cancelled service', 
        severity: 'error' 
      });
      return;
    }
    const normalizedFrequency = normalizeFrequencyValue(serviceToRemove?.frequency || '');
    if (normalizedFrequency === 'one-time' && serviceToRemove.oneTimeApplied) {
      setSnackbar({
        open: true,
        message: 'Cannot remove one-time service after it has been invoiced',
        severity: 'error'
      });
      return;
    }
    if (serviceHasImmutablePayments(serviceId)) {
      const message = normalizedFrequency === 'one-time'
        ? 'Cannot remove one-time service after it has been invoiced'
        : 'Cannot remove service - invoiced activity exists';
      setSnackbar({
        open: true,
        message,
        severity: 'error'
      });
      return;
    }
    const updatedServices = formData.services.filter(s => s.id !== serviceId);
    const newSchedule = regeneratePaymentSchedule(updatedServices, formData.partnerRoles, {
      currentSchedule: formData.paymentSchedule,
      clientEntityId: formData.clientEntityId,
    });
    const recalculated = recalculateTotalsAndMilestones(newSchedule, formData.partnerRoles, {
      ...formData,
      services: updatedServices,
      paymentSchedule: newSchedule,
    });
    
    const updatedFormData = {
      ...formData,
      services: updatedServices,
      paymentSchedule: newSchedule,
      totalAmount: recalculated.totalAmount,
      totalTransportFees: recalculated.totalTransportFees,
      milestoneLogs: recalculated.milestoneLogs,
      serviceModifications: [...formData.serviceModifications, {
        id: Date.now(),
        type: serviceModificationTypes.REMOVE,
        serviceId: serviceId,
        serviceName: serviceToRemove?.name || 'Unknown Service',
        timestamp: new Date().toISOString(),
        userId: currentUser.uid,
      }]
    };
    
    setFormData(updatedFormData);
    
    // Auto-save to Firebase if editing an existing contract
    if (editingContract?.id) {
      try {
        await updateContract(editingContract.id, updatedFormData);
        setSnackbar({ open: true, message: 'Service removed and saved', severity: 'success' });
      } catch (err) {
        console.error('Auto-save failed after removing service:', err);
        setSnackbar({ open: true, message: 'Service removed locally but save failed - please click Save', severity: 'warning' });
      }
    }
  };

  // Cancel a monthly service from next month (non-destructive). Leaves historical/invoiced months intact.
  const cancelServiceFromNextMonth = async (serviceId) => {
    // Only allow for services present
    const serviceToCancel = (formData.services || []).find(s => s && s.id === serviceId);
    if (!serviceToCancel) return;
    // Compute next month in YYYY-MM
    const nextYm = getNextMonthYm(new Date());
    const updatedServices = (formData.services || []).map(s => {
      if (!s || s.id !== serviceId) return s;
      const existing = String(s.cancellationEffectiveYm || '');
      // Keep the earliest cancellation month if already set
      const effectiveYm = existing && existing < nextYm ? existing : nextYm;
      return { ...s, cancellationEffectiveYm: effectiveYm };
    });
    const newSchedule = regeneratePaymentSchedule(updatedServices, formData.partnerRoles, {
      currentSchedule: formData.paymentSchedule,
      clientEntityId: formData.clientEntityId,
    });
    const recalculated = recalculateTotalsAndMilestones(newSchedule, formData.partnerRoles, {
      ...formData,
      services: updatedServices,
      paymentSchedule: newSchedule,
    });
    
    // 🔍 DEBUG: Log what we're about to save
    console.log('[cancelServiceFromNextMonth] BEFORE cancellation - existing milestones:', {
      timestamp: new Date().toISOString(),
      milestones: formData.milestoneLogs.map(m => ({
        expectedDate: m.expectedDate,
        status: m.status,
        expectedAmount: m.expectedAmount,
        expectedRevenueBase: m.expectedRevenueBase,
        expectedCommission: m.expectedCommission
      }))
    });
    
    console.log('[cancelServiceFromNextMonth] AFTER cancellation - recalculated milestones:', {
      timestamp: new Date().toISOString(),
      serviceId,
      serviceName: serviceToCancel?.name,
      nextYm,
      cancelledService: updatedServices.find(s => s.id === serviceId),
      scheduleItems: newSchedule.map(item => ({
        serviceName: item.serviceName,
        amount: item.amount,
        cancellationEffectiveYm: item.cancellationEffectiveYm
      })),
      milestones: recalculated.milestoneLogs.map(m => ({
        expectedDate: m.expectedDate,
        status: m.status,
        expectedAmount: m.expectedAmount,
        expectedRevenueBase: m.expectedRevenueBase,
        expectedCommission: m.expectedCommission
      }))
    });
    
    // 🔍 DEBUG: Compare before/after to see what changed
    const comparison = recalculated.milestoneLogs.map((after, idx) => {
      const before = formData.milestoneLogs[idx];
      if (!before) return { status: 'NEW', after: { expectedDate: after.expectedDate, status: after.status } };
      const changed = (
        before.expectedCommission !== after.expectedCommission ||
        before.expectedAmount !== after.expectedAmount ||
        before.expectedRevenueBase !== after.expectedRevenueBase
      );
      return {
        expectedDate: after.expectedDate,
        status: after.status,
        changed,
        before: changed ? {
          expectedCommission: before.expectedCommission,
          expectedAmount: before.expectedAmount,
          expectedRevenueBase: before.expectedRevenueBase
        } : 'UNCHANGED',
        after: changed ? {
          expectedCommission: after.expectedCommission,
          expectedAmount: after.expectedAmount,
          expectedRevenueBase: after.expectedRevenueBase
        } : 'UNCHANGED'
      };
    });
    console.log('[cancelServiceFromNextMonth] Milestone comparison (BEFORE vs AFTER cancellation):', {
      timestamp: new Date().toISOString(),
      comparison,
      warning: comparison.some(c => c.status !== 'Scheduled' && c.changed) 
        ? '⚠️ WARNING: Non-Scheduled milestone values changed! This should not happen!' 
        : '✅ All non-Scheduled milestones preserved correctly'
    });
    
    const updatedFormData = {
      ...formData,
      services: updatedServices,
      paymentSchedule: newSchedule,
      totalAmount: recalculated.totalAmount,
      totalTransportFees: recalculated.totalTransportFees,
      milestoneLogs: recalculated.milestoneLogs,
      serviceModifications: [
        ...(formData.serviceModifications || []),
        {
          id: Date.now(),
          type: serviceModificationTypes.MODIFY,
          serviceId: serviceId,
          serviceName: serviceToCancel?.name || 'Unknown Service',
          timestamp: new Date().toISOString(),
          userId: currentUser.uid,
          note: `Cancelled from ${nextYm}`
        }
      ]
    };
    
    setFormData(updatedFormData);
    
    // Auto-save to Firebase if editing an existing contract
    if (editingContract?.id) {
      try {
        console.log('[cancelServiceFromNextMonth] Saving to Firebase...', {
          contractId: editingContract.id,
          milestonesToSave: updatedFormData.milestoneLogs.map(m => ({
            expectedDate: m.expectedDate,
            status: m.status,
            expectedAmount: m.expectedAmount
          }))
        });
        await updateContract(editingContract.id, updatedFormData);
        console.log('[cancelServiceFromNextMonth] Save completed successfully');
        setSnackbar({ open: true, message: `Service will end after ${nextYm} (saved)`, severity: 'success' });
      } catch (err) {
        console.error('Auto-save failed after cancelling service:', err);
        setSnackbar({ open: true, message: `Service cancelled locally but save failed - please click Save`, severity: 'warning' });
      }
    } else {
      setSnackbar({ open: true, message: `Service will end after ${nextYm}`, severity: 'success' });
    }
  };

  const regeneratePaymentSchedule = (contractServices, partnerRoles, options = {}) => {
    // Use passed schedule/clientEntityId to avoid stale formData closure
    const currentSchedule = options.currentSchedule ?? formData.paymentSchedule;
    const clientEntityId = options.clientEntityId ?? formData.clientEntityId;
    
    // Keep immutable (invoiced) items, regenerate others
    const immutableItems = (Array.isArray(currentSchedule) ? currentSchedule : []).filter(item => 
      paymentStatuses[item.status]?.immutable
    );
    
    const clientEntity = entities.find(e => e.id === clientEntityId);
    const clientCountry = clientEntity?.country || 'US';
    
    const newScheduleItems = generateEnhancedPaymentSchedule(
      contractServices, 
      partnerRoles, 
      clientCountry
    );
    
    return [...immutableItems, ...newScheduleItems];
  };

  // Recalculate totals and milestones while preserving non-scheduled invoices
  const recalculateTotalsAndMilestones = (schedule, partnerRoles, baseForm = formData) => {
    // Compute contract totalAmount as a monthly all-in value to match proposal 1:1 when possible
    const safeSchedule = Array.isArray(schedule) ? schedule : [];
    const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';
    const monthlyServiceAmount = safeSchedule
      .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency))
      .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
    // Transport is now included in service amounts, no need to calculate separately
    const monthlyTransportFromLines = 0; // Kept for backward compatibility
    const getItemInternalUnitLocal = (item) => Number(item?.serviceInternalUnitCost) || 0;
    const getItemQuantityLocal = (item) => Number(item?.serviceQuantity) || 0;
    
    // CRITICAL: Only use baseForm.services, never fall back to formData.services
    // formData may contain stale data from a previous contract during handleOpenDialog
    const baseFormServices = Array.isArray(baseForm?.services) ? baseForm.services : [];
    const findServiceLocal = (sid) => {
      return baseFormServices.find((s) => s && s.id === sid);
    };
    const isItemOneTimeLocal = (it) => String(it?.frequency || '').toLowerCase() !== 'monthly';
    const isOneTimeAppliedLocal = (it) => {
      if (!isItemOneTimeLocal(it)) return false;
      const svc = findServiceLocal(it?.serviceId);
      return !!svc?.oneTimeApplied;
    };
    const monthlyInternal = safeSchedule
      .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency))
      .reduce((sum, i) => sum + (getItemInternalUnitLocal(i) * getItemQuantityLocal(i)), 0);
    const partnerPercentLocal = (Array.isArray(partnerRoles) ? partnerRoles : [])
      .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0) / 100;
    const monthlyCommission = round2(monthlyInternal * partnerPercentLocal);
    const baseAllMonthly = round2(monthlyServiceAmount + monthlyCommission); // Transport already in service amount
    // CRITICAL: Only use baseForm values, never fall back to formData (may be stale)
    const methodLocal = String(baseForm?.defaultPaymentMethod || 'Transfer').toLowerCase();
    const processingPercentLocal = (methodLocal === 'credit card' || methodLocal === 'creditcard' || methodLocal === 'credit_card')
      ? creditCardProcessingPercent
      : (methodLocal === 'check' ? checkProcessingPercent : 0);
    // Prefer live/snapshot proposal total when available for 1:1 parity
    // CRITICAL: Only use baseForm.proposalId, not formData.proposalId
    const liveProposal = proposals.find((p) => p && p.id === baseForm?.proposalId);
    const proposalTotalRaw = Number.isFinite(Number(liveProposal?.totalAmount))
      ? Number(liveProposal.totalAmount)
      : Number(baseForm?.proposalSnapshot?.totalAmount);
    const totalAmount = (Number.isFinite(proposalTotalRaw) && proposalTotalRaw > 0)
      ? Math.round(proposalTotalRaw * 100) / 100
      : round2(baseAllMonthly + round2(baseAllMonthly * (processingPercentLocal / 100)));

    // Transport fees are now included in service amounts, not tracked separately
    const totalTransportFees = 0;

    // Auto-derive milestones for current dates
    // CRITICAL: Only use baseForm.defaultPaymentMethod and baseFormServices
    const auto = generateMonthlyMilestonesFromSchedule(
      schedule,
      partnerRoles || [],
      baseForm.startDate,
      baseForm.endDate,
      baseForm?.defaultPaymentMethod || 'Transfer',
      baseFormServices // Pass services explicitly to avoid stale formData closure
    );

    if (!auto || auto.length === 0) {
      // No start/end provided → keep existing milestones but update expected amounts to reflect new schedule
      const existing = Array.isArray(baseForm.milestoneLogs) ? baseForm.milestoneLogs : [];

      // Compute revenue components from schedule for fallback math
      const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';
      const monthlyServiceAmount = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i.type === 'Service' && isMonthly(i.frequency))
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const oneTimeRevenueAmount = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedLocal(i))
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const transportOneTimeAmount = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i.type === 'Transport Fee')
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const partnerPercent = (Array.isArray(partnerRoles) ? partnerRoles : [])
        .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0) / 100;

      // Internal base for commissions (fallback math when dates are not available)
      const monthlyInternal = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i.type === 'Service' && isMonthly(i.frequency))
        .reduce((sum, i) => sum + ((Number(i.serviceInternalUnitCost) || 0) * (Number(i.serviceQuantity) || 0)), 0);
      const oneTimeInternal = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedLocal(i))
        .reduce((sum, i) => sum + ((Number(i.serviceInternalUnitCost) || 0) * (Number(i.serviceQuantity) || 0)), 0);

      if (existing.length === 0) {
        // Create a single Scheduled milestone as a fallback preview so Contract Value updates
        const base = monthlyServiceAmount + oneTimeRevenueAmount;
        const processingPercent = (() => {
          // CRITICAL: Only use baseForm, never formData (may be stale)
          const method = String(baseForm?.defaultPaymentMethod || 'Transfer').toLowerCase();
          if (method === 'credit card' || method === 'creditcard' || method === 'credit_card') return creditCardProcessingPercent;
          if (method === 'check') return checkProcessingPercent;
          return 0;
        })();
        // Include processing fee as in proposals
        const baseAll = (base + transportOneTimeAmount + round2((monthlyInternal + oneTimeInternal) * partnerPercent));
        const expectedProcessingFee = Math.round((baseAll * (processingPercent / 100)) * 100) / 100;
        const expectedAmount = Math.round(((baseAll + expectedProcessingFee)) * 100) / 100;
        // 🔧 SAFETY: Ensure expectedCommission is always a valid number
        const calculatedCommission = round2((monthlyInternal + oneTimeInternal) * partnerPercent);
        const safeCommission = Number.isFinite(calculatedCommission) ? calculatedCommission : 0;
        if (!Number.isFinite(calculatedCommission)) {
          console.warn('[recalculateTotalsAndMilestones] Invalid expectedCommission, defaulting to 0:', {
            monthlyInternal,
            oneTimeInternal,
            partnerPercent,
            partnerRoles
          });
        }
        
        const single = [{
          id: Date.now(),
          milestone: '1',
          expectedDate: '',
          actualDate: '',
          invoiceId: '',
          invoiceUrl: '',
          expectedRevenueBase: base,
          expectedTransport: transportOneTimeAmount,
          expectedCommission: safeCommission,
          expectedProcessingFee,
          expectedAmount,
          actualAmount: 0,
          appliedAmount: 0,
          status: 'Scheduled',
          financialTriggers: []
        }];
        return { totalAmount, totalTransportFees, milestoneLogs: single };
      }

      // Find the first Scheduled milestone; one-time is applied there only.
      const firstScheduledIdx = existing.findIndex((m) => m && m.status === 'Scheduled');
      const updated = existing.map((m, idx) => {
        if (!m || m.status !== 'Scheduled') {
          console.log('[recalculateTotalsAndMilestones] PRESERVING non-Scheduled milestone (Invoiced/Paid/Partial/Cancelled):', {
            ym: m?.expectedDate,
            status: m?.status,
            expectedCommission: m?.expectedCommission,
            expectedAmount: m?.expectedAmount,
            reason: 'Status is not Scheduled - milestone values are LOCKED and unchanged by cancellation'
          });
          return m; // do not alter non-scheduled months
        }
        
        const ym = String(m.expectedDate || '');
        
        // 🔧 FIX: Check if this Scheduled milestone should be affected by cancellations
        // Only apply cancellation logic to Scheduled milestones with expectedDate >= earliest cancellationEffectiveYm
        // This prevents cancellations from affecting work already invoiced/done before the cancellation date
        const earliestCancellationYm = (() => {
          const services = Array.isArray(baseForm?.services) ? baseForm.services : [];
          const cancellationDates = services
            .map(s => s?.cancellationEffectiveYm)
            .filter(Boolean)
            .sort((a, b) => String(a).localeCompare(String(b)));
          return cancellationDates.length > 0 ? cancellationDates[0] : null;
        })();
        
        // If this Scheduled milestone is BEFORE any cancellation date, preserve its values
        // (it represents work done/invoiced before the service was cancelled)
        if (earliestCancellationYm && String(ym) < String(earliestCancellationYm)) {
          console.log('[recalculateTotalsAndMilestones] PRESERVING Scheduled milestone (before cancellation date):', {
            ym,
            status: m?.status,
            earliestCancellationYm,
            expectedCommission: m?.expectedCommission,
            expectedAmount: m?.expectedAmount,
            reason: 'Milestone date is before cancellation - represents work already done'
          });
          return m; // Preserve milestone as-is
        }
        
        // ⚠️ IMPORTANT: Scheduled milestones with expectedDate >= cancellationEffectiveYm 
        // should be recalculated to exclude cancelled services
        // This ensures that cancelling a service correctly updates all future Scheduled milestone amounts
        
        // Respect per-service cancellationEffectiveYm for monthly services
        const allMonthlyScheduleItems = (Array.isArray(schedule) ? schedule : [])
          .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency));
        
        console.log('[recalculateTotalsAndMilestones] Recalculating Scheduled milestone:', {
          ym,
          milestoneIndex: idx,
          allMonthlyCount: allMonthlyScheduleItems.length,
          allMonthlyItems: allMonthlyScheduleItems.map(i => ({
            name: i.serviceName,
            amount: i.amount,
            cancellationEffectiveYm: i.cancellationEffectiveYm,
            shouldInclude: isServiceActiveForMonth(i, ym)
          }))
        });
        
        // ✅ FIX: Use isServiceActiveForMonth helper for consistent cancellation filtering
        const monthlyForYm = allMonthlyScheduleItems
          .filter((i) => isServiceActiveForMonth(i, ym))
          .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
        // CRITICAL FIX: Calculate internal cost per month, respecting cancellation dates
        const monthlyInternalForYm = allMonthlyScheduleItems
          .filter((i) => isServiceActiveForMonth(i, ym))
          .reduce((sum, i) => sum + ((getItemInternalUnitLocal(i) || 0) * (getItemQuantityLocal(i) || 0)), 0);
        const baseRevenue = monthlyForYm + (idx === firstScheduledIdx ? oneTimeRevenueAmount : 0);
        const transport = idx === firstScheduledIdx ? transportOneTimeAmount : 0;
        const processingPercent = (() => {
          // CRITICAL: Only use baseForm, never formData (may be stale)
          const method = String(baseForm?.defaultPaymentMethod || 'Transfer').toLowerCase();
          if (method === 'credit card' || method === 'creditcard' || method === 'credit_card') return creditCardProcessingPercent;
          if (method === 'check') return checkProcessingPercent;
          return 0;
        })();
        // Include processing fee as in proposals
        const oneTimeInternalForYm = idx === firstScheduledIdx ? oneTimeInternal : 0;
        const commissionForYm = round2((monthlyInternalForYm + oneTimeInternalForYm) * partnerPercent);
        
        // 🔧 SAFETY: Ensure expectedCommission is always a valid number
        const safeCommissionForYm = Number.isFinite(commissionForYm) ? commissionForYm : 0;
        if (!Number.isFinite(commissionForYm)) {
          console.warn('[recalculateTotalsAndMilestones] Invalid commission for milestone, defaulting to 0:', {
            ym,
            monthlyInternalForYm,
            oneTimeInternalForYm,
            partnerPercent,
            partnerRoles
          });
        }
        
        const baseAll = (baseRevenue + transport + safeCommissionForYm);
        const expectedProcessingFee = Math.round((baseAll * (processingPercent / 100)) * 100) / 100;
        return {
          ...m,
          expectedRevenueBase: baseRevenue,
          expectedTransport: transport,
          expectedCommission: safeCommissionForYm,
          expectedProcessingFee,
          expectedAmount: Math.round(((baseAll + expectedProcessingFee)) * 100) / 100,
        };
      });

      const reindexed = reindexMilestones(updated);
      const mergedWithInvoice = preserveInvoiceMetadata(reindexed, baseForm.milestoneLogs || []);
      
      // 🔍 VERIFICATION: Ensure Invoiced/Paid/Partial milestones were truly preserved
      const verification = (baseForm.milestoneLogs || [])
        .filter(m => m && m.status !== 'Scheduled')
        .map(originalM => {
          const updatedM = mergedWithInvoice.find(m => m && m.id === originalM.id);
          if (!updatedM) return { id: originalM.id, status: 'MISSING', error: 'Milestone disappeared!' };
          const commissionsMatch = Math.abs((originalM.expectedCommission || 0) - (updatedM.expectedCommission || 0)) < 0.01;
          const amountsMatch = Math.abs((originalM.expectedAmount || 0) - (updatedM.expectedAmount || 0)) < 0.01;
          return {
            id: originalM.id,
            expectedDate: originalM.expectedDate,
            status: originalM.status,
            commissionsMatch,
            amountsMatch,
            originalCommission: originalM.expectedCommission,
            updatedCommission: updatedM.expectedCommission,
            originalAmount: originalM.expectedAmount,
            updatedAmount: updatedM.expectedAmount,
            preserved: commissionsMatch && amountsMatch
          };
        });
      
      const anyNonScheduledChanged = verification.some(v => !v.preserved);
      if (anyNonScheduledChanged) {
        console.error('[recalculateTotalsAndMilestones] ⚠️ CRITICAL BUG: Non-Scheduled milestones were changed!', {
          verification,
          affectedMilestones: verification.filter(v => !v.preserved)
        });
      } else {
        console.log('[recalculateTotalsAndMilestones] ✅ VERIFIED: All non-Scheduled milestones preserved correctly', {
          verifiedCount: verification.length,
          verification
        });
      }
      
      return { totalAmount, totalTransportFees, milestoneLogs: mergedWithInvoice };
    }

    // Preserve non-scheduled (Invoiced/Paid/Partial/Cancelled) existing milestones
    // Trim auto-generated months to not predate the earliest existing Scheduled milestone
    const earliestScheduledYm = (() => {
      const existing = Array.isArray(baseForm.milestoneLogs) ? baseForm.milestoneLogs : [];
      const yms = existing
        .filter((m) => m && m.status === 'Scheduled' && m.expectedDate)
        .map((m) => String(m.expectedDate))
        .sort((a, b) => String(a).localeCompare(String(b)));
      return yms.length > 0 ? yms[0] : null;
    })();
    const autoEarliestYm = (() => {
      if (!Array.isArray(auto) || auto.length === 0) return null;
      const firstWithDate = auto.find((m) => m && m.expectedDate);
      return firstWithDate ? String(firstWithDate.expectedDate) : null;
    })();
    const shouldTrimAuto = Boolean(
      earliestScheduledYm &&
      autoEarliestYm &&
      String(autoEarliestYm) >= String(earliestScheduledYm)
    );
    const autoTrimmed = shouldTrimAuto
      ? (auto || []).filter((m) => String(m?.expectedDate || '') >= String(earliestScheduledYm))
      : (auto || []);
    const resultByMonth = new Map(autoTrimmed.map((m) => [m.expectedDate, { ...m }]));
    
    // Preserve non-Scheduled milestones (Invoiced/Paid/Partial/Cancelled)
    (Array.isArray(baseForm.milestoneLogs) ? baseForm.milestoneLogs : [])
      .filter((m) => m && m.status && m.status !== 'Scheduled' && m.expectedDate)
      .forEach((m) => {
        resultByMonth.set(m.expectedDate, { ...m });
      });
    
    // 🔧 FIX: Also preserve Scheduled milestones that are BEFORE any cancellation date
    // These represent work already done/invoiced before the service was cancelled
    const earliestCancellationYm = (() => {
      const services = Array.isArray(baseForm?.services) ? baseForm.services : [];
      const cancellationDates = services
        .map(s => s?.cancellationEffectiveYm)
        .filter(Boolean)
        .sort((a, b) => String(a).localeCompare(String(b)));
      return cancellationDates.length > 0 ? cancellationDates[0] : null;
    })();
    
    if (earliestCancellationYm) {
      (Array.isArray(baseForm.milestoneLogs) ? baseForm.milestoneLogs : [])
        .filter((m) => m && m.status === 'Scheduled' && m.expectedDate && String(m.expectedDate) < String(earliestCancellationYm))
        .forEach((m) => {
          console.log('[recalculateTotalsAndMilestones] PRESERVING Scheduled milestone (before cancellation date):', {
            ym: m.expectedDate,
            status: m.status,
            earliestCancellationYm,
            expectedCommission: m.expectedCommission,
            expectedAmount: m.expectedAmount,
            reason: 'Milestone date is before cancellation - represents work already done'
          });
          resultByMonth.set(m.expectedDate, { ...m });
        });
    }

    const merged = Array.from(resultByMonth.values())
      .sort((a, b) => String(a.expectedDate).localeCompare(String(b.expectedDate)));

    // Ensure one-time additions are applied to the first Scheduled milestone (nearest next Scheduled)
    // and not placed at the contract start month.
    try {
      const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';
        const oneTimeRevenueAmount = (Array.isArray(schedule) ? schedule : [])
          .filter((i) => i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedLocal(i))
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const transportOneTimeAmount = 0; // transport included in service amounts
      const partnerPercent = (Array.isArray(partnerRoles) ? partnerRoles : [])
        .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0) / 100;

      // Precompute monthly parity targets (per-month) using proposal first-payment total
      const monthlyServiceAmountAll = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency))
        .reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
      const monthlyTransportAll = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency))
        .reduce((sum, i) => sum + (Number(i.transportFeeUsd) || 0), 0);
      const monthlyInternalAll = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency))
        .reduce((sum, i) => sum + ((Number(i.serviceInternalUnitCost) || 0) * (Number(i.serviceQuantity) || 0)), 0);
      const baseAllMonthlyAll = round2(monthlyServiceAmountAll + monthlyTransportAll + round2(monthlyInternalAll * partnerPercent));

      const oneTimeInternalAll = (Array.isArray(schedule) ? schedule : [])
        .filter((i) => i && i.type === 'Service' && !isMonthly(i.frequency) && !isOneTimeAppliedLocal(i))
        .reduce((sum, i) => sum + ((Number(i.serviceInternalUnitCost) || 0) * (Number(i.serviceQuantity) || 0)), 0);
      const expectedOneTimeCommissionAll = round2(oneTimeInternalAll * partnerPercent);
      const baseAllOneTimeAll = round2(oneTimeRevenueAmount + transportOneTimeAmount + round2(oneTimeInternalAll * partnerPercent));

      // CRITICAL: Use baseForm for parity calculation, not formData (may be stale)
      const liveForParity = proposals.find((p) => p && p.id === (baseForm?.proposalId || ''));
      // In proposal schema, totalAmount represents the Monthly Payments Total
      const proposalMonthlyTotal = Number.isFinite(Number(liveForParity?.totalAmount))
        ? Number(liveForParity.totalAmount)
        : Number(baseForm?.proposalSnapshot?.totalAmount);
      const hasProposalMonthlyTotal = Number.isFinite(proposalMonthlyTotal) && proposalMonthlyTotal > 0;

      const hasOneTimeAdditions = oneTimeRevenueAmount > 0 || transportOneTimeAmount > 0 || expectedOneTimeCommissionAll > 0;
      if (hasOneTimeAdditions) {
          const firstScheduledIdx = merged.findIndex((m) => m && m.status === 'Scheduled');
          if (firstScheduledIdx >= 0) {
            const m = merged[firstScheduledIdx];
          const ym = String(m.expectedDate || '');
          // ✅ FIX: Use isServiceActiveForMonth helper for consistent cancellation filtering
          const monthlyActive = (Array.isArray(schedule) ? schedule : [])
            .filter((i) => i && i.type === 'Service' && isMonthly(i.frequency) && isServiceActiveForMonth(i, ym));
          const monthlyServiceAmountForYm = monthlyActive.reduce((sum, i) => sum + (Number(i.amount) || 0), 0);
          const monthlyTransportForYm = monthlyActive.reduce((sum, i) => sum + (Number(i.transportFeeUsd) || 0), 0);
          const monthlyInternalForYm = monthlyActive.reduce((sum, i) => sum + ((Number(i.serviceInternalUnitCost) || 0) * (Number(i.serviceQuantity) || 0)), 0);
          const expectedMonthlyCommissionForYm = round2(monthlyInternalForYm * partnerPercent);

          const baseExtra = Number(m.expectedRevenueBase || 0) - monthlyServiceAmountForYm;
          const transportExtra = Number(m.expectedTransport || 0) - monthlyTransportForYm;
          const commissionExtra = Number(m.expectedCommission || 0) - expectedMonthlyCommissionForYm;
          const missingBase = Math.max(0, oneTimeRevenueAmount - Math.max(0, baseExtra));
          const missingTransport = Math.max(0, transportOneTimeAmount - Math.max(0, transportExtra));
          const missingCommission = Math.max(0, expectedOneTimeCommissionAll - Math.max(0, commissionExtra));

          if (missingBase > 0 || missingTransport > 0 || missingCommission > 0) {
            const newExpectedRevenueBase = Number(m.expectedRevenueBase || 0) + missingBase;
            const newExpectedTransport = Number(m.expectedTransport || 0) + missingTransport;
            const newExpectedCommission = Number(m.expectedCommission || 0) + missingCommission;
            const newBaseAll = newExpectedRevenueBase + newExpectedCommission + newExpectedTransport;
            const newProcessingFee = round2(newBaseAll * (getProcessingPercentForMethod(baseForm?.defaultPaymentMethod || 'Transfer') / 100));

            merged[firstScheduledIdx] = {
              ...m,
              expectedRevenueBase: newExpectedRevenueBase,
              expectedTransport: newExpectedTransport,
              expectedCommission: newExpectedCommission,
              expectedProcessingFee: newProcessingFee,
              expectedAmount: Math.round((newBaseAll + newProcessingFee) * 100) / 100,
            };
          }
        }
      }

      // Normalize scheduled months to reflect active services only (handle cancellations)
      // ⚠️ CRITICAL: Only recalculate milestones with missing/invalid values
      // Preserve "locked" milestone amounts that were correctly calculated at creation
      try {
        const methodLocal = String(baseForm?.defaultPaymentMethod || formData?.defaultPaymentMethod || '').toLowerCase();
        const processingPercentLocal = (methodLocal === 'credit card' || methodLocal === 'creditcard' || methodLocal === 'credit_card')
          ? creditCardProcessingPercent
          : (methodLocal === 'check' ? checkProcessingPercent : 0);
        const firstScheduledIdx = merged.findIndex((m) => m && m.status === 'Scheduled');
        for (let i = 0; i < merged.length; i++) {
          if (hasOneTimeAdditions && i === firstScheduledIdx) continue; // keep one-time in first scheduled when present
          const m = merged[i];
          if (!m || m.status !== 'Scheduled') continue;
          
          // ✅ NEW: Check if milestone has valid locked values - if so, preserve them
          const hasValidLockedValues = (
            typeof m.expectedAmount === 'number' && m.expectedAmount > 0 &&
            typeof m.expectedRevenueBase === 'number' &&
            typeof m.expectedCommission === 'number'
          );
          
          if (hasValidLockedValues) {
            console.log('[recalculateTotalsAndMilestones] Preserving locked milestone values (NOT recalculating):', {
              milestoneIndex: i,
              expectedDate: m.expectedDate,
              expectedAmount: m.expectedAmount,
              expectedRevenueBase: m.expectedRevenueBase,
              expectedCommission: m.expectedCommission,
              reason: 'Values are locked at creation'
            });
            continue; // Skip recalculation - preserve locked values
          }
          
          const ym = String(m.expectedDate || '');
          const allMonthlyItems = (Array.isArray(schedule) ? schedule : [])
            .filter((it) => it && it.type === 'Service' && isMonthly(it.frequency));
          // ✅ FIX: Use isServiceActiveForMonth helper for consistent cancellation filtering
          const monthlyActive = allMonthlyItems
            .filter((it) => isServiceActiveForMonth(it, ym));
          
          console.log('[recalculateTotalsAndMilestones] Normalization step (values missing - recalculating):', {
            ym,
            allMonthlyCount: allMonthlyItems.length,
            monthlyActiveCount: monthlyActive.length,
            services: allMonthlyItems.map(it => ({
              name: it.serviceName,
              amount: it.amount,
              cancellationEffectiveYm: it.cancellationEffectiveYm,
              included: isServiceActiveForMonth(it, ym)
            }))
          });
          
          const monthlyServiceAmountForYm = monthlyActive.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);
          const monthlyInternalForYm = monthlyActive.reduce((sum, it) => sum + ((Number(it.serviceInternalUnitCost) || 0) * (Number(it.serviceQuantity) || 0)), 0);
          const monthlyCommissionForYm = round2(monthlyInternalForYm * partnerPercent);
          const baseAllForYm = round2(monthlyServiceAmountForYm + monthlyCommissionForYm);
          const processingForYm = round2(baseAllForYm * (processingPercentLocal / 100));

          merged[i] = {
            ...m,
            expectedRevenueBase: round2(monthlyServiceAmountForYm),
            expectedTransport: 0,
            expectedCommission: round2(monthlyCommissionForYm),
            expectedProcessingFee: processingForYm,
            expectedAmount: round2(baseAllForYm + processingForYm),
          };
        }
      } catch (_) { /* ignore */ }
    } catch (_) {
      // best-effort; never block on milestone adjustment
    }

    const reindexed = reindexMilestones(merged);
    const mergedWithInvoice = preserveInvoiceMetadata(reindexed, baseForm.milestoneLogs || []);

    // 🔍 VERIFICATION: Ensure Invoiced/Paid/Partial milestones were truly preserved
    const verification = (baseForm.milestoneLogs || [])
      .filter(m => m && m.status !== 'Scheduled')
      .map(originalM => {
        const updatedM = mergedWithInvoice.find(m => m && m.id === originalM.id);
        if (!updatedM) return { id: originalM.id, status: 'MISSING', error: 'Milestone disappeared!' };
        const commissionsMatch = Math.abs((originalM.expectedCommission || 0) - (updatedM.expectedCommission || 0)) < 0.01;
        const amountsMatch = Math.abs((originalM.expectedAmount || 0) - (updatedM.expectedAmount || 0)) < 0.01;
        return {
          id: originalM.id,
          expectedDate: originalM.expectedDate,
          status: originalM.status,
          commissionsMatch,
          amountsMatch,
          originalCommission: originalM.expectedCommission,
          updatedCommission: updatedM.expectedCommission,
          originalAmount: originalM.expectedAmount,
          updatedAmount: updatedM.expectedAmount,
          preserved: commissionsMatch && amountsMatch
        };
      });
    
    const anyNonScheduledChanged = verification.some(v => !v.preserved);
    if (anyNonScheduledChanged) {
      console.error('[recalculateTotalsAndMilestones] ⚠️ CRITICAL BUG: Non-Scheduled milestones were changed!', {
        verification,
        affectedMilestones: verification.filter(v => !v.preserved)
      });
    } else {
      console.log('[recalculateTotalsAndMilestones] ✅ VERIFIED: All non-Scheduled milestones preserved correctly', {
        verifiedCount: verification.length,
        verification
      });
    }

    return { totalAmount, totalTransportFees, milestoneLogs: mergedWithInvoice };
  };

  // Validation functions
  const canModifyServices = (contract) => {
    // Allow modifying services at any time. Immutable (invoiced/paid/cancelled/partial) items
    // in the payment schedule are preserved by regeneratePaymentSchedule.
    return true;
  };

 

  const validatePartnerCommission = (percent) => {
    return Number(percent) >= 0 && Number(percent) <= 20;
  };


  // Beneficiary Company choices: only active companies (keep the contract's current beneficiary visible when editing)
  const getBeneficiaryCompanyOptions = (currentId = '') =>
    entities.filter(e => e.entityType === 'company' && (e.status === 'active' || (currentId && e.id === currentId)));

  const isActivePartnerEntity = (entity) => {
    if (!entity) return false;
    const status = String(entity.status || entity.state || '').toLowerCase();
    return String(entity.entityType) === 'partner' && (status === 'active' || entity.active === true || entity.isActive === true);
  };

  // Validate that ALL partner roles are defined for Ready status
  const validateAllPartnerRolesDefined = (roles) => {
    const list = Array.isArray(roles) ? roles : [];
    for (const position of partnerPositions) {
      const role = list.find(r => r && r.positionId === position.id);
      if (!role) {
        return { ok: false, message: `All partner roles must be defined. Missing: ${position.name}` };
      }
      if (position.id === 'company') {
        const ent = entities.find(e => e.id === role.entityId);
        const isCompany = ent && ent.entityType === 'company' && ent.accountType !== 'sub';
        if (!isCompany) {
          return { ok: false, message: 'Company role must be assigned to a Company entity' };
        }
      } else {
        if (!role.entityId) {
          const isNobody = String(role.entityName || '') === 'Nobody';
          if (!isNobody) {
            return { ok: false, message: `Select an active Partner or Nobody for ${position.name}` };
          }
          if ((Number(role.percent) || 0) !== 0) {
            return { ok: false, message: `When Nobody is selected for ${position.name}, percent must be 0%` };
          }
        } else {
          const ent = entities.find(e => e.id === role.entityId);
          if (!isActivePartnerEntity(ent)) {
            return { ok: false, message: `Selected entity for ${position.name} must be an Active Partner` };
          }
          const pct = Number(role.percent) || 0;
          if (pct < 0 || pct > 20) {
            return { ok: false, message: `${position.name} commission must be between 0% and 20%` };
          }
        }
      }
    }
    return { ok: true };
  };

  // Financial integration functions
  const generateFinancialTriggers = (contract) => {
    const triggers = [];
    
    // AR triggers for client invoicing
    contract.paymentSchedule
      .filter(item => item.type === 'Service' && item.status === 'INVOICED')
      .forEach(item => {
        triggers.push({
          id: `ar-${item.id}`,
          type: 'AR',
          module: 'accounts_receivable',
          action: 'create_invoice',
          amount: item.amount,
          entityId: contract.clientEntityId,
          serviceId: item.serviceId,
          dueDate: item.invoiceDate,
          status: 'pending'
        });
      });
    
    // AP triggers for partner/vendor payments
    contract.paymentSchedule
      .filter(item => (item.type === 'Partner Commission' || item.type === 'Service') && 
                     item.status === 'PAID' && item.entityId)
      .forEach(item => {
        triggers.push({
          id: `ap-${item.id}`,
          type: 'AP',
          module: 'accounts_payable',
          action: 'create_payment',
          amount: item.amount,
          entityId: item.entityId,
          subAccountId: item.subAccountId,
          serviceId: item.serviceId,
          paymentDate: item.paymentDate,
          status: 'pending'
        });
      });
    
    // Ledger triggers for financial recording
    contract.paymentSchedule
      .filter(item => item.status === 'PAID')
      .forEach(item => {
        triggers.push({
          id: `ledger-${item.id}`,
          type: 'LEDGER',
          module: 'ledger',
          action: 'record_transaction',
          amount: item.amount,
          debitAccount: item.type === 'Service' ? 'revenue' : 'commission_expense',
          creditAccount: 'cash',
          entityId: item.entityId,
          serviceId: item.serviceId,
          status: 'pending'
        });
      });
    
    return triggers;
  };

  // Calculate company percentage automatically (with remaining after partner allocations)
  const calculateCompanyPercentage = (partnerRoles) => {
    const totalAllocated = partnerRoles
      .filter(role => role.positionId !== 'company')
      .reduce((sum, role) => sum + (role.percent || 0), 0);
    return Math.max(0, 100 - totalAllocated);
  };

  // Check if contract can be edited
  const canEditContract = (contract) => {
    if (!contract) return true; // New contract
    if (contract.status === 'Signed' || contract.status === 'Complete') {
      return isSa; // Only superadmin can edit signed/completed contracts
    }
    return true;
  };

  // Deep copy proposal data with enhanced service mapping
  const deepCopyProposalData = (proposal, servicesForSnapshot = []) => {
    return {
      proposalSnapshot: {
        id: proposal.id,
        title: proposal.title,
        totalAmount: proposal.totalAmount,
        costs: proposal.costs ? [...proposal.costs] : [],
        client_id: proposal.client_id,
        created_at: proposal.created_at,
        created_by: proposal.created_by,
        // Deep copy partner roles and derived services from proposal at first save
        partnerRoles: Array.isArray(proposal.partnerRoles)
          ? JSON.parse(JSON.stringify(proposal.partnerRoles))
          : [],
        servicesFromProposal: JSON.parse(JSON.stringify(servicesForSnapshot)),
      }
    };
  };

  // Firestore operations
  const fetchContracts = async () => {
    try {
      setLoading(true);
      const contractsRef = collection(db, 'contracts');
      const q = query(contractsRef, orderBy('createdAt', 'desc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const contractsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          contractsData.push({
            id: doc.id,
            ...data,
            signedDate: data.signedDate?.toDate?.()?.toISOString()?.split('T')[0] || null,
            startDate: data.startDate?.toDate?.()?.toISOString()?.split('T')[0] || '',
            endDate: data.endDate?.toDate?.()?.toISOString()?.split('T')[0] || '',
            partnerRoles: data.partnerRoles || [],
            paymentSchedule: data.paymentSchedule || [],
            services: data.services || [],
            transportFees: data.transportFees || [],
            milestoneLogs: data.milestoneLogs || [],
            bankingInfo: data.bankingInfo || {},
            totalAmount: data.totalAmount || 0,
            copiedCosts: data.copiedCosts || [],
            financialTriggers: data.financialTriggers || [],
            beneficiaryLocked: data.beneficiaryLocked || false,
            startDateLocked: data.startDateLocked || (data.status === 'Active' && !!data.startDate) || false,
            scheduledReceivableTiming: normalizeScheduledReceivableTiming(data.scheduledReceivableTiming),
          });
        });
        setContracts(contractsData);
        setLoading(false);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching contracts:', error);
      setSnackbar({ open: true, message: 'Error loading contracts', severity: 'error' });
      setLoading(false);
    }
  };

  const fetchProposals = async () => {
    try {
      const proposalsRef = collection(db, 'proposals');
      const q = query(proposalsRef, where('status', '==', 'Accepted'), orderBy('title', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const proposalsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          proposalsData.push({
            id: doc.id,
            ...data,
          });
        });
        setProposals(proposalsData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching proposals:', error);
      return null;
    }
  };

  const fetchClients = async () => {
    try {
      const clientsRef = collection(db, 'clients');
      const q = query(clientsRef, orderBy('clientName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const clientsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          clientsData.push({
            id: doc.id,
            ...data,
          });
        });
        setClients(clientsData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching clients:', error);
      return null;
    }
  };

  const fetchContacts = async () => {
    try {
      const contactsRef = collection(db, 'contacts');
      const q = query(contactsRef);
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const contactsData = [];
        querySnapshot.forEach((doc) => {
          contactsData.push({
            id: doc.id,
            ...doc.data(),
          });
        });
        setContacts(contactsData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching contacts:', error);
      return null;
    }
  };

  const fetchEntities = async () => {
    try {
      const entitiesRef = collection(db, 'entities');
      const q = query(entitiesRef, orderBy('entityName', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const entitiesData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          entitiesData.push({
            id: doc.id,
            ...data,
          });
        });
        setEntities(entitiesData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching entities:', error);
      return null;
    }
  };

  // Firestore operations for services
  const fetchServices = async () => {
    try {
      const servicesRef = collection(db, 'services');
      const q = query(servicesRef, orderBy('name', 'asc'));
      
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const servicesData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          servicesData.push({
            id: doc.id,
            ...data,
          });
        });
        setServices(servicesData);
      });

      return unsubscribe;
    } catch (error) {
      console.error('Error fetching services:', error);
      return null;
    }
  };

  // Also pull from master costs so contracts can add services just like proposals
  const fetchCosts = async () => {
    try {
      const costsRef = collection(db, 'costs');
      const q = query(costsRef, orderBy('name', 'asc'));
      const unsubscribe = onSnapshot(q, (querySnapshot) => {
        const costsData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          costsData.push({ id: doc.id, ...data });
        });
        setCosts(costsData);
      });
      return unsubscribe;
    } catch (error) {
      console.error('Error fetching costs for contracts:', error);
      return null;
    }
  };

  // Enhanced form data handlers
  const handlePartnerRoleChange = (index, field, value) => {
    // Use functional update to avoid stale state issues
    setFormData(prevFormData => {
      // Lock partner percentages when contract is Active
      if (String(prevFormData.status || '') === 'Active' && field === 'percent') {
        setSnackbar({ 
          open: true, 
          message: 'Cannot change partner percentages on an Active contract', 
          severity: 'warning' 
        });
        return prevFormData;
      }
      const newPartnerRoles = [...prevFormData.partnerRoles];
      
      if (field === 'percent') {
        const numValue = Number(value);
        if (!validatePartnerCommission(numValue)) {
          setSnackbar({ 
            open: true, 
            message: 'Partner commission must be between 0% and 20%', 
            severity: 'warning' 
          });
          return prevFormData;
        }
        const role = newPartnerRoles[index] || {};
        const isNobody = (role.entityId == null) && String(role.entityName || '').toLowerCase() === 'nobody' && role.positionId !== 'company';
        if (isNobody) {
          newPartnerRoles[index].percent = 0;
          setSnackbar({ open: true, message: 'When Nobody is selected, percent is forced to 0%', severity: 'info' });
        } else {
          newPartnerRoles[index].percent = numValue;
        }
      } else if (field === 'entityId') {
        // Special case: Nobody selection (non-company roles)
        if (value === 'nobody') {
          newPartnerRoles[index].entityId = null;
          newPartnerRoles[index].entityName = 'Nobody';
          newPartnerRoles[index].percent = 0;
          newPartnerRoles[index].subAccountId = null;
        } else if (value === '') {
          // Unassigned
          newPartnerRoles[index].entityId = null;
          newPartnerRoles[index].entityName = '';
          newPartnerRoles[index].subAccountId = null;
        } else {
          const selectedEntity = entities.find(e => e.id === value);
          const currentRole = newPartnerRoles[index] || {};
          const isCompanyRole = currentRole.positionId === 'company';
          if (isCompanyRole) {
            const isValidCompany = selectedEntity && selectedEntity.entityType === 'company' && selectedEntity.accountType !== 'sub';
            if (!isValidCompany) {
              setSnackbar({ 
                open: true, 
                message: 'Select a Company entity', 
                severity: 'warning' 
              });
              return prevFormData;
            }
          } else {
            const validPartner = isActivePartnerEntity(selectedEntity);
            if (!validPartner) {
              setSnackbar({ 
                open: true, 
                message: 'Select an active Partner or Nobody', 
                severity: 'warning' 
              });
              return prevFormData;
            }
          }
          newPartnerRoles[index].entityId = value;
          newPartnerRoles[index].entityName = selectedEntity ? selectedEntity.entityName : '';
          newPartnerRoles[index].subAccountId = null; // Reset sub-account when entity changes
        }
      } else {
        newPartnerRoles[index][field] = value;
      }
      
      // Regenerate schedule and recalculate milestones so changes reflect in next milestones
      const newSchedule = regeneratePaymentSchedule(prevFormData.services, newPartnerRoles, {
        currentSchedule: prevFormData.paymentSchedule,
        clientEntityId: prevFormData.clientEntityId,
      });
      const baseForRecalc = { ...prevFormData, partnerRoles: newPartnerRoles, paymentSchedule: newSchedule };
      const recalculated = recalculateTotalsAndMilestones(newSchedule, newPartnerRoles, baseForRecalc);

      return {
        ...prevFormData,
        partnerRoles: newPartnerRoles,
        paymentSchedule: newSchedule,
        totalAmount: recalculated.totalAmount,
        totalTransportFees: recalculated.totalTransportFees,
        milestoneLogs: recalculated.milestoneLogs,
      };
    });
  };
 

  // Compute margin percent from service pricing
  const getServiceMarginPercent = (svc) => {
    const internal = Number(svc.internalUnitCost) || 0;
    const unit = Number(svc.unitPrice) || 0;
    if (internal <= 0) {
      const stored = Number(svc.marginPercent);
      return Number.isFinite(stored) ? stored : 0;
    }
    // Exclude transport component from unit before computing margin percent
    const transportPct = Number.isFinite(Number(svc.transportPercent)) ? Number(svc.transportPercent) : 0;
    const unitExTransport = Math.max(0, unit - (internal * (transportPct / 100)));
    // Round to avoid floating precision like 19.999999999
    const raw = Math.max(-100, ((unitExTransport / internal) - 1) * 100);
    return Math.round((raw + Number.EPSILON) * 100) / 100;
  };

  // Visual helper: map margin percent to chip colors (dark red -> light green @20% -> dark green @50%+)
  const getMarginChipStyles = (percentRaw) => {
    const p = Math.max(-100, Math.min(500, Number(percentRaw) || 0));
    if (p <= 0) return { bg: '#b71c1c', fg: '#ffffff', b: '#7f0000' };
    if (p >= 50) return { bg: '#1b5e20', fg: '#e8f5e9', b: '#1b5e20' };
    if (p <= 20) {
      const hue = (p / 20) * 120; // 0 (red) to 120 (green)
      return { bg: `hsl(${hue} 60% 85%)`, fg: `hsl(${hue} 50% 20%)`, b: `hsl(${hue} 45% 50%)` };
    }
    // between 20 and 50
    return { bg: '#a5d6a7', fg: '#1b5e20', b: '#66bb6a' };
  };

  // Normalize stored frequency values to one of: 'one-time' | 'monthly'
  const normalizeFrequencyValue = (raw) => {
    const s = String(raw || '').toLowerCase().replace(/_/g, '-').trim();
    if (s.includes('month')) return 'monthly';
    if (s === 'one-time' || s === 'one time' || s === 'one') return 'one-time';
    return 'one-time';
  };

  const coerceDateToMs = (value) => {
    if (!value) return null;
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }
    if (typeof value?.toDate === 'function') {
      try {
        const d = value.toDate();
        if (d instanceof Date && !Number.isNaN(d.getTime())) {
          return d.getTime();
        }
      } catch {
        return null;
      }
    }
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return value.getTime();
    }
    if (typeof value === 'object' && value !== null && typeof value.seconds === 'number') {
      const ms = (value.seconds * 1000) + Math.round((value.nanoseconds || 0) / 1e6);
      return Number.isFinite(ms) ? ms : null;
    }
    if (typeof value === 'string') {
      const parsed = Date.parse(value);
      return Number.isNaN(parsed) ? null : parsed;
    }
    return null;
  };

  const getServiceSortTimestamp = (svc) => {
    if (!svc || typeof svc !== 'object') return 0;
    const candidates = [
      svc.addedAt,
      svc.added_at,
      svc.createdAt,
      svc.created_at,
      svc.updatedAt,
      svc.updated_at,
    ];
    for (const candidate of candidates) {
      const ms = coerceDateToMs(candidate);
      if (ms !== null) {
        return ms;
      }
    }
    if (typeof svc.id === 'number' && Number.isFinite(svc.id)) {
      return svc.id;
    }
    if (typeof svc.id === 'string') {
      const numericMatch = svc.id.match(/\d{10,}/);
      if (numericMatch) {
        const parsed = Number(numericMatch[0]);
        if (Number.isFinite(parsed)) {
          return parsed;
        }
      }
    }
    return 0;
  };

  const handleServiceFieldChange = (serviceId, field, rawValue) => {
    // Use functional update to avoid stale state issues with rapid changes
    setFormData(prevFormData => {
      const updatedServices = (Array.isArray(prevFormData.services) ? [...prevFormData.services] : []);
      const idx = updatedServices.findIndex(s => s && s.id === serviceId);
      if (idx === -1) return prevFormData;

      const current = { ...updatedServices[idx] };
      let value = rawValue;

      if (field === 'quantity' || field === 'unitPrice' || field === 'internalUnitCost') {
        const num = Number(rawValue);
        value = Number.isFinite(num) ? num : 0;
      }

      if (field === 'marginPercent') {
        const marginPct = Math.max(-100, Math.min(500, Number(rawValue) || 0));
        current.marginPercent = marginPct;
        // Re-sync inclusive unit price when margin changes
        current.unitPrice = computeInclusiveUnitPrice(current.internalUnitCost, current.marginPercent, current.transportPercent);
      } else if (field === 'unitPrice') {
        // Interpret entered value as unit price EXCLUDING margin and EXCLUDING transport → internal unit cost
        const internalEntered = Math.max(0, Number(value));
        current.internalUnitCost = round2(internalEntered);
        const marginPct = Number.isFinite(Number(current.marginPercent)) ? Number(current.marginPercent) : 0;
        // Store inclusive of transport using current margin and transport percent
        current.unitPrice = computeInclusiveUnitPrice(current.internalUnitCost, marginPct, current.transportPercent);
      } else if (field === 'internalUnitCost') {
        current.internalUnitCost = Math.max(0, Number(value));
        // Recompute inclusive based on current margin and transport
        current.unitPrice = computeInclusiveUnitPrice(current.internalUnitCost, current.marginPercent, current.transportPercent);
      } else if (field === 'quantity') {
        // Preserve fractional quantities, minimum > 0
        const num = Number(value);
        current.quantity = (Number.isFinite(num) && num > 0) ? num : 1;
      } else if (field === 'frequency') {
        current.frequency = String(value) === 'monthly' ? 'monthly' : 'one-time';
      } else if (field === 'assignedEntityId') {
        current.assignedEntityId = String(value || '');
      } else if (field === 'name') {
        current.name = String(value || '');
      } else if (field === 'description') {
        current.description = String(value || '');
      } else if (field === 'category') {
        current.category = String(value || 'General');
      }

      // CRITICAL: Explicitly preserve oneTimeApplied state - never lose it
      current.oneTimeApplied = !!updatedServices[idx].oneTimeApplied;
      current.oneTimeAppliedMilestoneId = updatedServices[idx].oneTimeAppliedMilestoneId || null;

      updatedServices[idx] = current;

      const newSchedule = regeneratePaymentSchedule(updatedServices, prevFormData.partnerRoles, {
        currentSchedule: prevFormData.paymentSchedule,
        clientEntityId: prevFormData.clientEntityId,
      });
      const baseForRecalc = { ...prevFormData, services: updatedServices, paymentSchedule: newSchedule };
      const recalculated = recalculateTotalsAndMilestones(newSchedule, prevFormData.partnerRoles, baseForRecalc);
      
      return {
        ...prevFormData,
        services: updatedServices,
        paymentSchedule: newSchedule,
        totalAmount: recalculated.totalAmount,
        totalTransportFees: recalculated.totalTransportFees,
        milestoneLogs: recalculated.milestoneLogs,
      };
    });
  };

  // Blur handler to round and resync unit/margin precisely
  const handleServiceFieldBlur = (serviceId, field) => {
    // Use functional update to avoid stale state issues
    setFormData(prevFormData => {
      const updatedServices = (Array.isArray(prevFormData.services) ? [...prevFormData.services] : []);
      const idx = updatedServices.findIndex(s => s && s.id === serviceId);
      if (idx === -1) return prevFormData;
      
      const current = { ...updatedServices[idx] };
      if (field === 'unitPrice') {
        // Blur: round internal, then recompute inclusive using current margin and transport
        current.internalUnitCost = round2(current.internalUnitCost);
        const marginPct = Number.isFinite(Number(current.marginPercent)) ? Number(current.marginPercent) : 0;
        current.unitPrice = computeInclusiveUnitPrice(current.internalUnitCost, marginPct, current.transportPercent);
      } else if (field === 'marginPercent') {
        current.marginPercent = round2(Number(current.marginPercent) || 0);
      } else if (field === 'internalUnitCost') {
        current.internalUnitCost = round2(current.internalUnitCost);
      }
      
      // CRITICAL: Explicitly preserve oneTimeApplied state - never lose it
      current.oneTimeApplied = !!updatedServices[idx].oneTimeApplied;
      current.oneTimeAppliedMilestoneId = updatedServices[idx].oneTimeAppliedMilestoneId || null;
      
      updatedServices[idx] = current;
      const newSchedule = regeneratePaymentSchedule(updatedServices, prevFormData.partnerRoles, {
        currentSchedule: prevFormData.paymentSchedule,
        clientEntityId: prevFormData.clientEntityId,
      });
      const baseForRecalc = { ...prevFormData, services: updatedServices, paymentSchedule: newSchedule };
      const recalculated = recalculateTotalsAndMilestones(newSchedule, prevFormData.partnerRoles, baseForRecalc);
      
      return {
        ...prevFormData,
        services: updatedServices,
        paymentSchedule: newSchedule,
        totalAmount: recalculated.totalAmount,
        totalTransportFees: recalculated.totalTransportFees,
        milestoneLogs: recalculated.milestoneLogs,
      };
    });
  };

  

  // Function to sanitize data for Firestore (remove undefined values)
  const sanitizeForFirestore = (obj) => {
    if (obj === undefined) {
      return undefined;
    }
    if (obj === null) {
      return null;
    }
    // Preserve Date instances and Firestore sentinel/typed values (non-plain objects)
    if (obj instanceof Date) {
      return obj;
    }
    if (Array.isArray(obj)) {
      return obj.map((item) => sanitizeForFirestore(item)).filter((v) => v !== undefined);
    }
    if (typeof obj === 'object') {
      const isPlain = Object.prototype.toString.call(obj) === '[object Object]';
      if (!isPlain) {
        // Likely a Firestore FieldValue (e.g., serverTimestamp) or Timestamp
        return obj;
      }
      const sanitized = {};
      Object.keys(obj).forEach((key) => {
        const value = sanitizeForFirestore(obj[key]);
        if (value !== undefined) {
          sanitized[key] = value;
        }
      });
      return sanitized;
    }
    return obj;
  };

 

  // Enhanced contract operations with financial integration
  const addContract = async (contractData) => {
    try {
      setSaving(true);
      
      // ✅ FIX: Validate contract consistency before save
      const validation = validateContractConsistency(contractData);
      if (validation.warnings.length > 0) {
        console.warn('[addContract] Contract validation warnings:', validation.warnings);
      }
      if (!validation.valid) {
        console.error('[addContract] Contract validation failed:', validation.errors);
        throw new Error(`Contract validation failed: ${validation.errors.join(', ')}`);
      }
      
      // Validation
      if (contractData.startDate && contractData.endDate) {
        const s = new Date(`${contractData.startDate}T00:00:00`).getTime();
        const eTime = new Date(`${contractData.endDate}T00:00:00`).getTime();
        if (s && eTime && eTime <= s) {
          throw new Error('End date must be after start date');
        }
      }
      if (contractData.status === 'Active' && !isSa) {
        throw new Error('Only Super Admin can activate contracts');
      }
      if (contractData.status === 'Ready') {
        // Enforce Ready preconditions
        if (!String(contractData.title || '').trim()) throw new Error('Contract Title is required to set Ready');
        if (!contractData.startDate) throw new Error('Start date is required to set Ready');
        if (!contractData.endDate) throw new Error('End date is required to set Ready');
        const roles = Array.isArray(contractData.partnerRoles) ? contractData.partnerRoles : [];
        if (roles.length === 0) throw new Error('Partner roles must be set to set Ready');
        const nonCompany = roles.filter(r => r && r.positionId !== 'company');
        if (nonCompany.length === 0) throw new Error('At least one partner commission must be set');
        const commissionsOk = nonCompany.every(r => Number(r.percent) > 0);
        if (!commissionsOk) throw new Error('All partner commissions must be set (> 0)');
        if (!contractData.clientSignatoryName || !contractData.clientSignatoryTitle) {
          throw new Error('Client signatory name and title are required to set Ready');
        }
        const svcCount = Array.isArray(contractData.services) ? contractData.services.length : 0;
        if (svcCount < 1) throw new Error('Contract must have at least one service to set Ready');
      }
      if (contractData.status === 'Active') {
        // Enforce Active preconditions also on create
        if (!contractData.startDate) throw new Error('Start date is required to set Active');
        if (!contractData.endDate) throw new Error('End date is required to set Active');
        const roles = Array.isArray(contractData.partnerRoles) ? contractData.partnerRoles : [];
        if (roles.length === 0) throw new Error('Partner roles must be set to set Active');
        const nonCompany = roles.filter(r => r && r.positionId !== 'company');
        const validCommissionsActive = nonCompany.every(r => {
          const isNobody = !r?.entityId;
          const percent = Number(r?.percent) || 0;
          if (isNobody) return percent === 0;
          const ent = entities.find(e => e.id === r.entityId);
          return isActivePartnerEntity(ent) && percent >= 0 && percent <= 20;
        });
        if (!validCommissionsActive) throw new Error('Partner roles must be Active Partners or Nobody (0%), commissions 0-20%');
        if (!contractData.clientSignatoryName || !contractData.clientSignatoryTitle) throw new Error('Client signatory name and title are required to set Active');
        const svcCountActive = Array.isArray(contractData.services) ? contractData.services.length : 0;
        if (svcCountActive < 1) throw new Error('Contract must have at least one service to set Active');
      }
      if (contractData.beneficiaryCompanyId && contractData.beneficiaryLocked) {
        throw new Error('Cannot change beneficiary company - contract is locked');
      }

      // Get the selected proposal for deep copying
      const selectedProposal = proposals.find(p => p.id === contractData.proposalId);
      if (!selectedProposal) {
        throw new Error('Selected proposal not found');
      }

      // Convert proposal costs to contract services
      const contractServices = (selectedProposal.costs || []).map((cost, index) => {
        const service = convertCostToContractService(cost, cost.quantity || 1, contractData.beneficiaryCompanyId || '');
        // Add vendor entity name for display
        const vid = service.vendorEntityId;
        service.vendorEntityName = vid ? (entities.find(e => e.id === vid)?.entityName || '') : '';
        return service;
      });

      // Calculate transport fees
      const clientEntity = entities.find(e => e.id === contractData.clientEntityId);
      const clientCountry = clientEntity?.country || 'US';
      
      // Generate enhanced payment schedule
      const generatedSchedule = generateEnhancedPaymentSchedule(
        contractServices, 
        contractData.partnerRoles || [],
        clientCountry
      );

      // Calculate totals using 1:1 proposal parity logic
      const recalculatedAdd = recalculateTotalsAndMilestones(
        generatedSchedule,
        contractData.partnerRoles || [],
        { ...contractData, services: contractServices, paymentSchedule: generatedSchedule }
      );
      const totalAmount = recalculatedAdd.totalAmount;
      const totalTransportFees = recalculatedAdd.totalTransportFees;

      // Deep copy proposal data
      const proposalSnapshot = deepCopyProposalData(selectedProposal, contractServices);
      
      // Generate initial financial triggers
      const financialTriggers = generateFinancialTriggers({
        ...contractData,
        paymentSchedule: generatedSchedule
      }) || [];

      // Sanitize partner roles
      const cleanPartnerRoles = (contractData.partnerRoles || []).map(role => ({
        positionId: role.positionId || '',
        positionName: role.positionName || '',
        entityId: role.entityId || null,
        entityName: role.entityName || (role.entityId ? (entities.find(e => e.id === role.entityId)?.entityName || '') : (role.entityName || '')),
        subAccountId: role.subAccountId || null,
        percent: role.entityId ? (Number(role.percent) || 0) : 0,
        editable: role.editable !== undefined ? role.editable : true
      }));

      // Sanitize banking info
      const cleanBankingInfo = {
        accountNumber: contractData.bankingInfo?.accountNumber || '',
        routingNumber: contractData.bankingInfo?.routingNumber || '',
        bankName: contractData.bankingInfo?.bankName || '',
        swiftCode: contractData.bankingInfo?.swiftCode || ''
      };

      // Auto-generate milestone logs from schedule and dates if provided
      // Pass contractServices explicitly to respect oneTimeApplied flag
      const autoMilestonesAdd = generateMonthlyMilestonesFromSchedule(
        generatedSchedule,
        contractData.partnerRoles || [],
        contractData.startDate,
        contractData.endDate,
        contractData.defaultPaymentMethod,
        contractServices
      );
      // Sanitize milestone logs (prefer auto-generated when available)
      // ⚠️ CRITICAL: Preserve ALL milestone fields required for status transitions
      const cleanMilestoneLogs = (autoMilestonesAdd.length > 0 ? autoMilestonesAdd : (contractData.milestoneLogs || [])).map(milestone => ({
        id: milestone.id || Date.now(),
        milestone: milestone.milestone || '',
        expectedDate: milestone.expectedDate || '',
        actualDate: milestone.actualDate || '',
        expectedAmount: milestone.expectedAmount || 0,
        actualAmount: milestone.actualAmount || 0,
        appliedAmount: milestone.appliedAmount || 0,
        status: milestone.status || 'Scheduled',
        invoiceId: milestone.invoiceId || '',
        invoiceUrl: milestone.invoiceUrl || '',
        financialTriggers: milestone.financialTriggers || [],
        // ⚠️ CRITICAL: These fields are required for Invoiced->Paid transitions
        expectedRevenueBase: typeof milestone.expectedRevenueBase === 'number' ? milestone.expectedRevenueBase : 0,
        expectedCommission: typeof milestone.expectedCommission === 'number'
          ? milestone.expectedCommission
          : (deriveExpectedCommission(milestone) ?? 0),
        expectedTransport: typeof milestone.expectedTransport === 'number' ? milestone.expectedTransport : 0,
        expectedProcessingFee: typeof milestone.expectedProcessingFee === 'number' ? milestone.expectedProcessingFee : 0
      }));
      const mergedMilestonesForCreate = preserveInvoiceMetadata(cleanMilestoneLogs, contractData.milestoneLogs || []);

      // Determine start date locking behavior
      const isActivatingOnCreate = (contractData.status === 'Active');
      let startDateToSave = contractData.startDate;
      if (isActivatingOnCreate && !startDateToSave) {
        startDateToSave = new Date().toISOString().split('T')[0];
      }

      // Prepare clean contract data
      const cleanContractData = {
        proposalId: contractData.proposalId || '',
        title: contractData.title || '',
        status: contractData.status || 'Draft',
        clientEntityId: contractData.clientEntityId || '',
        beneficiaryCompanyId: contractData.beneficiaryCompanyId || '',
        currency: 'USD',
        defaultPaymentMethod: contractData.defaultPaymentMethod || 'Transfer',
        scheduledReceivableTiming: normalizeScheduledReceivableTiming(contractData.scheduledReceivableTiming),
        notes: contractData.notes || '',
        services: contractServices,
        paymentSchedule: generatedSchedule,
        totalAmount: totalAmount,
        totalTransportFees: totalTransportFees,
        partnerRoles: cleanPartnerRoles,
        milestoneLogs: mergedMilestonesForCreate,
        bankingInfo: cleanBankingInfo,
        financialTriggers: financialTriggers,
        serviceModifications: [],
        signedDate: toFirestoreDate(contractData.signedDate),
        startDate: toFirestoreDate(startDateToSave),
        endDate: toFirestoreDate(contractData.endDate),
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        createdBy: currentUser.uid,
        beneficiaryLocked: !!contractData.beneficiaryCompanyId,
        startDateLocked: !!contractData.startDateLocked || isActivatingOnCreate,
        clientSignatoryName: contractData.clientSignatoryName || '',
        clientSignatoryTitle: contractData.clientSignatoryTitle || '',
        ...proposalSnapshot
      };

      // Final sanitization to remove any remaining undefined values
      const sanitizedData = sanitizeForFirestore(cleanContractData);

      const docRef = await addDoc(collection(db, 'contracts'), {
        ...sanitizedData,
        shareEnabled: false,
        shareToken: null,
      });

      // Mark proposal as Contracted and link to the new contract
      try {
        if (selectedProposal?.id) {
          await updateDoc(doc(db, 'proposals', selectedProposal.id), {
            status: 'Contracted',
            linkedContractId: docRef.id,
            lastModifiedAt: serverTimestamp(),
            lastModifiedBy: currentUser?.uid || 'system'
          });
        }
      } catch (e) {
        console.warn('Failed to mark proposal as Contracted:', e);
      }

      setSnackbar({ open: true, message: 'Contract created successfully!', severity: 'success' });
      return docRef.id;
    } catch (error) {
      console.error('Error adding contract:', error);
      setSnackbar({ open: true, message: error.message || 'Error creating contract', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const updateContract = async (contractId, contractData) => {
    try {
      setSaving(true);
      
      // Backfill missing expectedRevenueBase for legacy milestones before validation/save
      const revenueBaseBackfill = backfillMissingExpectedRevenueBase(
        contractData?.milestoneLogs,
        contractData?.defaultPaymentMethod
      );
      if (revenueBaseBackfill.changed) {
        contractData = {
          ...contractData,
          milestoneLogs: revenueBaseBackfill.milestones
        };
      }

      // ✅ FIX: Validate contract consistency before save
      const validation = validateContractConsistency(contractData);
      if (validation.warnings.length > 0) {
        console.warn('[updateContract] Contract validation warnings:', validation.warnings);
      }
      if (!validation.valid) {
        console.error('[updateContract] Contract validation failed:', validation.errors);
        throw new Error(`Contract validation failed: ${validation.errors.join(', ')}`);
      }
      
      // Check editing permissions
      const existingContract = contracts.find(c => c.id === contractId);
      if (!canEditContract(existingContract)) {
        throw new Error('Cannot edit signed contracts without superadmin privileges');
      }
      // Enforce end date after start date when both provided
      if (contractData.startDate && contractData.endDate) {
        const s = new Date(`${contractData.startDate}T00:00:00`).getTime();
        const eTime = new Date(`${contractData.endDate}T00:00:00`).getTime();
        if (s && eTime && eTime <= s) {
          throw new Error('End date must be after start date');
        }
      }

      // Only super admin can set status to Active
      const isActivatingNow = (contractData.status === 'Active' && existingContract?.status !== 'Active');
      if (!canTransitionContractStatus(existingContract?.status, contractData.status, isSa)) {
        throw new Error(`Cannot transition from ${existingContract?.status || 'Unknown'} to ${contractData.status}`);
      }
      if (isActivatingNow && !isSa) {
        throw new Error('Only Super Admin can activate contracts');
      }

      // Preconditions for moving into Active from any other status (enforced at save time)
      if (isActivatingNow) {
        if (!contractData.startDate) throw new Error('Start date is required to set Active');
        if (!contractData.endDate) throw new Error('End date is required to set Active');
        const roles = Array.isArray(contractData.partnerRoles) ? contractData.partnerRoles : [];
        if (roles.length === 0) throw new Error('Partner roles must be set to set Active');
        const nonCompany = roles.filter(r => r && r.positionId !== 'company');
        const validCommissionsActive = nonCompany.every(r => {
          const isNobody = !r?.entityId;
          const percent = Number(r?.percent) || 0;
          if (isNobody) return percent === 0;
          const ent = entities.find(e => e.id === r.entityId);
          return isActivePartnerEntity(ent) && percent >= 0 && percent <= 20;
        });
        if (!validCommissionsActive) throw new Error('Partner roles must be Active Partners or Nobody (0%), commissions 0-20%');
        if (!contractData.clientSignatoryName || !contractData.clientSignatoryTitle) throw new Error('Client signatory name and title are required to set Active');
        const svcCountActive = Array.isArray(contractData.services) ? contractData.services.length : 0;
        if (svcCountActive < 1) throw new Error('Contract must have at least one service to set Active');
      }

      // Ready preconditions when transitioning into Ready
      const isSettingReadyNow = (contractData.status === 'Ready' && existingContract?.status !== 'Ready');
      if (contractData.status === 'Ready' && !String(contractData.title || '').trim()) {
        throw new Error('Contract Title is required while status is Ready');
      }
      if (isSettingReadyNow) {
        if (!contractData.startDate) throw new Error('Start date is required to set Ready');
        if (!contractData.endDate) throw new Error('End date is required to set Ready');
        const roles = Array.isArray(contractData.partnerRoles) ? contractData.partnerRoles : [];
        const allDefined = validateAllPartnerRolesDefined(roles);
        if (!allDefined.ok) throw new Error(allDefined.message || 'All partner roles must be defined and valid');
        if (!contractData.clientSignatoryName || !contractData.clientSignatoryTitle) {
          throw new Error('Client signatory name and title are required to set Ready');
        }
        const svcCount = Array.isArray(contractData.services) ? contractData.services.length : 0;
        if (svcCount < 1) throw new Error('Contract must have at least one service to set Ready');
      }

      // Beneficiary immutability check
      // Once a beneficiary is saved on a contract (even in older records without the lock flag), it cannot be changed
      if ((existingContract.beneficiaryLocked || !!existingContract.beneficiaryCompanyId) &&
          contractData.beneficiaryCompanyId !== existingContract.beneficiaryCompanyId) {
        throw new Error('Cannot change beneficiary company - contract is locked');
      }

      // Regenerate financial triggers
      const updatedTriggers = generateFinancialTriggers(contractData) || [];

      // Sanitize partner roles
      const cleanPartnerRoles = (contractData.partnerRoles || []).map(role => ({
        positionId: role.positionId || '',
        positionName: role.positionName || '',
        entityId: role.entityId || null,
        entityName: role.entityName || (role.entityId ? (entities.find(e => e.id === role.entityId)?.entityName || '') : (role.entityName || '')),
        subAccountId: role.subAccountId || null,
        percent: role.entityId ? (Number(role.percent) || 0) : 0,
        editable: role.editable !== undefined ? role.editable : true
      }));

      // Sanitize banking info
      const cleanBankingInfo = {
        accountNumber: contractData.bankingInfo?.accountNumber || '',
        routingNumber: contractData.bankingInfo?.routingNumber || '',
        bankName: contractData.bankingInfo?.bankName || '',
        swiftCode: contractData.bankingInfo?.swiftCode || ''
      };

      // Auto-generate milestone logs from schedule and dates if provided (on update, only when current logs are empty)
      // Pass services explicitly to respect oneTimeApplied flag
      const autoMilestonesUpdate = generateMonthlyMilestonesFromSchedule(
        contractData.paymentSchedule || [],
        contractData.partnerRoles || [],
        contractData.startDate,
        contractData.endDate,
        contractData.defaultPaymentMethod,
        contractData.services || []
      );
      // Sanitize milestone logs (prefer auto-generated when available and there are no existing logs)
      // ⚠️ CRITICAL: Preserve ALL milestone fields required for status transitions
      const cleanMilestoneLogs = ((Array.isArray(contractData.milestoneLogs) && contractData.milestoneLogs.length > 0) ? contractData.milestoneLogs : autoMilestonesUpdate).map(milestone => ({
        id: milestone.id || Date.now(),
        milestone: milestone.milestone || '',
        expectedDate: milestone.expectedDate || '',
        actualDate: milestone.actualDate || '',
        expectedAmount: milestone.expectedAmount || 0,
        actualAmount: milestone.actualAmount || 0,
        appliedAmount: milestone.appliedAmount || 0,
        status: milestone.status || 'Scheduled',
        invoiceId: milestone.invoiceId || '',
        invoiceUrl: milestone.invoiceUrl || '',
        financialTriggers: milestone.financialTriggers || [],
        // ⚠️ CRITICAL: These fields are required for Invoiced->Paid transitions
        expectedRevenueBase: typeof milestone.expectedRevenueBase === 'number' ? milestone.expectedRevenueBase : 0,
        expectedCommission: typeof milestone.expectedCommission === 'number'
          ? milestone.expectedCommission
          : (deriveExpectedCommission(milestone) ?? 0),
        expectedTransport: typeof milestone.expectedTransport === 'number' ? milestone.expectedTransport : 0,
        expectedProcessingFee: typeof milestone.expectedProcessingFee === 'number' ? milestone.expectedProcessingFee : 0
      }));
      const mergedMilestoneLogs = preserveInvoiceMetadata(cleanMilestoneLogs, getLatestSavedMilestoneLogs(contractId));

      // Sanitize payment schedule
      // CRITICAL: Preserve all fields needed for commission calculations and status tracking
      const cleanPaymentSchedule = (contractData.paymentSchedule || []).map(item => ({
        id: item.id || `item-${Date.now()}`,
        type: item.type || 'Service',
        serviceId: item.serviceId || '',
        serviceName: item.serviceName || '',
        description: item.description || '',
        amount: item.amount || 0,
        frequency: item.frequency || 'one-time',
        status: item.status || 'PENDING',
        category: item.category || 'General',
        entityId: item.entityId || null,
        subAccountId: item.subAccountId || null,
        invoiced: item.invoiced || false,
        paid: item.paid || false,
        partiallyPaid: item.partiallyPaid || false,
        paidAmount: item.paidAmount || 0,
        immutable: item.immutable || false,
        createdAt: item.createdAt || new Date().toISOString(),
        // CRITICAL: Preserve cancellationEffectiveYm for cancelled services
        cancellationEffectiveYm: item.cancellationEffectiveYm || null,
        // Preserve commission calculation fields
        serviceInternalUnitCost: item.serviceInternalUnitCost || 0,
        serviceQuantity: item.serviceQuantity || 0,
        partnerRolesSnapshot: item.partnerRolesSnapshot || [],
        partnerComponentTargetUsd: item.partnerComponentTargetUsd || 0,
        companyComponentTargetUsd: item.companyComponentTargetUsd || 0,
        transportFeeUsd: item.transportFeeUsd || 0,
        invoiceDate: item.invoiceDate || null,
        paymentDate: item.paymentDate || null,
        partnerRole: item.partnerRole || null,
        partnerPercent: item.partnerPercent || 0,
        parentServiceId: item.parentServiceId || null,
      }));

      // Sanitize services
      // CRITICAL: Preserve oneTimeApplied and oneTimeAppliedMilestoneId - these are persisted state
      const cleanServices = (contractData.services || []).map(service => ({
        id: service.id || `service-${Date.now()}`,
        name: service.name || '',
        description: service.description || '',
        category: service.category || 'General',
        unitPrice: service.unitPrice || 0,
        internalUnitCost: service.internalUnitCost || 0,
        marginPercent: (typeof service.marginPercent === 'number' ? service.marginPercent : (service.internalUnitCost ? (((Number(service.unitPrice) || 0) / Number(service.internalUnitCost)) - 1) * 100 : 0)),
        quantity: service.quantity || 1,
        frequency: service.frequency || 'one-time',
        vendorEntityId: service.vendorEntityId || service.vendorId || service.entity_id || null,
        assignedEntityId: service.assignedEntityId || '',
        subAccountId: service.subAccountId || null,
        originalCostId: service.originalCostId || '',
        cancellationEffectiveYm: service.cancellationEffectiveYm || null,
        // CRITICAL: Always preserve these flags - they represent invoiced state
        oneTimeApplied: !!service.oneTimeApplied,
        oneTimeAppliedMilestoneId: service.oneTimeAppliedMilestoneId || null,
      }));

      // Enforce start date locking: once Active, lock and set if missing
      const isActivating = (contractData.status === 'Active');
      const startDateLockedNext = Boolean(contractData.startDateLocked || existingContract.startDateLocked || isActivating);
      let startDateForUpdate = contractData.startDate;
      // Do not auto-fill start date; must be explicitly provided before activation as per spec
      if (startDateLockedNext) {
        // prevent changing startDate if already locked
        if (existingContract.startDateLocked && contractData.startDate && contractData.startDate !== existingContract.startDate) {
          throw new Error('Start date is locked and cannot be changed after activation');
        }
      }

      // 🔍 DEBUG: Log what we're about to save
      console.log('[updateContract] Preparing to save:', {
        timestamp: new Date().toISOString(),
        contractId,
        services: cleanServices.map(s => ({
          name: s.name,
          cancellationEffectiveYm: s.cancellationEffectiveYm
        })),
        scheduleItems: cleanPaymentSchedule.filter(i => i.type === 'Service').map(i => ({
          serviceName: i.serviceName,
          cancellationEffectiveYm: i.cancellationEffectiveYm
        }))
      });

      // Prepare clean update data
      const cleanUpdateData = {
        proposalId: contractData.proposalId || '',
        title: contractData.title || '',
        status: contractData.status || 'Draft',
        clientEntityId: contractData.clientEntityId || '',
        beneficiaryCompanyId: contractData.beneficiaryCompanyId || '',
        currency: 'USD',
        defaultPaymentMethod: contractData.defaultPaymentMethod || existingContract?.defaultPaymentMethod || 'Transfer',
        scheduledReceivableTiming: normalizeScheduledReceivableTiming(
          contractData.scheduledReceivableTiming || existingContract?.scheduledReceivableTiming
        ),
        notes: contractData.notes || '',
        partnerRoles: cleanPartnerRoles,
        paymentSchedule: cleanPaymentSchedule,
        services: cleanServices,
        milestoneLogs: mergedMilestoneLogs,
        bankingInfo: cleanBankingInfo,
        financialTriggers: updatedTriggers,
        serviceModifications: contractData.serviceModifications || [],
        totalAmount: contractData.totalAmount || 0,
        totalTransportFees: contractData.totalTransportFees || 0,
        signedDate: toFirestoreDate(contractData.signedDate),
        startDate: toFirestoreDate(startDateForUpdate || existingContract.startDate),
        endDate: toFirestoreDate(contractData.endDate),
        updatedAt: serverTimestamp(),
        // Lock beneficiary as soon as a beneficiaryCompanyId exists (cannot be changed afterward)
        beneficiaryLocked: (existingContract.beneficiaryLocked || !!existingContract.beneficiaryCompanyId || !!contractData.beneficiaryCompanyId),
        startDateLocked: startDateLockedNext,
        clientSignatoryName: contractData.clientSignatoryName || '',
        clientSignatoryTitle: contractData.clientSignatoryTitle || ''
      };

      // Final sanitization to remove any remaining undefined values
      const sanitizedData = sanitizeForFirestore(cleanUpdateData);

      const contractRef = doc(db, 'contracts', contractId);
      await updateDoc(contractRef, sanitizedData);
      
      setSnackbar({ open: true, message: 'Contract updated successfully!', severity: 'success' });
    } catch (error) {
      console.error('Error updating contract:', error);
      setSnackbar({ open: true, message: error.message || 'Error updating contract', severity: 'error' });
      throw error;
    } finally {
      setSaving(false);
    }
  };

  const deleteContract = async (contractId) => {
    try {
      const contract = contracts.find(c => c.id === contractId);
      if (contract && (contract.status === 'Signed' || contract.beneficiaryLocked)) {
        throw new Error('Cannot delete signed or locked contracts');
      }

      // Check for immutable payment items
      if (contract.paymentSchedule?.some(item => paymentStatuses[item.status]?.immutable)) {
        throw new Error('Cannot delete contracts with invoiced or paid items');
      }

      const contractRef = doc(db, 'contracts', contractId);
      await deleteDoc(contractRef);
      setSnackbar({ open: true, message: 'Contract deleted successfully!', severity: 'info' });
    } catch (error) {
      console.error('Error deleting contract:', error);
      setSnackbar({ open: true, message: error.message || 'Error deleting contract', severity: 'error' });
      throw error;
    }
  };

  useEffect(() => {
    const unsubscribeContracts = fetchContracts();
    const unsubscribeProposals = fetchProposals();
    const unsubscribeClients = fetchClients();
    const unsubscribeContacts = fetchContacts();
    const unsubscribeEntities = fetchEntities();
    const unsubscribeServices = fetchServices();
    const unsubscribeCosts = fetchCosts();
    
    return () => {
      if (unsubscribeContracts && typeof unsubscribeContracts === 'function') {
        unsubscribeContracts();
      }
      if (unsubscribeProposals && typeof unsubscribeProposals === 'function') {
        unsubscribeProposals();
      }
      if (unsubscribeClients && typeof unsubscribeClients === 'function') {
        unsubscribeClients();
      }
      if (unsubscribeContacts && typeof unsubscribeContacts === 'function') {
        unsubscribeContacts();
      }
      if (unsubscribeEntities && typeof unsubscribeEntities === 'function') {
        unsubscribeEntities();
      }
      if (unsubscribeServices && typeof unsubscribeServices === 'function') {
        unsubscribeServices();
      }
      if (unsubscribeCosts && typeof unsubscribeCosts === 'function') {
        unsubscribeCosts();
      }
    };
  }, []);

  useEffect(() => {
    let filtered = contracts;
    
    if (filters.status) {
      filtered = filtered.filter(contract => contract.status === filters.status);
    } else {
      // By default, exclude Inactive contracts from the table
      filtered = filtered.filter(contract => contract.status !== 'Inactive');
    }
    // Members never see Inactive contracts
    if (isMember()) {
      filtered = filtered.filter(contract => contract.status !== 'Inactive');
    }
    if (filters.client) {
      filtered = filtered.filter(contract => contract.clientEntityId === filters.client);
    }
    if (filters.search) {
      const term = filters.search.trim().toLowerCase();
      const clientNames = new Map(clients.map(client => [client.id, String(client.clientName || '').toLowerCase()]));
      filtered = filtered.filter(contract => (
        String(contract.title || '').toLowerCase().includes(term)
        || (clientNames.get(contract.clientEntityId) || '').includes(term)
      ));
    }
    
    setFilteredContracts(filtered);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, contracts, clients, userProfile?.role]);

  const handleOpenDialog = (contract = null) => {
    if (contract) {
      // 🔍 DEBUG: Log what we're loading from database
      console.log('[handleOpenDialog] Loading contract from database:', {
        timestamp: new Date().toISOString(),
        contractId: contract.id,
        services: contract.services?.map(s => ({
          name: s.name,
          cancellationEffectiveYm: s.cancellationEffectiveYm
        })),
        milestones: contract.milestoneLogs?.map(m => ({
          expectedDate: m.expectedDate,
          status: m.status,
          expectedAmount: m.expectedAmount,
          expectedRevenueBase: m.expectedRevenueBase,
          expectedCommission: m.expectedCommission
        }))
      });
      
      // Check editing permissions
      if (!canEditContract(contract)) {
        setSnackbar({ 
          open: true, 
          message: 'Cannot edit signed contracts without superadmin privileges', 
          severity: 'warning' 
        });
        return;
      }

      setEditingContract(contract);
      // Normalize date fields for date inputs (YYYY-MM-DD) using LOCAL time to avoid timezone shifts
      const toDateString = (v) => {
        try {
          const d = (() => {
            if (!v) return null;
            if (typeof v?.toDate === 'function') return v.toDate();
            if (v instanceof Date && !isNaN(v.getTime())) return v;
            if (typeof v === 'string') {
              // Expect YYYY-MM or YYYY-MM-DD; do not convert to Date to avoid TZ shifts
              const s = String(v).trim();
              if (s.length >= 10) return s.slice(0, 10);
              if (s.length === 7) return `${s}-01`;
              return '';
            }
            return null;
          })();
          if (!d) return '';
          if (typeof d === 'string') return d;
          const yyyy = d.getFullYear();
          const mm = String(d.getMonth() + 1).padStart(2, '0');
          const dd = String(d.getDate()).padStart(2, '0');
          return `${yyyy}-${mm}-${dd}`;
        } catch {
          return '';
        }
      };
      // expose for reuse above
      const toDateStringRef = toDateString;
      // Fallback: if older contract has no services, hydrate from proposal snapshot
      // CRITICAL: Preserve oneTimeApplied flag and cancellationEffectiveYm - these are persisted state, not derived
      const normalizedContractServices = (Array.isArray(contract.services) ? contract.services : []).map(s => ({
        ...s,
        vendorEntityId: s.vendorEntityId || s.entity_id || s.vendorId || null,
        // Explicitly preserve oneTimeApplied - never lose this flag
        oneTimeApplied: !!s.oneTimeApplied,
        oneTimeAppliedMilestoneId: s.oneTimeAppliedMilestoneId || null,
        // Explicitly preserve cancellationEffectiveYm - critical for cancelled services
        cancellationEffectiveYm: s.cancellationEffectiveYm || null,
      }));
      const normalizedSnapshotServices = (contract.proposalSnapshot?.servicesFromProposal || []).map(s => ({
        ...s,
        vendorEntityId: s.vendorEntityId || s.entity_id || s.vendorId || null,
        // Snapshot services default to false unless explicitly set
        oneTimeApplied: !!s.oneTimeApplied,
        oneTimeAppliedMilestoneId: s.oneTimeAppliedMilestoneId || null,
        // Explicitly preserve cancellationEffectiveYm
        cancellationEffectiveYm: s.cancellationEffectiveYm || null,
      }));
      
      // Prefer contract services over snapshot - contract services have the real oneTimeApplied state
      // Use 'let' so we can update to normalizedServices after regeneration
      let hydratedServices = (normalizedContractServices.length > 0)
        ? normalizedContractServices
        : normalizedSnapshotServices;

      // Check if any one-time service has been applied (invoiced) - if so, never force regenerate
      const hasAppliedOneTimeService = hydratedServices.some(
        s => normalizeFrequencyValue(s?.frequency) === 'one-time' && s?.oneTimeApplied
      );

      // If we hydrated services, regenerate schedule and milestones to ensure UI shows data
      let hydratedSchedule = contract.paymentSchedule || [];
      let hydratedMilestones = contract.milestoneLogs || [];
      const scheduleHasServiceLines = Array.isArray(hydratedSchedule) && hydratedSchedule.some(i => i && i.type === 'Service');
      const scheduleServiceLines = Array.isArray(hydratedSchedule) ? hydratedSchedule.filter(i => i && i.type === 'Service') : [];
      const scheduleAmountsAllZero = scheduleServiceLines.length > 0 && scheduleServiceLines.every(i => !(Number(i.amount || 0) > 0));
      
      // Force regeneration ONLY if:
      // 1. Proposal costs exist AND no one-time services have been applied (contract hasn't been invoiced yet)
      // 2. OR the schedule is missing/empty/all-zero
      // NEVER force regenerate if oneTimeApplied is true - that would corrupt the applied state
      const shouldForceRegenerate = !hasAppliedOneTimeService && 
        (contract.proposalSnapshot?.costs && contract.proposalSnapshot.costs.length > 0);
      
      // Show notification if we're updating the contract with corrected amounts
    
      if (shouldForceRegenerate || (((!contract.services || contract.services.length === 0) || !scheduleHasServiceLines || scheduleAmountsAllZero) && hydratedServices.length > 0 && !hasAppliedOneTimeService)) {
        // Ensure vendorEntityId exists on hydrated services
        // CRITICAL: Preserve oneTimeApplied through normalization
        const normalizedServices = hydratedServices.map(s => {
          const internal = Number(s.internalUnitCost) || 0;
          const marginPct = Number(s.marginPercent) || 0;
          const transportPct = Number(s.transportPercent) || 0;
          const fallbackUnit = Math.round((internal * (1 + (marginPct / 100)) + internal * (transportPct / 100)) * 100) / 100;
          return {
            ...s,
            vendorEntityId: s.vendorEntityId || s.entity_id || s.vendorId || null,
            unitPrice: Number(s.unitPrice) || fallbackUnit,
            // Explicitly preserve oneTimeApplied
            oneTimeApplied: !!s.oneTimeApplied,
            oneTimeAppliedMilestoneId: s.oneTimeAppliedMilestoneId || null,
            // Explicitly preserve cancellationEffectiveYm
            cancellationEffectiveYm: s.cancellationEffectiveYm || null,
          };
        });
        // Pass contract's schedule and clientEntityId to avoid stale formData closure
        const regenerated = regeneratePaymentSchedule(normalizedServices, contract.partnerRoles || [], {
          currentSchedule: contract.paymentSchedule || [],
          clientEntityId: contract.clientEntityId,
        });
        
        // ⚠️ CRITICAL: When opening a saved contract, preserve milestone values if they're valid
        // Milestones are locked at save time and should not be recalculated on load
        // Only recalculate if values are missing or invalid
        const existingMilestonesHaveValidValues = Array.isArray(hydratedMilestones) && 
          hydratedMilestones.length > 0 &&
          hydratedMilestones.every(m => {
            if (!m) return false;
            const hasValidTypes = (
              typeof m.expectedAmount === 'number' && 
              typeof m.expectedRevenueBase === 'number' &&
              typeof m.expectedCommission === 'number' &&
              typeof m.expectedProcessingFee === 'number'
            );
            const hasValidAmount = Number(m.expectedAmount) > 0 || 
              (Number(m.expectedRevenueBase) + Number(m.expectedCommission)) > 0;
            return hasValidTypes && hasValidAmount;
          });
        
        if (existingMilestonesHaveValidValues && !shouldForceRegenerate) {
          // Preserve existing milestone values - don't recalculate
          console.log('[handleOpenDialog] Preserving milestone values from database (schedule regenerated):', {
            contractId: contract.id,
            milestonesCount: hydratedMilestones.length,
            reason: 'Milestones have valid locked values - preserving saved amounts',
            preservedMilestones: hydratedMilestones.map(m => ({
              expectedDate: m.expectedDate,
              status: m.status,
              expectedAmount: m.expectedAmount
            }))
          });
          
          hydratedSchedule = regenerated;
          // Keep existing hydratedMilestones, don't recalculate
          const totalAmount = hydratedMilestones.reduce((sum, m) => sum + (Number(m.expectedAmount) || 0), 0);
          var openTotals = { totalAmount, totalTransportFees: contract.totalTransportFees || 0 };
        } else {
          // Recalculate milestones from regenerated schedule
          console.log('[handleOpenDialog] Recalculating milestones (schedule regenerated):', {
            contractId: contract.id,
            shouldForceRegenerate,
            existingMilestonesValid: existingMilestonesHaveValidValues,
            scheduleItems: regenerated.map(item => ({
              serviceName: item.serviceName,
              amount: item.amount,
              cancellationEffectiveYm: item.cancellationEffectiveYm
            }))
          });
          
          const baseForRecalc = {
            ...contract,
            startDate: toDateStringRef(contract.startDate),
            endDate: toDateStringRef(contract.endDate),
            services: normalizedServices,
            paymentSchedule: regenerated,
          };
          const recalculated = recalculateTotalsAndMilestones(regenerated, contract.partnerRoles || [], baseForRecalc);
          console.log('[handleOpenDialog] Recalculation result:', {
            recalculatedMilestones: recalculated.milestoneLogs.map(m => ({
              expectedDate: m.expectedDate,
              status: m.status,
              expectedAmount: m.expectedAmount
            }))
          });
          hydratedSchedule = regenerated;
          hydratedMilestones = recalculated.milestoneLogs;
          var openTotals = { totalAmount: recalculated.totalAmount, totalTransportFees: recalculated.totalTransportFees };
        }
        
        // CRITICAL: Update hydratedServices to use the normalized version so it gets saved to formData
        hydratedServices = normalizedServices;
      } else {
        // ⚠️ CRITICAL: Check if milestones already have valid values - if so, DON'T recalculate
        // Milestone amounts should be locked at save time and preserved on reload
        // Check that all critical fields are numbers (can be 0 for commission if no partners)
        const existingMilestonesValid = Array.isArray(hydratedMilestones) && 
          hydratedMilestones.length > 0 &&
          hydratedMilestones.every(m => {
            if (!m) return false;
            
            // All these must be numbers (not strings, not undefined)
            const hasValidTypes = (
              typeof m.expectedAmount === 'number' && 
              typeof m.expectedRevenueBase === 'number' &&
              typeof m.expectedCommission === 'number' &&
              typeof m.expectedProcessingFee === 'number'
            );
            
            // expectedAmount should be > 0 (or revenueBase + commission should be > 0)
            const hasValidAmount = Number(m.expectedAmount) > 0 || 
              (Number(m.expectedRevenueBase) + Number(m.expectedCommission)) > 0;
            
            return hasValidTypes && hasValidAmount;
          });
        
        if (existingMilestonesValid) {
          // Milestones have valid locked values - preserve them, don't recalculate
          console.log('[handleOpenDialog] Preserving existing milestone values from database:', {
            contractId: contract.id,
            milestonesCount: hydratedMilestones.length,
            reason: 'All milestones have valid locked values - preserving saved amounts'
          });
          
          // Calculate totals from existing milestones instead of recalculating
          const totalAmount = hydratedMilestones.reduce((sum, m) => sum + (Number(m.expectedAmount) || 0), 0);
          var openTotals = { 
            totalAmount, 
            totalTransportFees: contract.totalTransportFees || 0 
          };
        } else {
          // Milestones missing or invalid - recalculate
          console.log('[handleOpenDialog] Recalculating milestones (values missing or invalid):', {
            contractId: contract.id,
            milestonesCount: hydratedMilestones.length,
            existingMilestonesValid
          });
          
          const baseForRecalc2 = {
            ...contract,
            startDate: toDateStringRef(contract.startDate),
            endDate: toDateStringRef(contract.endDate),
            services: hydratedServices,
            paymentSchedule: hydratedSchedule,
          };
          const recalculated2 = recalculateTotalsAndMilestones(hydratedSchedule, contract.partnerRoles || [], baseForRecalc2);
          hydratedMilestones = recalculated2.milestoneLogs;
          var openTotals = { totalAmount: recalculated2.totalAmount, totalTransportFees: recalculated2.totalTransportFees };
        }
      }

      // Backfill any missing expected amounts on Scheduled/Invoiced lines
      // Only do this when schedule is missing/empty/all-zero to avoid overwriting real values
      const allowParityBackfill = !scheduleHasServiceLines || scheduleAmountsAllZero;
      if (allowParityBackfill) {
        try {
        const methodStr = String(contract.defaultPaymentMethod || 'Transfer').toLowerCase();
        const pct = (methodStr === 'credit card' || methodStr === 'creditcard' || methodStr === 'credit_card')
          ? creditCardProcessingPercent
          : (methodStr === 'check' ? checkProcessingPercent : 0);
        // Compute baseline monthly/one-time from proposal parity (works even if schedule lacks internals)
        const parity = computeProposalParityTotals({
          proposalSnapshot: contract.proposalSnapshot,
          partnerRoles: contract.partnerRoles,
          defaultPaymentMethod: contract.defaultPaymentMethod,
          services: hydratedServices,
        });
        // Only add one-time parity amounts if there are unapplied one-time services
        const hasUnappliedOneTime = (Array.isArray(hydratedServices) ? hydratedServices : []).some(
          (s) => normalizeFrequencyValue(s?.frequency || 'one-time') === 'one-time' && !s?.oneTimeApplied
        );
        const firstIdx = (Array.isArray(hydratedMilestones) ? hydratedMilestones : []).findIndex(m => m && m.status === 'Scheduled');
        for (let i = 0; i < hydratedMilestones.length; i++) {
          const m = hydratedMilestones[i];
          if (!m || (m.status !== 'Scheduled' && m.status !== 'Invoiced')) continue;
          const hasExpected = Number(m.expectedAmount || 0) > 0;
          const baseIsZero = !hasExpected && !((Number(m.expectedRevenueBase || 0) + Number(m.expectedCommission || 0)) > 0);
          if (hasExpected && !baseIsZero) continue;
          // Derive base components from parity totals
          const addOneTime = (i === firstIdx) && hasUnappliedOneTime;
          const baseRevenue = (parity.monthlyBase || 0) + (addOneTime ? (parity.oneTimeBase || 0) : 0);
          const commission = (parity.monthlyCommission || 0) + (addOneTime ? (parity.oneTimeCommission || 0) : 0);
          const baseComponents = baseRevenue + commission; // transport already in base
          const fee = Math.round((baseComponents * (pct / 100)) * 100) / 100;
          hydratedMilestones[i] = {
            ...m,
            expectedRevenueBase: Math.round(baseRevenue * 100) / 100,
            expectedTransport: 0,
            expectedCommission: Math.round(commission * 100) / 100,
            expectedProcessingFee: fee,
            expectedAmount: Math.round((baseComponents + fee) * 100) / 100,
          };
        }
        } catch (_) { /* ignore backfill errors */ }
      }

      // Ensure invoice metadata entered elsewhere (e.g., Accounts Receivable) is not lost in dialog hydration.
      // This keeps the Contracts "Payments" table aligned with AR view.
      hydratedMilestones = preserveInvoiceMetadata(hydratedMilestones, contract.milestoneLogs || []);

      // Backfill missing expectedCommission for legacy milestones without changing amounts.
      hydratedMilestones = (Array.isArray(hydratedMilestones) ? hydratedMilestones : []).map((m) => {
        if (!m || typeof m.expectedCommission === 'number') return m;
        const derivedCommission = deriveExpectedCommission(m);
        if (derivedCommission === null) return m;
        return { ...m, expectedCommission: derivedCommission };
      });

      // Backfill missing expectedRevenueBase for legacy milestones without changing amounts.
      const revenueBaseBackfill = backfillMissingExpectedRevenueBase(
        hydratedMilestones,
        contract.defaultPaymentMethod
      );
      hydratedMilestones = revenueBaseBackfill.milestones;

      setFormData({ 
        ...contract,
        signedDate: toDateString(contract.signedDate),
        startDate: toDateString(contract.startDate),
        endDate: toDateString(contract.endDate),
        partnerRoles: contract.partnerRoles || [],
        milestoneLogs: hydratedMilestones,
        bankingInfo: contract.bankingInfo || {},
        paymentSchedule: hydratedSchedule,
        services: hydratedServices,
        transportFees: contract.transportFees || [],
        financialTriggers: contract.financialTriggers || [],
        serviceModifications: contract.serviceModifications || [],
        clientSignatoryName: contract.clientSignatoryName || '',
        clientSignatoryTitle: contract.clientSignatoryTitle || '',
        scheduledReceivableTiming: normalizeScheduledReceivableTiming(contract.scheduledReceivableTiming),
        totalAmount: Number.isFinite(Number(openTotals?.totalAmount)) ? Number(openTotals.totalAmount) : (contract.totalAmount || 0),
        totalTransportFees: Number.isFinite(Number(openTotals?.totalTransportFees)) ? Number(openTotals.totalTransportFees) : (contract.totalTransportFees || 0)
      });
    } else {
      setEditingContract(null);
      const activeCompanies = getBeneficiaryCompanyOptions();
      setFormData({
        proposalId: '',
        title: '',
        status: 'Draft',
        clientEntityId: '',
        // Default the beneficiary only when there is exactly one active company to choose from
        beneficiaryCompanyId: activeCompanies.length === 1 ? activeCompanies[0].id : '',
        currency: 'USD',
        defaultPaymentMethod: 'Transfer',
        scheduledReceivableTiming: DEFAULT_SCHEDULED_RECEIVABLE_TIMING,
        bankingInfo: {
          accountNumber: '',
          routingNumber: '',
          bankName: '',
          swiftCode: ''
        },
        // partnerRoles will be inherited from proposal selection; keep empty by default
        partnerRoles: [],
        paymentSchedule: [],
        services: [],
        transportFees: [],
        milestoneLogs: [],
        signedDate: '',
        startDate: '',
        endDate: '',
        notes: '',
        totalAmount: 0,
        totalTransportFees: 0,
        serviceModifications: [],
        financialTriggers: [],
        createdBy: currentUser.uid,
        beneficiaryLocked: false,
        startDateLocked: false,
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    // Prevent closing while operations are in progress
    if (saving || operationInProgress) {
      setSnackbar({
        open: true,
        message: 'Please wait for the current operation to complete before closing',
        severity: 'warning'
      });
      return;
    }
    
    setOpenDialog(false);
    setEditingContract(null);
    setCurrentTab(0);
    setShowServiceModification(false);
    openedContractIdRef.current = null;

    if (routeContractId || new URLSearchParams(location?.search || '').get('openContractId')) {
      navigate('/contracts', { replace: true });
    }
  };

  const handleOpenContractByUrl = (contract) => {
    if (!contract?.id) return;
    openedContractIdRef.current = contract.id;
    navigate(getContractDirectPath(contract.id));
    handleOpenDialog(contract);
  };

  const handleSubmit = async () => {
    try {
      // Validate partner commissions
      const invalidCommissions = formData.partnerRoles.filter(role => 
        role.positionId !== 'company' && !validatePartnerCommission(role.percent)
      );
      
      if (invalidCommissions.length > 0) {
        setSnackbar({ 
          open: true, 
          message: 'All partner commissions must be between 0% and 20%', 
          severity: 'error' 
        });
        return;
      }

      // Recalculate milestone fees on submit (always, to reflect payment method changes)
      const withRecalculated = (() => {
        const next = { ...formData };
        try {
          const logs = Array.isArray(next.milestoneLogs) ? next.milestoneLogs.map(m => ({ ...m })) : [];
          const live = proposals.find((p) => p && p.id === next.proposalId);
          {
            const method = String(next.defaultPaymentMethod || '').toLowerCase();
            const pct = (method === 'credit card' || method === 'creditcard' || method === 'credit_card')
              ? creditCardProcessingPercent
              : (method === 'check' ? checkProcessingPercent : 0);
            for (let i = 0; i < logs.length; i++) {
              const m = logs[i];
              const st = String(m?.status || '');
              // Only recalculate for Scheduled status, not Invoiced
              if (st !== 'Scheduled') continue;
              const baseComponents = (
                Number(m?.expectedRevenueBase || 0) +
                Number(m?.expectedTransport || 0) +
                Number(m?.expectedCommission || 0)
              );
              if (baseComponents > 0) {
                const fee = Math.round((baseComponents * (pct / 100)) * 100) / 100;
                const total = Math.round((baseComponents + fee) * 100) / 100;
                logs[i].expectedProcessingFee = fee;
                logs[i].expectedAmount = total;
              }
            }
          }
          next.milestoneLogs = logs;
        } catch (_) { /* ignore */ }
        return next;
      })();

      const contractData = {
        ...withRecalculated,
        partnerRoles: formData.partnerRoles,
        beneficiaryLocked: formData.beneficiaryLocked,
      };

      if (editingContract) {
        await updateContract(editingContract.id, contractData);
      } else {
        await addContract(contractData);
      }
      
      handleCloseDialog();
    } catch (error) {
      // Error handling is done in the respective functions
    }
  };

  const handleSave = async () => {
    try {
      const invalidCommissions = formData.partnerRoles.filter(role => 
        role.positionId !== 'company' && !validatePartnerCommission(role.percent)
      );
      if (invalidCommissions.length > 0) {
        setSnackbar({ 
          open: true, 
          message: 'All partner commissions must be between 0% and 20%', 
          severity: 'error' 
        });
        return;
      }
      if (formData.startDate && formData.endDate) {
        const s = new Date(`${formData.startDate}T00:00:00`).getTime();
        const eTime = new Date(`${formData.endDate}T00:00:00`).getTime();
        if (s && eTime && eTime <= s) {
          setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' });
          return;
        }
      }

      // Recalculate milestone fees on save (always, to reflect payment method changes)
      const withRecalculated = (() => {
        const next = { ...formData };
        try {
          const logs = Array.isArray(next.milestoneLogs) ? next.milestoneLogs.map(m => ({ ...m })) : [];
          const live = proposals.find((p) => p && p.id === next.proposalId);
          {
            const method = String(next.defaultPaymentMethod || '').toLowerCase();
            const pct = (method === 'credit card' || method === 'creditcard' || method === 'credit_card')
              ? creditCardProcessingPercent
              : (method === 'check' ? checkProcessingPercent : 0);
            for (let i = 0; i < logs.length; i++) {
              const m = logs[i];
              const st = String(m?.status || '');
              // Only recalculate for Scheduled status, not Invoiced
              if (st !== 'Scheduled') continue;
              const baseComponents = (
                Number(m?.expectedRevenueBase || 0) +
                Number(m?.expectedTransport || 0) +
                Number(m?.expectedCommission || 0)
              );
              if (baseComponents > 0) {
                const fee = Math.round((baseComponents * (pct / 100)) * 100) / 100;
                const total = Math.round((baseComponents + fee) * 100) / 100;
                logs[i].expectedProcessingFee = fee;
                logs[i].expectedAmount = total;
              }
            }
          }
          next.milestoneLogs = logs;
        } catch (error) {
          console.error('Error recalculating milestone fees:', error);
         }
        return next;
      })();

      const contractData = {
        ...withRecalculated,
        partnerRoles: formData.partnerRoles,
        beneficiaryLocked: formData.beneficiaryLocked,
      };

      if (editingContract) {
        await updateContract(editingContract.id, contractData);
      } else {
        const newId = await addContract(contractData);
        setEditingContract({ ...contractData, id: newId });
        openedContractIdRef.current = newId;
        navigate(getContractDirectPath(newId), { replace: true });
      }
    } catch (error) {
      // handled in called functions
    }
  };

  const saveInvoiceMeta = async (milestoneId, field, value, options = {}) => {
    if (!editingContract?.id) return;
    if (!canEditInvoiceMeta) return;
    
    operationsInProgress.current++;
    setOperationInProgress(true);
    try {
      const latestSavedMilestoneLogs = getLatestSavedMilestoneLogs(editingContract.id);
      const localLogs = Array.isArray(formData.milestoneLogs) ? formData.milestoneLogs : [];
      const logs = preserveInvoiceMetadata(localLogs, latestSavedMilestoneLogs).map((m) => ({ ...m }));
      const idx = logs.findIndex((m) => m && m.id === milestoneId);
      if (idx < 0) return;
      const nextValue = (value ?? '').toString().trim();
      if (field === 'invoiceId') {
        logs[idx].invoiceId = nextValue;
      } else if (field === 'invoiceUrl') {
        logs[idx].invoiceUrl = nextValue;
      } else {
        return;
      }
      const logsToPersist = preserveInvoiceMetadata(logs, latestSavedMilestoneLogs);
      setFormData((prev) => ({ ...prev, milestoneLogs: logsToPersist }));
      await updateDoc(doc(db, 'contracts', editingContract.id), { milestoneLogs: logsToPersist });
      if (!options?.silent) {
        setSnackbar({ open: true, message: 'Invoice details saved', severity: 'success' });
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to save invoice details', severity: 'error' });
    } finally {
      operationsInProgress.current--;
      if (operationsInProgress.current === 0) {
        setOperationInProgress(false);
      }
    }
  };

  const queueInvoiceSave = (milestoneId, field, value) => {
    if (!editingContract?.id) return;
    if (!canEditInvoiceMeta) return;
    const key = `${milestoneId}:${field}`;
    if (invoiceSaveTimers.current[key]) {
      clearTimeout(invoiceSaveTimers.current[key]);
    }
    invoiceSaveTimers.current[key] = setTimeout(() => {
      saveInvoiceMeta(milestoneId, field, value, { silent: true });
      delete invoiceSaveTimers.current[key];
    }, 600);
  };

  useEffect(() => () => {
    Object.values(invoiceSaveTimers.current).forEach((t) => clearTimeout(t));
    invoiceSaveTimers.current = {};
  }, []);

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this contract? This action cannot be undone.')) {
      try {
        await deleteContract(id);
      } catch (error) {
        // Error handling is done in the deleteContract function
      }
    }
  };

  // Helper functions for UI
  const getStatusColor = (status) => {
    if (typeof status === 'object' && status.label) {
      return status.color;
    }
    // Backward compatibility
    switch (status) {
      case 'Draft': return 'default';
      case 'Signed': return 'info';
      case 'Active': return 'success';
      case 'Complete': return 'primary';
      case 'Archived': return 'default';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  // const getPaymentStatusColor = (status) => {
  //   return paymentStatuses[status]?.color || 'default';
  // };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getEntityName = (entityId) => {
    const entity = entities.find(e => e.id === entityId);
    return entity ? entity.entityName : 'Unassigned';
  };

  // Resolve vendor display name from service, falling back to snapshot or ID
  const getVendorName = (svc) => {
    if (!svc) return 'Unassigned';
    const id = svc.vendorEntityId || svc.entity_id || svc.vendorId || null;
    if (id) {
      const ent = entities.find(e => e.id === id);
      if (ent) return ent.entityName;
    }
    if (svc.vendorEntityName) return svc.vendorEntityName;
    return 'Unassigned';
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client ? client.clientName : 'Unknown Client';
  };

  const getClientBillingContactEmail = (clientId) => {
    const billingContact = (Array.isArray(contacts) ? contacts : []).find((contact) => (
      (contact?.associatedClient === clientId || contact?.clientId === clientId) &&
      (contact?.isPrimaryBillingContact === true || contact?.isPrimary === true) &&
      String(contact?.email || '').trim()
    ));
    return String(billingContact?.email || '').trim();
  };

  const getClientDriveUrl = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    return client?.googleDriveURL || '';
  };

  // const isClientActive = (client) => {
  //   return String(client?.status || 'Active').toLowerCase() === 'active';
  // };

  const getProposalTitle = (proposalId) => {
    const proposal = proposals.find(p => p.id === proposalId);
    if (proposal) return proposal.title;
    // Fallback: when proposal has moved to Contracted and is no longer in the proposals list,
    // try to resolve from any contract snapshot that references this proposalId
    const fromSnapshot = contracts.find(c => c && c.proposalId === proposalId && c.proposalSnapshot && c.proposalSnapshot.title);
    return fromSnapshot?.proposalSnapshot?.title || 'Unknown Proposal';
  };

  // Fallback provider for service options when master list is empty
  const getAvailableServicesForDropdown = () => {
    const sortByName = (arr) => ([...(arr || [])].sort((a, b) => String(a?.name || '').localeCompare(String(b?.name || ''), undefined, { sensitivity: 'base' })));
    // Prefer explicit master lists; fall back to items already on the contract/snapshot
    if (Array.isArray(services) && services.length > 0) return sortByName(services);
    if (Array.isArray(costs) && costs.length > 0) return sortByName(costs);
    if (Array.isArray(formData?.services) && formData.services.length > 0) return sortByName(formData.services);
    if (formData?.proposalSnapshot?.servicesFromProposal && formData.proposalSnapshot.servicesFromProposal.length > 0) {
      return sortByName(formData.proposalSnapshot.servicesFromProposal);
    }
    return [];
  };

  // const calculateCompletionPercentage = (contract) => {
  //   const paidMilestones = contract.milestoneLogs.filter(m => m.status === 'Paid').length;
  //   return contract.milestoneLogs.length > 0 ? (paidMilestones / contract.milestoneLogs.length) * 100 : 0;
  // };

  // const getTotalScheduleValue = (schedule) => {
  //   return schedule.reduce((total, item) => total + (item.amount || 0), 0);
  // };

  // const getRemainingPercentage = (partnerRoles) => {
  //   const totalAllocated = partnerRoles
  //     .filter(role => role.positionId !== 'company')
  //     .reduce((sum, role) => sum + (role.percent || 0), 0);
  //   return Math.max(0, 100 - totalAllocated);
  // };

  // const getTransportFeeInfo = (countryCode) => {
  //   return transportFees[countryCode] || transportFees.default;
  // };

  // Ensure milestone sequence numbers are always in order
  const reindexMilestones = (milestones) => {
    return (milestones || []).map((milestone, index) => ({
      ...milestone,
      milestone: String(index + 1),
    }));
  };

  function getLatestSavedMilestoneLogs(contractId = editingContract?.id) {
    const latestContract = Array.isArray(contracts)
      ? contracts.find((contract) => contract && contract.id === contractId)
      : null;
    if (Array.isArray(latestContract?.milestoneLogs)) return latestContract.milestoneLogs;
    if (Array.isArray(editingContract?.milestoneLogs)) return editingContract.milestoneLogs;
    return Array.isArray(formData?.milestoneLogs) ? formData.milestoneLogs : [];
  }

  function preserveInvoiceMetadata(nextLogs = [], prevLogs = []) {
    const previousLogs = Array.isArray(prevLogs) ? prevLogs : [];
    const buildKeys = (log, idx) => {
      if (!log) return [];
      const keys = [];
      if (log.id !== undefined && log.id !== null) keys.push(`id:${String(log.id)}`);
      if (log.expectedDate) keys.push(`date:${String(log.expectedDate)}`);
      keys.push(`sequence:${String(log.milestone || idx)}`);
      return keys;
    };
    const prevByKey = new Map();
    previousLogs.forEach((log, idx) => {
      if (!log) return;
      buildKeys(log, idx).forEach((key) => {
        if (!prevByKey.has(key)) prevByKey.set(key, log);
      });
    });
    return (Array.isArray(nextLogs) ? nextLogs : []).map((log, idx) => {
      if (!log) return log;
      const prev = buildKeys(log, idx).map((key) => prevByKey.get(key)).find(Boolean);
      if (!prev) return log;
      const merged = { ...log };
      const hasPostingRefs = Array.isArray(merged.postingRefs) && merged.postingRefs.length > 0;
      const prevHasPostingRefs = Array.isArray(prev.postingRefs) && prev.postingRefs.length > 0;
      if (!hasPostingRefs && prevHasPostingRefs) merged.postingRefs = prev.postingRefs;
      // Treat empty strings as "missing" so regenerated milestones don't wipe existing metadata
      const missingInvoiceId = (merged.invoiceId === undefined || merged.invoiceId === null || String(merged.invoiceId).trim() === '');
      const missingInvoiceUrl = (merged.invoiceUrl === undefined || merged.invoiceUrl === null || String(merged.invoiceUrl).trim() === '');
      const prevHasInvoiceId = !(prev.invoiceId === undefined || prev.invoiceId === null || String(prev.invoiceId).trim() === '');
      const prevHasInvoiceUrl = !(prev.invoiceUrl === undefined || prev.invoiceUrl === null || String(prev.invoiceUrl).trim() === '');
      if (missingInvoiceId && prevHasInvoiceId) merged.invoiceId = prev.invoiceId;
      if (missingInvoiceUrl && prevHasInvoiceUrl) merged.invoiceUrl = prev.invoiceUrl;

      // Deposit linkage is immutable row metadata. Milestone regeneration/sanitization
      // must never make a deposited receivable appear available for deposit again.
      const exactRow = previousLogs.find((candidate) => (
        candidate
        && candidate.id !== undefined
        && candidate.id !== null
        && log.id !== undefined
        && log.id !== null
        && String(candidate.id) === String(log.id)
      ));
      const samePeriodRows = log.expectedDate
        ? previousLogs.filter((candidate) => (
          candidate
          && String(candidate.expectedDate || '') === String(log.expectedDate)
          && String(candidate.status || '') === String(log.status || '')
        ))
        : [];
      const linkedPreviousRow = exactRow || (samePeriodRows.length === 1 ? samePeriodRows[0] : null);
      if (linkedPreviousRow) {
        [
          'autoDepositBankLineId',
          'autoDepositClientBalanceEntryId',
          'autoDepositFeeBankLineId',
          'autoDepositLedgerEntryId',
          'autoDepositAmountUsd',
          'autoDepositFeeUsd',
          'autoDepositPaymentMethod',
          'autoDepositedAt',
          'autoDepositedBy',
          'clientBalanceTracked',
        ].forEach((field) => {
          const nextValueMissing = merged[field] === undefined || merged[field] === null || merged[field] === '';
          if (nextValueMissing && linkedPreviousRow[field] !== undefined) {
            merged[field] = linkedPreviousRow[field];
          }
        });
      }
      return merged;
    });
  }

  const formatHumanDate = (value, opts = { includeDay: true }) => {
    if (!value) return '';
    let d = null;
    if (typeof value?.toDate === 'function') d = value.toDate();
    if (!d && value instanceof Date) d = value;
    if (!d && typeof value === 'string') {
      // Try YYYY-MM-DD then YYYY-MM
      d = new Date(/^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00:00` : `${value}-01T00:00:00`);
    }
    if (!d || isNaN(d.getTime())) return String(value);
    const months = ['January','February','March','April','May','June','July','August','September','October','November','December'];
    const day = d.getDate();
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return opts.includeDay ? `${day} ${month}, ${year}` : `${month} ${year}`;
  };

  const formatTerm = (start, end) => {
    if (!start && !end) return '';
    if (start && end) {
      const s = formatHumanDate(start, { includeDay: false });
      const e = formatHumanDate(end, { includeDay: false });
      return `${s} to ${e}`;
    }
    if (start) return `from ${formatHumanDate(start, { includeDay: false })}`;
    return `until ${formatHumanDate(end, { includeDay: false })}`;
  };

  const getClientDetails = (clientId) => {
    const c = clients.find(x => x.id === clientId) || {};
    const line1 = c.streetAddress || c.addressLine1 || c.address1 || c.street || c.street1 || c.address || '';
    const city = c.city || '';
    const state = c.state || c.region || '';
    const zip = c.zip || c.postalCode || '';
    const country = c.country || '';
    const phone = c.phone || c.phoneNumber || c.mobile || c.contactPhone || '';
    const addressLines = [];
    if (line1) addressLines.push(line1);
    const line2 = [city, state, zip].filter(Boolean).join(', ');
    if (line2) addressLines.push(line2);
    if (country) addressLines.push(country);
    return {
      name: c.clientName || getClientName(clientId),
      addressLines,
      phone,
      billingEmail: getClientBillingContactEmail(clientId),
    };
  };

  // const stripHtml = (html) => {
  //   if (!html) return '';
  //   const text = String(html)
  //     .replace(/<\/(p|div|li|h1|h2|h3|h4|h5|h6)>/g, '\n')
  //     .replace(/<br\s*\/?>(\s*\n)*/gi, '\n')
  //     .replace(/<li>/g, '• ')
  //     .replace(/<[^>]*>/g, '')
  //     .replace(/&nbsp;/g, ' ')
  //     .replace(/&amp;/g, '&')
  //     .replace(/&lt;/g, '<')
  //     .replace(/&gt;/g, '>');
  //   return text.replace(/\n{3,}/g, '\n\n').trim();
  // };

  // Helper: convert ArrayBuffer to base64 string for jsPDF VFS
  const arrayBufferToBase64 = (buffer) => {
    try {
      let binary = '';
      const bytes = new Uint8Array(buffer);
      const len = bytes.byteLength;
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
      }
      return (typeof btoa === 'function' ? btoa(binary) : window.btoa(binary));
    } catch (_) {
      return '';
    }
  };

  // Helper: fetch and register WhisperingSignature TTF if available under /fonts
  const fetchAndRegisterSignatureFont = async (doc) => {
    try {
      const res = await fetch('/fonts/WhisperingSignature.ttf', { cache: 'force-cache' });
      if (!res.ok) return false;
      const buf = await res.arrayBuffer();
      const base64 = arrayBufferToBase64(buf);
      if (!base64) return false;
      doc.addFileToVFS('WhisperingSignature.ttf', base64);
      doc.addFont('WhisperingSignature.ttf', 'WhisperingSignature', 'normal');
      return true;
    } catch (_) {
      return false;
    }
  };

  const generateContractPDF = async (contract) => {
    const doc = new jsPDF('p', 'pt', 'a4');
    const margin = 50;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const usableWidth = pageWidth - margin * 2;

    // Proposal PDF design tokens (keep contract content intact; change only presentation)
    const UCENA_RED = { r: 244, g: 67, b: 54 }; // #F44336
    const LIGHT_GRAY = { r: 240, g: 240, b: 240 }; // #F0F0F0

    // Try to load custom signature font once per document
    const signatureFontReady = await fetchAndRegisterSignatureFont(doc);

    const client = getClientDetails(contract.clientEntityId);
    const contractTitle = contract.title || 'Project';
    const effectiveDate = contract.signedDate || contract.startDate || new Date().toISOString().split('T')[0];
    const term = formatTerm(contract.startDate, contract.endDate);

    const documentTitle = (pdfTexts?.contracts?.documentTitle) || 'Services Agreement';
    const clientBlockName = client.name || 'Client';

    const applyContentBackground = () => {
      doc.setFillColor(LIGHT_GRAY.r, LIGHT_GRAY.g, LIGHT_GRAY.b);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      doc.setTextColor(0, 0, 0);
    };

    const loadPdfImage = (src) => new Promise((resolve, reject) => {
      const i = new Image();
      i.crossOrigin = 'Anonymous';
      i.onload = () => resolve(i);
      i.onerror = (e) => reject(e);
      i.src = src;
    });

    const drawCoverBackground = async () => {
      try {
        const bg = await loadPdfImage(ucenaContractCoverBackground);
        const bgProps = doc.getImageProperties(bg);
        const scale = Math.max(pageWidth / bgProps.width, pageHeight / bgProps.height);
        const bgWidth = bgProps.width * scale;
        const bgHeight = bgProps.height * scale;
        const bgX = (pageWidth - bgWidth) / 2;
        const bgY = (pageHeight - bgHeight) / 2;
        doc.addImage(bg, 'PNG', bgX, bgY, bgWidth, bgHeight);
        return true;
      } catch (_) {
        doc.setFillColor(UCENA_RED.r, UCENA_RED.g, UCENA_RED.b);
        doc.rect(0, 0, pageWidth, pageHeight, 'F');
        return false;
      }
    };

    const loadLogo = async () => {
      try {
        const branding = (typeof getBrandingConfig === 'function') ? getBrandingConfig() : {};
        const logoSrc = branding?.logo || '/assets/logo.png';
        const img = await loadPdfImage(logoSrc);
        return img;
      } catch (_) {
        return null;
      }
    };

    const drawCoverPage = async () => {
      const coverBackgroundLoaded = await drawCoverBackground();
      if (coverBackgroundLoaded) return;

      // Logo (best-effort) only when falling back to the legacy solid background.
      const img = await loadLogo();
      if (img) {
        try {
          const imgProps = doc.getImageProperties(img);
          const logoHeight = 30;
          const logoWidth = (imgProps.width * logoHeight) / imgProps.height;
          doc.addImage(img, 'PNG', margin, margin, logoWidth, logoHeight);
        } catch (_) {
          // ignore
        }
      } else {
        doc.setTextColor(255, 255, 255);
        doc.setFont('helvetica', 'bold');
        doc.setFontSize(24);
        doc.text('UCENA', margin, margin + 24);
      }

      // Title (match proposal cover typography)
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(48);
      const titleParts = String(documentTitle || '').toUpperCase().split(' ').filter(Boolean);
      const line1 = titleParts.slice(0, Math.ceil(titleParts.length / 2)).join(' ') || documentTitle;
      const line2 = titleParts.slice(Math.ceil(titleParts.length / 2)).join(' ');
      const titleY = pageHeight * 0.37;
      doc.text(line1, margin, titleY);
      if (line2) doc.text(line2, margin, titleY + 58);

      // Prepared for (uses existing content only)
      doc.setFont('times', 'roman');
      doc.setFontSize(15);
      const coverInfoY = pageHeight * 0.675;
      doc.text('Prepared for:', margin, coverInfoY);
      doc.text(`${clientBlockName} | ${contractTitle}`, margin, coverInfoY + 22);

      // Footer Info (match proposal cover)
      doc.setFont('times', 'roman');
      doc.setFontSize(14);
      const footerY = pageHeight - margin;
      doc.text('www.ucena.com', margin, footerY - 48);
      doc.text('info@ucena.com', margin, footerY - 24);
      doc.text('30 North Gould Street, Sheridan, WY 82801', margin, footerY);
    };

    // --- COVER PAGE ---
    await drawCoverPage();

    // --- CONTENT PAGES ---
    doc.addPage();
    applyContentBackground();

    let y = margin;

    const newContentPage = () => {
      doc.addPage();
      applyContentBackground();
      y = margin;
    };

    const ensureSpace = (heightNeeded) => {
      if (y + heightNeeded > pageHeight - margin) {
        newContentPage();
      }
    };

    const addLines = (lines, fontSize = 11, bold = false, color = { r: 0, g: 0, b: 0 }) => {
      const lineStep = fontSize + 4;
      doc.setFont('helvetica', bold ? 'bold' : 'normal');
      doc.setFontSize(fontSize);
      doc.setTextColor(color.r, color.g, color.b);

      (Array.isArray(lines) ? lines : [lines]).forEach((ln) => {
        const chunks = Array.isArray(ln) ? ln : [ln];
        chunks.forEach((text) => {
          const wrapped = doc.splitTextToSize(String(text || ''), usableWidth);
          wrapped.forEach((row) => {
            ensureSpace(lineStep);
            doc.text(String(row), margin, y);
            y += lineStep;
          });
        });
      });
      y += 4;
      doc.setTextColor(0, 0, 0);
    };

    const addCenteredHeading = (text, fontSize = 14) => {
      ensureSpace(fontSize + 20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(String(text || ''), pageWidth / 2, y, { align: 'center' });
      y += fontSize + 16;
      doc.setTextColor(0, 0, 0);
    };

    const addLeftHeading = (text, fontSize = 13) => {
      ensureSpace(fontSize + 20);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(fontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(String(text || ''), margin, y);
      y += fontSize + 12;
      doc.setTextColor(0, 0, 0);
    };

    const normalizeLines = (lines) => {
      const out = [];
      (Array.isArray(lines) ? lines : [lines]).forEach((ln) => {
        const s = String(ln ?? '').trim();
        if (!s) return;
        // Avoid visually large gaps if upstream provides repeated empties
        if (out.length > 0 && out[out.length - 1] === '' && s === '') return;
        out.push(s);
      });
      return out;
    };

    const drawPartiesCard = (betweenTitle, betweenLines, andTitle, andLines) => {
      const cardX = margin;
      const cardWidth = usableWidth;
      const padX = 18;
      const padY = 16;
      const textX = cardX + padX;
      const textWidth = cardWidth - (padX * 2);
      const headingFontSize = 13;
      const bodyFontSize = 11;
      const headingStep = 18;
      const bodyStep = 15;

      const betweenWrapped = normalizeLines(betweenLines).flatMap((ln) => doc.splitTextToSize(String(ln), textWidth));
      const andWrapped = normalizeLines(andLines).flatMap((ln) => doc.splitTextToSize(String(ln), textWidth));

      const betweenBlockHeight = headingStep + (betweenWrapped.length * bodyStep);
      const andBlockHeight = headingStep + (andWrapped.length * bodyStep);
      const dividerGapTop = 8;
      const dividerGapBottom = 14;
      const cardHeight = padY + betweenBlockHeight + dividerGapTop + dividerGapBottom + andBlockHeight + padY;

      ensureSpace(cardHeight + 10);

      // Card background
      doc.setFillColor(255, 255, 255);
      doc.setDrawColor(220, 220, 220);
      doc.setLineWidth(1);
      doc.rect(cardX, y, cardWidth, cardHeight, 'FD');

      let cy = y + padY + 12;

      // Between
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(headingFontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(String(betweenTitle || 'Between:'), textX, cy);
      cy += headingStep;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(bodyFontSize);
      doc.setTextColor(0, 0, 0);
      betweenWrapped.forEach((row) => {
        doc.text(String(row), textX, cy);
        cy += bodyStep;
      });

      // Divider
      cy += dividerGapTop;
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.5);
      doc.line(textX, cy, cardX + cardWidth - padX, cy);
      cy += dividerGapBottom;

      // And
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(headingFontSize);
      doc.setTextColor(0, 0, 0);
      doc.text(String(andTitle || 'And:'), textX, cy);
      cy += headingStep;

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(bodyFontSize);
      doc.setTextColor(0, 0, 0);
      andWrapped.forEach((row) => {
        doc.text(String(row), textX, cy);
        cy += bodyStep;
      });

      y += cardHeight + 26;
      doc.setTextColor(0, 0, 0);
    };

    // Heading: Client | Project
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(20);
    doc.setTextColor(0, 0, 0);
    doc.text(`${clientBlockName} | ${contractTitle}`, pageWidth / 2, y + 20, { align: 'center' });
    y += 55;

    // Metadata Box (UCENA Red) — reuses the existing contract meta lines (no duplicated/new content)
    const metaBaseLines = [
      `Effective Date: ${formatHumanDate(effectiveDate)}`,
      `Project Name: ${contractTitle}`,
      `Project Term: ${term || '—'}`,
      'Payment Terms: Defined in the separate "Project Scope Document"',
    ];
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(11);
    doc.setTextColor(255, 255, 255);
    const metaPaddingTop = 28;
    const metaPaddingBottom = 22;
    const metaLineStep = 16;
    const metaMaxWidth = usableWidth - 40;
    const wrappedMeta = metaBaseLines.flatMap((ln) => doc.splitTextToSize(String(ln || ''), metaMaxWidth));
    const metaHeight = metaPaddingTop + (wrappedMeta.length * metaLineStep) + metaPaddingBottom;
    doc.setFillColor(UCENA_RED.r, UCENA_RED.g, UCENA_RED.b);
    doc.rect(0, y, pageWidth, metaHeight, 'F');
    let my = y + metaPaddingTop;
    wrappedMeta.forEach((row) => {
      doc.text(String(row), margin + 20, my);
      my += metaLineStep;
    });
    doc.setTextColor(0, 0, 0);
    y += metaHeight + 30;

    // Between/And block (same content, more elegant layout)
    const ucenaBlockLines = (
      (pdfTexts?.contracts?.ucenaBlock && Array.isArray(pdfTexts.contracts.ucenaBlock) && pdfTexts.contracts.ucenaBlock.length > 0)
        ? pdfTexts.contracts.ucenaBlock
        : ['UCENA LLC', '30 North Gould Street', 'Sheridan, WY 82801', 'Email: legal@ucena.com', '("UCENA")']
    );
    // Client identity block with signatory (contact) name and static "Client" label
    const clientMetaLines = [];
    clientMetaLines.push(client.name || 'Client');
    if (contract.clientSignatoryName) clientMetaLines.push(contract.clientSignatoryName);
    if (client.addressLines && client.addressLines.length > 0) {
      client.addressLines.forEach((ln) => clientMetaLines.push(ln));
    } else {
      clientMetaLines.push('');
    }
    if (client.billingEmail) clientMetaLines.push(`Email: ${client.billingEmail}`);
    if (client.phone) clientMetaLines.push(client.phone);
    clientMetaLines.push('"Client"');

    drawPartiesCard(
      pdfTexts?.contracts?.betweenHeading || 'Between:',
      ucenaBlockLines,
      pdfTexts?.contracts?.andHeading || 'And:',
      clientMetaLines
    );

    // Sections
    const section = (num, title, bodyLines) => {
      addLeftHeading(`${num}. ${title}`, 13);
      addLines(bodyLines, 12, false);
    };

    section('1', 'Services and Scope', [
      pdfTexts?.contracts?.sections?.servicesAndScope || 'UCENA LLC shall provide strategic advice, coordination, and oversight for marketing services including brand storytelling, digital advertising, social media management and content production. Actual execution will be performed by independent subcontractors coordinated by UCENA. Detailed scope, specific services, and fees are outlined separately in the "Project Scope Document".'
    ]);

    // Build fees and payments with schedule
    const milestones = Array.isArray(contract.milestoneLogs) ? contract.milestoneLogs.slice() : [];
    milestones.sort((a, b) => String(a.expectedDate || '').localeCompare(String(b.expectedDate || '')));
    const scheduleLines = [];
    if (milestones.length > 0) {
      scheduleLines.push(pdfTexts?.contracts?.sections?.feesAndPaymentsIntro || 'No work begins until the first payment is received. Subsequent monthly invoices will be as follows:');
      milestones.forEach((m, idx) => {
        const amount = Number(m.expectedAmount || m.actualAmount || 0);
        const dateStr = m.invoiceDate || m.expectedDate || '';
        const nice = /\d{4}-\d{2}-\d{2}/.test(String(dateStr)) ? formatHumanDate(dateStr) : formatHumanDate(String(dateStr), { includeDay: false });
        scheduleLines.push(`Transaction #${idx + 1} – ${formatCurrency(amount, 'USD')} (Scheduled on ${nice || 'TBD'})`);
      });
      // Monthly budget after one-time transactions
      const monthlyBudget = (Array.isArray(contract.paymentSchedule) ? contract.paymentSchedule : [])
        .filter(i => i && i.type === 'Service' && String(i.frequency || '').toLowerCase() === 'monthly')
        .reduce((s, i) => s + (Number(i.amount) || 0), 0);
      // if (monthlyBudget > 0) {
      //   scheduleLines.push(`After completing the above transactions, the monthly budget will be ${formatCurrency(monthlyBudget, 'USD')}.`);
      // }
      scheduleLines.push(pdfTexts?.contracts?.sections?.delayedPaymentClause || 'If an ongoing monthly payment is delayed, UCENA reserves the right to pause all services until payment is received.');
    } else {
      scheduleLines.push('Fees and payment schedules are defined in the "Project Scope Document". No work begins until the first payment is received.');
    }
    section('2', 'Fees and Payments', scheduleLines);

    section('3', 'Client Obligations', [
      pdfTexts?.contracts?.sections?.clientObligations || 'Client shall provide timely approvals, feedback, and assets. UCENA may pause Services and continue billing if the Client causes delays.'
    ]);
    section('4', 'Expenses and Third-Party Services', [
      pdfTexts?.contracts?.sections?.expensesThirdParty || 'Client is solely responsible for third-party fees and accounts (Google, Facebook, etc.).'
    ]);
    section('5', 'Cancellation', [
      pdfTexts?.contracts?.sections?.cancellation || 'Client may cancel at any time by emailing legal@ucena.com and must pay the current month plus one additional month.'
    ]);
    section('6', 'Intellectual Property', [
      pdfTexts?.contracts?.sections?.intellectualProperty || 'Upon full payment, UCENA transfers all intellectual property rights in deliverables to Client, retaining no rights thereafter.'
    ]);
    section('7', 'No Data Custody', [
      pdfTexts?.contracts?.sections?.noDataCustody || 'UCENA does not store or manage Client data; all work is executed on Client-owned platforms.'
    ]);
    section('8', 'Subcontractor Non-Solicitation', [
      pdfTexts?.contracts?.sections?.nonSolicitation || 'Client agrees not to solicit or hire UCENA subcontractors for six months after termination.'
    ]);
    section('9', 'Independent Contractors', [
      pdfTexts?.contracts?.sections?.independentContractors || 'UCENA engages subcontractors to perform actual work and is not liable for their actions. If Client objects to any subcontractor, they must promptly email legal@ucena.com, and UCENA will make reasonable efforts to reassign.'
    ]);
    section('10', 'Force Majeure', [
      pdfTexts?.contracts?.sections?.forceMajeure || 'Neither party is liable for delays or failures beyond reasonable control (natural disasters, wars, pandemics, etc.).'
    ]);
    section('11', 'Liability Limitation', [
      pdfTexts?.contracts?.sections?.liabilityLimitation || `UCENA's total liability under this Agreement is limited to 20% of total fees paid and excludes indirect or consequential damages.`
    ]);
    section('12', 'Assignment', [
      pdfTexts?.contracts?.sections?.assignment || `Client may not assign this Agreement without UCENA's prior written consent.`
    ]);
    section('13', 'Dispute Resolution', [
      pdfTexts?.contracts?.sections?.disputeResolution || 'Disputes shall be resolved via binding arbitration by AAA rules in Los Angeles County, California. Prevailing party recovers attorney fees and costs.'
    ]);
    section('14', 'Notices and Official Communications', [
      pdfTexts?.contracts?.sections?.notices || 'Official communications must be sent to legal@ucena.com. Day-to-day interactions may occur through any authorized UCENA email.'
    ]);
    section('15', 'Entire Agreement', [
      pdfTexts?.contracts?.sections?.entireAgreement || 'This Agreement constitutes the entire agreement between parties, superseding all prior agreements or understandings.'
    ]);

    // Acceptance & Signatures
    addLeftHeading(pdfTexts?.contracts?.acceptanceHeading || 'Acceptance and Signatures', 13);
    addLines([pdfTexts?.contracts?.acceptancePreface || 'The parties agree to the terms and conditions outlined herein:'], 12, false);
    // Increase whitespace before signature blocks
    addLines(['']);

    const drawSignatureBlock = (heading, name, dateStr, title) => {
      addLines([heading], 12, true);
      y += 8;
      // Ensure space for signature block
      if (y + 80 > pageHeight - margin) {
        newContentPage();
      }
      // Signature line with default handwritten-style name next to it
      addLines([`Signature: `], 12, false);
      try {
        const sigLabel = 'Signature: ';
        const sigFontSize = 12;
        const lineStep = sigFontSize + 4; // matches addLines spacing for fontSize 12
        const sigY = y - lineStep + 1; // position on the same line as the underscores
        const sigX = margin + doc.getTextWidth(sigLabel);
        doc.setTextColor(60, 60, 60);
        if (heading === 'UCENA LLC') {
          if (signatureFontReady) {
            doc.setFont('WhisperingSignature', 'normal');
            doc.setFontSize(10);
            doc.text('UCENA ', sigX, sigY);
          } else {
            doc.setFont('courier', 'italic');
            doc.setFontSize(18);
            doc.text('Amoor avakian', sigX, sigY);
          }
        } else {
          doc.setFont('helvetica', 'normal');
          doc.setFontSize(12);
          doc.text('_____________________', sigX-5, sigY);
        }
        // restore defaults
        doc.setTextColor(0, 0, 0);
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
      } catch (_) {
        // best-effort; continue without handwritten styling if unsupported
      }
      y += 15;
      addLines([`Date: ${dateStr || '_____________________'}`], 12, false);
      addLines(['By'], 12, true);
      addLines([`Name: ${name || ''}`], 12, false);
      addLines([`Title: ${title || ''}`], 12, false);
      y += 10;
    };

    const ucenaSignDate = formatHumanDate(contract.signedDate || new Date());
    const ucenaTitle = pdfTexts?.contracts?.signatoryTitle || 'Managing Member';
    // Display title line above UCENA block
    addLines([`Title: ${ucenaTitle}`]);
    drawSignatureBlock('UCENA LLC', pdfTexts?.contracts?.signatoryName || 'Amoor Avakian', ucenaSignDate, ucenaTitle);

    // Use contract-selected client signatory name and title
    const clientSignerName = contract.clientSignatoryName || clientBlockName;
    const clientSignerTitle = contract.clientSignatoryTitle || (pdfTexts?.contracts?.clientSignerTitle || '_____________________');
    drawSignatureBlock(clientBlockName, clientSignerName, '', clientSignerTitle);

    // Footer with page numbers
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      if (i === 1) continue;
      const pageHeight = doc.internal.pageSize.getHeight();
      doc.setFontSize(9);
      doc.setTextColor(150);
      doc.text(`Page ${i} of ${pageCount}`, pageWidth - margin, pageHeight - 20, { align: 'right' });
    }

    const safe = (s) => (s || '').replace(/[^a-z0-9\-_]+/gi, '_').slice(0, 60);
    const filename = `Contract_${safe(clientBlockName)}_${safe(contractTitle)}.pdf`;
    doc.save(filename);
  };

  const handleGeneratePDF = async (contract) => {
    try {
      const status = String(contract?.status || '');
      if (status !== 'Ready' && status !== 'Active') {
        setSnackbar({ open: true, message: 'Set contract status to Ready or Active before generating PDF', severity: 'warning' });
        return;
      }
      await generateContractPDF(contract);
    } catch (e) {
      console.error('Error generating contract PDF', e);
      setSnackbar({ open: true, message: 'Error generating PDF', severity: 'error' });
    }
  };

  const generateShareToken = () => {
    try {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      return Array.from(bytes).map((b) => b.toString(16).padStart(2, '0')).join('');
    } catch {
      return Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    }
  };

  const buildContractShareLink = (contractId, token) => {
    const origin = window.location.origin;
    return `${origin}/c/${encodeURIComponent(contractId)}/${encodeURIComponent(token)}`;
  };

  const handleCopyDirectLink = async (contract) => {
    if (!contract?.id) return;
    const link = buildContractDirectLink(contract.id);
    try {
      await navigator.clipboard.writeText(link);
      setSnackbar({ open: true, message: 'Contract URL copied to clipboard', severity: 'success' });
    } catch (e) {
      window.prompt('Copy this contract URL', link);
    }
  };

  const handleShareClick = async (contract) => {
    try {
      let token = contract.shareToken;
      if (!contract.shareEnabled || !token) {
        token = generateShareToken();
        const ref = doc(db, 'contracts', contract.id);
        await updateDoc(ref, { shareEnabled: true, shareToken: token, updatedAt: serverTimestamp() });
      }
      const link = buildContractShareLink(contract.id, token);
      try {
        await navigator.clipboard.writeText(link);
        setSnackbar({ open: true, message: 'Share link copied to clipboard', severity: 'success' });
      } catch (e) {
        window.prompt('Copy this link', link);
      }
    } catch (e) {
      setSnackbar({ open: true, message: 'Error creating share link', severity: 'error' });
    }
  };

  // Payments are editable only for Super Admin AND when contract is Active or Inactive
  const canEditPayments = isSa && (String(formData.status || '') === 'Active' || String(formData.status || '') === 'Inactive');
  const canEditInvoiceMeta = isSa;

  const invoiceSaveTimers = useRef({});
  const operationsInProgress = useRef(0);

  const clientGrouping = useClientGrouping('contracts.groupByClient');

  const sortedContracts = useMemo(() => {
    const direction = contractSort.direction === 'desc' ? -1 : 1;
    return (Array.isArray(filteredContracts) ? filteredContracts : [])
      .map((contract, index) => {
        let sortValue;
        switch (contractSort.field) {
          case 'client':
            sortValue = clients.find(client => client.id === contract.clientEntityId)?.clientName || '';
            break;
          case 'status':
            sortValue = contract.status || '';
            break;
          case 'value':
            sortValue = getContractValue(contract);
            break;
          case 'contract':
          default:
            sortValue = contract.title || '';
            break;
        }
        return { contract, index, sortValue };
      })
      .sort((left, right) => {
        const result = compareTableValues(left.sortValue, right.sortValue);
        return result === 0 ? left.index - right.index : result * direction;
      })
      .map(item => item.contract);
  }, [filteredContracts, clients, contractSort]);

  const renderContractRow = (contract) => {
    const status = String(contract?.status || '');
    const canExportPdf = status === 'Ready' || status === 'Active';
    return (
      <TableRow key={contract.id} hover>
      {/* Client */}
      <TableCell>
        <Typography variant="body2">
          {getClientName(contract.clientEntityId)}
        </Typography>
      </TableCell>
      {/* Drive */}
      <TableCell>
        {(() => {
          const url = getClientDriveUrl(contract.clientEntityId);
          return url ? (
            <Tooltip title="Open Google Drive">
              <IconButton
                size="small"
                component="a"
                href={url}
                target="_blank"
                rel="noopener noreferrer"
                color="primary"
              >
                <DriveIcon />
              </IconButton>
            </Tooltip>
          ) : null;
        })()}
      </TableCell>
      {/* Contract */}
      <TableCell>
        <Box>
          <Typography variant="body2" fontWeight="bold">
            {contract.title}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            Based on: {(() => {
              const live = getProposalTitle(contract.proposalId);
              if (live && live !== 'Unknown Proposal') return live;
              const snap = contract?.proposalSnapshot?.title;
              return snap || 'Unknown Proposal';
            })()}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
            {(contract.status === 'Signed' || contract.status === 'Complete') && (
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                <LockIcon sx={{ fontSize: 12, mr: 0.5, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary">
                  Immutable
                </Typography>
              </Box>
            )}
          </Box>
        </Box>
      </TableCell>
      {/* Status */}
      <TableCell>
        <Chip 
          label={contract.status} 
          color={getStatusColor(contract.status)}
          size="small"
        />
      </TableCell>
      {/* Value */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <MoneyIcon sx={{ fontSize: 16, mr: 1, color: 'text.secondary' }} />
          {formatCurrency(getContractValue(contract), 'USD')}
        </Box>
      </TableCell>
      {/* Edit */}
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={canExportPdf ? 'Export PDF' : 'Set status to Ready or Active to enable PDF export'}>
            <span>
              <IconButton
                size="small"
                color="secondary"
                onClick={() => handleGeneratePDF(contract)}
                disabled={!canExportPdf}
              >
                <PdfIcon />
              </IconButton>
            </span>
          </Tooltip>
        {/* <Tooltip title="Share link">
          <span>
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleShareClick(contract)}
            >
              <ShareIcon />
            </IconButton>
          </span>
        </Tooltip> */}
          <Tooltip title="Copy contract URL">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={() => handleCopyDirectLink(contract)}
              >
                <ShareIcon />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={canEditContract(contract) ? "Edit" : "Read Only"}>
            <span>
              <IconButton
                size="small"
                onClick={() => handleOpenContractByUrl(contract)}
                color="primary"
                disabled={!canEditContract(contract) && !isSa}
              >
                <EditIcon />
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </TableCell>
      </TableRow>
    );
  };

  const handleContractSort = (field) => {
    setContractSort((previous) => ({
      field,
      direction: previous.field === field && previous.direction === 'asc' ? 'desc' : 'asc',
    }));
  };

  const sortedServices = useMemo(() => {
    const list = Array.isArray(formData.services) ? [...formData.services] : [];
    list.sort((a, b) => {
      const fa = normalizeFrequencyValue(a?.frequency);
      const fb = normalizeFrequencyValue(b?.frequency);
      if (fa !== fb) {
        return fa === 'monthly' ? -1 : 1;
      }
      const ta = getServiceSortTimestamp(a);
      const tb = getServiceSortTimestamp(b);
      if (ta !== tb) {
        return tb - ta;
      }
      const nameA = String(a?.name || '').toLowerCase();
      const nameB = String(b?.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });
    return list;
  }, [formData.services]);

  // Contract Services sections: active Monthly, active One-time, and a
  // collapsible History for cancelled services and invoiced one-times.
  const [servicesHistoryOpen, setServicesHistoryOpen] = useState(false);
  // Newly added service: scrolled into view and briefly highlighted
  const [justAddedServiceId, setJustAddedServiceId] = useState(null);
  useEffect(() => {
    if (!justAddedServiceId) return undefined;
    const frame = requestAnimationFrame(() => {
      document
        .querySelector(`[data-service-id="${justAddedServiceId}"]`)
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
    const timer = setTimeout(() => setJustAddedServiceId(null), 2000);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(timer);
    };
  }, [justAddedServiceId]);
  // History starts collapsed every time a contract is opened
  useEffect(() => {
    if (openDialog) setServicesHistoryOpen(false);
  }, [openDialog, editingContract?.id]);
  const { activeMonthlyServices, activeOneTimeServices, historyServices } = useMemo(() => {
    const isHistoryService = (svc) => {
      if (svc?.cancellationEffectiveYm) return true;
      return normalizeFrequencyValue(svc?.frequency) === 'one-time' && !!svc?.oneTimeApplied;
    };
    const active = sortedServices.filter((svc) => !isHistoryService(svc));
    return {
      activeMonthlyServices: active.filter((svc) => normalizeFrequencyValue(svc?.frequency) === 'monthly'),
      activeOneTimeServices: active.filter((svc) => normalizeFrequencyValue(svc?.frequency) !== 'monthly'),
      historyServices: sortedServices.filter(isHistoryService),
    };
  }, [sortedServices]);

  const servicesClipboardText = useMemo(() => {
    const invoicedServiceIds = new Set(
      (Array.isArray(formData.paymentSchedule) ? formData.paymentSchedule : [])
        .filter((item) => {
          if (!item || item.type !== 'Service' || !item.serviceId) return false;
          const status = normalizeScheduleStatus(item.status);
          return Boolean(
            item.invoiced
            || item.paid
            || item.partiallyPaid
            || paymentStatuses[status]?.immutable
          );
        })
        .map(item => item.serviceId)
    );

    return sortedServices
      .filter((service) => {
        const status = String(service?.status || '').toLowerCase();
        const isCancelled = Boolean(service?.cancellationEffectiveYm)
          || status === 'cancelled'
          || status === 'canceled';
        const isInvoiced = Boolean(service?.oneTimeApplied)
          || invoicedServiceIds.has(service?.id);
        return !isCancelled && !isInvoiced;
      })
      .map(service => String(service?.name || '').trim())
      .filter(Boolean)
      .join('\n');
  }, [sortedServices, formData.paymentSchedule]);

  const handleCopyServicesText = async () => {
    if (!servicesClipboardText) {
      setSnackbar({ open: true, message: 'No uninvoiced, uncanceled services to copy', severity: 'info' });
      return;
    }
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard API unavailable');
      await navigator.clipboard.writeText(servicesClipboardText);
      setSnackbar({ open: true, message: 'Uninvoiced service names copied for Mercury', severity: 'success' });
    } catch (_) {
      window.prompt('Copy services text', servicesClipboardText);
      setSnackbar({ open: true, message: 'Copy the services text from the dialog', severity: 'info' });
    }
  };

  // One service card (accordion) in the Contract Services list
  const renderServiceAccordion = (svc) => {
    const normalizedFrequency = normalizeFrequencyValue(svc.frequency);
    const isOneTime = normalizedFrequency === 'one-time';
    const isMonthly = normalizedFrequency === 'monthly';
    const paymentItemsForService = getServicePaymentScheduleItems(svc.id);
    const hasImmutablePayments = paymentItemsForService.some((item) => isScheduleItemImmutable(item));
    const hasPendingPayments = paymentItemsForService.some((item) => String(item?.status || '').toUpperCase() === 'PENDING');
    const isOneTimeInvoiced = isOneTime && !!svc.oneTimeApplied;
    const lockedSx = (hasImmutablePayments || isOneTimeInvoiced)
      ? { bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.300' }
      : (isOneTime && hasPendingPayments ? {} : {});
    const isOneTimeLocked = isOneTime && (hasImmutablePayments || isOneTimeInvoiced);
    return (
      <Accordion
        data-service-id={svc.id}
        sx={{
          mb: 1,
          ...lockedSx,
          transition: 'box-shadow 300ms',
          ...(svc.id === justAddedServiceId ? { boxShadow: (theme) => `0 0 0 2px ${theme.palette.primary.main}` } : {})
        }}
        disableGutters
      >
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="subtitle1" fontWeight="bold" color={isOneTimeLocked ? 'success.main' : undefined} sx={{ mr: 1 }}>{svc.name}</Typography>
              {(() => {
                const nextMonthStr = (() => {
                  const now = new Date();
                  const y = now.getUTCFullYear();
                  const m = now.getUTCMonth();
                  const next = new Date(Date.UTC(y, m + 1, 1));
                  const ym = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}`;
                  return ym;
                })();
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: '50px' }}>
                    {isMonthly && (
                      <Tooltip title={svc.cancellationEffectiveYm ? `Already ending after ${svc.cancellationEffectiveYm}` : 'Cancel Uninvoiced'}>
                        <span>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            onClick={() => cancelServiceFromNextMonth(svc.id)}
                            disabled={Boolean(svc.cancellationEffectiveYm && svc.cancellationEffectiveYm <= nextMonthStr)}
                          >
                            {svc.cancellationEffectiveYm ? `Cancelled` : 'Cancel Uninvoiced'}
                          </Button>
                        </span>
                      </Tooltip>
                    )}
                    {(() => {
                      const isMonthlyLocked = isMonthly && hasImmutablePayments; // monthly services cannot be deleted once any invoice activity exists
                      if (isMonthlyLocked) return null; // hide trashcan entirely per spec
                      const deleteDisabled = Boolean(svc.cancellationEffectiveYm) || (isMonthly ? hasImmutablePayments : (hasImmutablePayments || isOneTimeInvoiced));
                      const deleteTooltip = svc.cancellationEffectiveYm
                        ? 'Cannot remove: service is cancelled'
                        : ((hasImmutablePayments || isOneTimeInvoiced)
                            ? (isOneTime ? 'Cannot remove: one-time has invoiced activity' : 'Cannot remove: invoiced activity exists')
                            : 'Remove');
                      return (
                        <Tooltip title={deleteTooltip}>
                          <span>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => removeServiceFromContract(svc.id)}
                              disabled={deleteDisabled}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                      );
                    })()}
                  </Box>
                );
              })()}
            </Box>
            <Typography variant="subtitle2" fontWeight="bold" sx={{ color: '#1b5e20' }}>
              Total: {formatCurrency((Number(svc.unitPrice) || 0) * (Number(svc.quantity) || 0), formData.currency)}{isMonthly ? ' / month' : ''}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color={isOneTimeLocked ? 'success.main' : 'text.secondary'}>
                Qty {svc.quantity} • Unit {formatCurrency(Number(svc.unitPrice) || 0, formData.currency)} • {svc.frequency}
              </Typography>
              {(() => {
                const pct = (Number.isFinite(Number(svc.marginPercent)) ? Number(svc.marginPercent) : 0);
                const styles = getMarginChipStyles(pct);
                return (
                  <Chip
                    size="small"
                    label={`${pct.toFixed(1)}%`}
                    sx={{
                      bgcolor: styles.bg,
                      color: styles.fg,
                      border: `1px solid ${styles.b}`,
                      height: 18,
                      '& .MuiChip-label': { px: 0.75, fontSize: 11, lineHeight: '18px' },
                    }}
                  />
                );
              })()}
              <Typography variant="caption" color="text.secondary">
                • Vendor: {getVendorName(svc)}
              </Typography>
              {svc.cancellationEffectiveYm && (
                <Chip size="small" color="warning" label="Cancelled" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11, lineHeight: '18px' } }} />
              )}
              {normalizeFrequencyValue(svc.frequency) === 'one-time' && !!svc.oneTimeApplied && (
                <Chip size="small" color="success" label="Invoiced" sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: 11, lineHeight: '18px' } }} />
              )}
            </Box>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Service Name" value={svc.name || ''} onChange={(e) => handleServiceFieldChange(svc.id, 'name', e.target.value)} disabled />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField fullWidth size="small" label="Category" value={svc.category || 'General'} onChange={(e) => handleServiceFieldChange(svc.id, 'category', e.target.value)} disabled />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth size="small" label="Description" value={svc.description || ''} onChange={(e) => handleServiceFieldChange(svc.id, 'description', e.target.value)} />
            </Grid>
            <Grid item xs={12} md={3}>
              <NumericField fullWidth size="small" label="Quantity" value={svc.quantity || 1} onValueChange={(n)=>handleServiceFieldChange(svc.id, 'quantity', n)} mode="decimal" decimals={2} min={1} />
            </Grid>
            <Grid item xs={12} md={0} sx={{ display: 'none' }}></Grid>
            <Grid item xs={12} md={6}>
              <NumericField fullWidth size="small" label="Unit Price" value={Number(svc.internalUnitCost || 0)} onValueChange={(n)=>handleServiceFieldChange(svc.id, 'unitPrice', n)} mode="decimal" decimals={2} min={0} />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                size="small"
                type="text"
                label="Margin %"
                inputProps={{ inputMode: 'decimal', pattern: '^\\d*\\.?\\d*$' }}
                value={String(Math.round(((Number.isFinite(Number(svc.marginPercent)) ? Number(svc.marginPercent) : 0) + Number.EPSILON) * 100) / 100)}
                onChange={(e) => handleServiceFieldChange(svc.id, 'marginPercent', e.target.value)}
                onBlur={() => handleServiceFieldBlur(svc.id, 'marginPercent')}
              />
            </Grid>
          </Grid>
        </AccordionDetails>
      </Accordion>
    );
  };

  return (
    <Container
      maxWidth={false}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', flex: 1, minHeight: 0, overflow: 'auto' }}
    >
     

      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2, flexWrap: 'wrap', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <ContractIcon sx={{ fontSize: 40, color: 'primary.main', mr: 2 }} />
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
              Contracts Management
            </Typography>
         
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <GroupByClientToggle grouped={clientGrouping.grouped} onChange={clientGrouping.setGrouped} />
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              openedContractIdRef.current = null;
              if (routeContractId) {
                navigate('/contracts');
              }
              handleOpenDialog();
            }}
          >
            Create Contract
          </Button>
        </Box>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                type="search"
                label="Search Contracts or Clients"
                placeholder="Search by contract title or client name"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Status</InputLabel>
                <Select
                  value={filters.status}
                  label="Status"
                  displayEmpty
                  notched
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                >
                  <MenuItem value="">Relevant</MenuItem>
                  {contractStatuses.filter(status => !(isMember() && status === 'Inactive')).map(status => (
                    <MenuItem key={status} value={status}>{status}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <FormControl fullWidth>
                <InputLabel shrink>Client</InputLabel>
                <Select
                  value={filters.client}
                  label="Client"
                  displayEmpty
                  notched
                  onChange={(e) => setFilters({ ...filters, client: e.target.value })}
                >
                  <MenuItem value="">All Clients</MenuItem>
                  {clients.map(client => (
                    <MenuItem key={client.id} value={client.id}>{client.clientName}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>
      </Paper>

      {/* Enhanced Contracts Table with additional columns */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <TableContainer sx={{ flex: 1, minHeight: 0, overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={contractSort.field === 'client' ? contractSort.direction : false}>
                  <TableSortLabel
                    active={contractSort.field === 'client'}
                    direction={contractSort.field === 'client' ? contractSort.direction : 'asc'}
                    onClick={() => handleContractSort('client')}
                  >
                    Client
                  </TableSortLabel>
                </TableCell>
                <TableCell>Drive</TableCell>
                <TableCell sortDirection={contractSort.field === 'contract' ? contractSort.direction : false}>
                  <TableSortLabel
                    active={contractSort.field === 'contract'}
                    direction={contractSort.field === 'contract' ? contractSort.direction : 'asc'}
                    onClick={() => handleContractSort('contract')}
                  >
                    Contract
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={contractSort.field === 'status' ? contractSort.direction : false}>
                  <TableSortLabel
                    active={contractSort.field === 'status'}
                    direction={contractSort.field === 'status' ? contractSort.direction : 'asc'}
                    onClick={() => handleContractSort('status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={contractSort.field === 'value' ? contractSort.direction : false}>
                  <TableSortLabel
                    active={contractSort.field === 'value'}
                    direction={contractSort.field === 'value' ? contractSort.direction : 'asc'}
                    onClick={() => handleContractSort('value')}
                  >
                    Value
                  </TableSortLabel>
                </TableCell>
                <TableCell>Edit</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <CircularProgress />
                  </TableCell>
                </TableRow>
              ) : sortedContracts.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center">
                    <Typography variant="h6" color="text.secondary">No contracts found.</Typography>
                  </TableCell>
                </TableRow>
              ) : clientGrouping.grouped ? (
                groupByClient(sortedContracts, (contract) => contract.clientEntityId, getClientName).map((group) => (
                  <React.Fragment key={`group-${group.key}`}>
                    <ClientGroupHeaderRow
                      group={group}
                      colSpan={6}
                      collapsed={clientGrouping.collapsed.has(group.key)}
                      onToggle={() => clientGrouping.toggleCollapsed(group.key)}
                      total={formatCurrency(group.items.reduce((sum, c) => sum + getContractValue(c), 0), 'USD')}
                    />
                    {!clientGrouping.collapsed.has(group.key) && group.items.map(renderContractRow)}
                  </React.Fragment>
                ))
              ) : (
                sortedContracts.map(renderContractRow)
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Enhanced Add/Edit Dialog with new tabs */}
      <Dialog 
        open={openDialog} 
        onClose={(event, reason) => {
          // Completely disable backdrop clicks
          if (reason === 'backdropClick') {
            return;
          }
          handleCloseDialog();
        }}
        maxWidth="xl" 
        fullWidth
        disableEscapeKeyDown={saving || operationInProgress}
        slotProps={{
          backdrop: {
            onClick: (e) => {
              e.stopPropagation();
              e.preventDefault();
            }
          }
        }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              {editingContract ? 'Edit Contract' : 'Create Contract'}
            </Typography>
            
          </Box>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ width: '100%' }}>
            <Tabs value={currentTab} onChange={(e, newValue) => setCurrentTab(newValue)}>
              <Tab label="Basic Info" />
              <Tab label="Services" />
              <Tab label="Partner Roles" />
              <Tab label="Payments" />
            </Tabs>

            {/* Basic Info Tab - Enhanced */}
            <TabPanel value={currentTab} index={0}>
              <Grid container spacing={2}>
                {editingContract?.id && (
                  <Grid item xs={12}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: 1,
                        p: 1.5,
                        border: 1,
                        borderColor: 'divider',
                        borderRadius: 1,
                      }}
                    >
                      <Button size="small" onClick={() => handleCopyDirectLink(editingContract)}>
                        Copy Contract URL
                      </Button>
                    </Box>
                  </Grid>
                )}
                 <Grid item xs={12} md={6}>
                   {editingContract ? (
                     <Box sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', height: '100%' }}>
                       <Typography variant="body1">
                         Client:{' '}
                         <Link href={`/clients?clientId=${encodeURIComponent(formData.clientEntityId || '')}`} underline="hover" target="_blank" rel="noopener noreferrer">
                           {getClientName(formData.clientEntityId)}
                         </Link>
                       </Typography>
                       {getClientBillingContactEmail(formData.clientEntityId) && (
                         <Typography variant="body2" color="text.secondary">
                           Billing contact email: {getClientBillingContactEmail(formData.clientEntityId)}
                         </Typography>
                       )}
                     </Box>
                   ) : (
                   <FormControl fullWidth disabled={!!editingContract}>
                     <InputLabel>Client</InputLabel>
                     <Select
                       value={formData.clientEntityId}
                       label="Client"
                       onChange={(e) => {
                         const newClientId = e.target.value;
                         setFormData((prev) => ({
                           ...prev,
                           clientEntityId: newClientId,
                           // Reset proposal when client changes to avoid mismatch
                           proposalId: ''
                         }));
                       }}
                     >
                      {clientsWithAcceptedProposals.map(client => (
                         <MenuItem key={client.id} value={client.id}>{client.clientName}</MenuItem>
                       ))}
                     </Select>
                   </FormControl>
                   )}
                 </Grid>
                <Grid item xs={12} md={6}>
                  {editingContract ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
                      <Typography variant="body1">
                         Proposal:{' '}
                        <Link href={`/proposals?proposalId=${encodeURIComponent(formData.proposalId || '')}`} underline="hover" target="_blank" rel="noopener noreferrer">
                          {getProposalTitle(formData.proposalId) !== 'Unknown Proposal' ? getProposalTitle(formData.proposalId) : (formData.proposalSnapshot?.title || 'Unknown Proposal')}
                        </Link>
                      </Typography>
                    </Box>
                  ) : (
                    <FormControl fullWidth disabled={!formData.clientEntityId}>
                    <InputLabel> Proposal (Accepted Only)</InputLabel>
                    <Select
                      value={formData.proposalId}
                      label=" Proposal (Accepted Only)"
                      onChange={(e) => {
                        const selectedProposal = proposals.find(p => p.id === e.target.value);
                        // Build services preview from proposal costs
                        const previewServices = (selectedProposal?.costs || []).map((cost, index) =>
                          convertCostToContractService(cost, cost.quantity || 1, formData.beneficiaryCompanyId || '')
                        );
                        // Generate schedule preview
                        const clientEntity = entities.find(e2 => e2.id === (selectedProposal?.client_id || formData.clientEntityId));
                        const clientCountry = clientEntity?.country || 'US';
                        const partnerRolesForPreview = Array.isArray(selectedProposal?.partnerRoles) ? selectedProposal.partnerRoles : formData.partnerRoles;
                        const schedulePreview = generateEnhancedPaymentSchedule(
                          previewServices,
                          partnerRolesForPreview,
                          clientCountry
                        );
                        // Build proper baseForm for recalculation to avoid stale closure
                        const baseForRecalc = {
                          ...formData,
                          proposalId: e.target.value,
                          clientEntityId: selectedProposal?.client_id || formData.clientEntityId,
                          services: previewServices,
                          paymentSchedule: schedulePreview,
                          partnerRoles: partnerRolesForPreview,
                          proposalSnapshot: selectedProposal ? { totalAmount: selectedProposal.totalAmount } : null,
                        };
                        const recalculated = recalculateTotalsAndMilestones(schedulePreview, partnerRolesForPreview, baseForRecalc);
                        setFormData((prev) => ({
                          ...prev,
                          proposalId: e.target.value,
                          title: selectedProposal ? `Contract for ${selectedProposal.title}` : '',
                          // Keep client consistent; proposals list is already filtered by client
                          clientEntityId: selectedProposal?.client_id || prev.clientEntityId,
                          services: previewServices,
                          paymentSchedule: schedulePreview,
                          totalAmount: recalculated.totalAmount,
                          totalTransportFees: recalculated.totalTransportFees,
                          milestoneLogs: recalculated.milestoneLogs,
                          // Inherit partner roles directly from proposal
                          partnerRoles: Array.isArray(selectedProposal?.partnerRoles) ? selectedProposal.partnerRoles : prev.partnerRoles
                        }));
                      }}
                    >
                      {(formData.clientEntityId ? proposals.filter(proposal => proposal.client_id === formData.clientEntityId) : []).map(proposal => (
                        <MenuItem key={proposal.id} value={proposal.id}>
                          {proposal.title} - {formatCurrency(proposal.totalAmount, 'USD')}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  )}
                </Grid>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <InputLabel>Status</InputLabel>
                    <Select value={formData.status} label="Status" onChange={(e) => {
                      const newStatus = e.target.value;
                      const fromStatus = formData.status;
                      if (!canTransitionContractStatus(fromStatus, newStatus, isSa)) {
                        setSnackbar({ open: true, message: `Cannot transition from ${fromStatus} to ${newStatus}`, severity: 'error' });
                        return;
                      }
                      if (newStatus === 'Active' && !isSa) {
                        setSnackbar({ open: true, message: 'Only Super Admin can set status to Active', severity: 'error' });
                        return;
                      }
                      if (newStatus === 'Ready' && !String(formData.title || '').trim()) {
                        setSnackbar({ open: true, message: 'Contract Title is required to set Ready', severity: 'error' });
                        return;
                      }
                      if (newStatus === 'Active' || newStatus === 'Ready') {
                        if (!formData.startDate) { setSnackbar({ open: true, message: `Start date is required to set ${newStatus}`, severity: 'error' }); return; }
                        if (!formData.endDate) { setSnackbar({ open: true, message: `End date is required to set ${newStatus}`, severity: 'error' }); return; }
                        const s = formData.startDate ? new Date(`${formData.startDate}T00:00:00`).getTime() : 0;
                        const eTime = formData.endDate ? new Date(`${formData.endDate}T00:00:00`).getTime() : 0;
                        if (s && eTime && eTime <= s) { setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' }); return; }
                        if (newStatus === 'Ready' && !formData.defaultPaymentMethod) {
                          setSnackbar({ open: true, message: 'Default Payment is required to set Ready', severity: 'error' });
                          return;
                        }
                      }
                      if (newStatus === 'Active') {
                        // Preconditions for moving to Active
                        if (!formData.startDate) { setSnackbar({ open: true, message: 'Start date is required to set Active', severity: 'error' }); return; }
                        if (!formData.endDate) { setSnackbar({ open: true, message: 'End date is required to set Active', severity: 'error' }); return; }
                        const roles = Array.isArray(formData.partnerRoles) ? formData.partnerRoles : [];
                        if (roles.length === 0) { setSnackbar({ open: true, message: 'Partner roles must be set to set Active', severity: 'error' }); return; }
                        const nonCompany = roles.filter(r => r && r.positionId !== 'company');
                        const validCommissionsActive = nonCompany.every(r => {
                          const isNobody = !r?.entityId;
                          const percent = Number(r?.percent) || 0;
                          if (isNobody) return percent === 0;
                          const ent = entities.find(e2 => e2.id === r.entityId);
                          return isActivePartnerEntity(ent) && percent >= 0 && percent <= 20;
                        });
                        if (!validCommissionsActive) { setSnackbar({ open: true, message: 'Partner roles must be Active Partners or Nobody (0%), commissions 0-20%', severity: 'error' }); return; }
                        if (!formData.clientSignatoryName || !formData.clientSignatoryTitle) { setSnackbar({ open: true, message: 'Client signatory name and title are required to set Active', severity: 'error' }); return; }
                        const svcCountActive = Array.isArray(formData.services) ? formData.services.length : 0;
                        if (svcCountActive < 1) { setSnackbar({ open: true, message: 'Contract must have at least one service to set Active', severity: 'error' }); return; }
                      }
                      if (newStatus === 'Ready') {
                        // Client-side validation for Ready prerequisites
                        if (!formData.startDate) { setSnackbar({ open: true, message: 'Start date is required to set Ready', severity: 'error' }); return; }
                        if (!formData.endDate) { setSnackbar({ open: true, message: 'End date is required to set Ready', severity: 'error' }); return; }
                        const s = formData.startDate ? new Date(`${formData.startDate}T00:00:00`).getTime() : 0;
                        const eTime = formData.endDate ? new Date(`${formData.endDate}T00:00:00`).getTime() : 0;
                        if (s && eTime && eTime <= s) { setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' }); return; }
                        const roles = Array.isArray(formData.partnerRoles) ? formData.partnerRoles : [];
                        const allDefined = validateAllPartnerRolesDefined(roles);
                        if (!allDefined.ok) { setSnackbar({ open: true, message: allDefined.message, severity: 'error' }); return; }
                        if (!formData.clientSignatoryName || !formData.clientSignatoryTitle) { setSnackbar({ open: true, message: 'Client signatory name and title are required to set Ready', severity: 'error' }); return; }
                        const svcCount = Array.isArray(formData.services) ? formData.services.length : 0;
                        if (svcCount < 1) { setSnackbar({ open: true, message: 'Contract must have at least one service to set Ready', severity: 'error' }); return; }
                      }
                      const updates = { status: newStatus };
                      if (newStatus === 'Active' && !formData.startDateLocked) {
                        updates.startDateLocked = true;
                      }
                      setFormData((prev) => ({ ...prev, ...updates }));
                    }} disabled={editingContract && !isSa && (editingContract.status === 'Signed' || editingContract.status === 'Complete')}>
                      {contractStatuses.map(status => {
                        const disabledByRole = (status === 'Active' && !isSa);
                        const disabledByFlow = !canTransitionContractStatus(formData.status, status, isSa);
                        return (
                          <MenuItem key={status} value={status} disabled={disabledByRole || disabledByFlow}>{status}</MenuItem>
                        );
                      })}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Contract Title"
                    value={formData.title}
                    onChange={(e) => setFormData((prev) => ({ ...prev, title: e.target.value }))}
                    disabled={editingContract && !canEditContract(editingContract)}
                    required
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Client Signatory Name"
                    value={formData.clientSignatoryName || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, clientSignatoryName: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Client Signatory Title"
                    value={formData.clientSignatoryTitle || ''}
                    onChange={(e) => setFormData((prev) => ({ ...prev, clientSignatoryTitle: e.target.value }))}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth disabled={!formData.proposalId}>
                    <InputLabel>Beneficiary Company</InputLabel>
                    <Select
                      value={formData.beneficiaryCompanyId}
                      label="Beneficiary Company"
                      onChange={(e) => setFormData((prev) => ({ ...prev, beneficiaryCompanyId: e.target.value }))}
                      disabled={(editingContract && (!canEditContract(editingContract) || !!editingContract.beneficiaryCompanyId)) || formData.beneficiaryLocked}
                    >
                      {getBeneficiaryCompanyOptions(formData.beneficiaryCompanyId).map(entity => (
                        <MenuItem key={entity.id} value={entity.id}>
                          {entity.entityName}
                        </MenuItem>
                      ))}
                    </Select>
                    {(formData.beneficiaryLocked || (editingContract && !!editingContract.beneficiaryCompanyId)) && (
                      <Typography variant="caption" color="warning.main" sx={{ mt: 0.5 }}>
                        Beneficiary company is locked and cannot be changed
                      </Typography>
                    )}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Default Payment</InputLabel>
                    <Select
                      value={formData.defaultPaymentMethod || 'Transfer'}
                      label="Default Payment"
                      onChange={(e) => {
                        const nextMethod = e.target.value;
                        // Update form data first
                        const nextForm = { ...formData, defaultPaymentMethod: nextMethod };
                        try {
                          // Recompute processing for Scheduled and Invoiced milestones (not Paid/Partial/Cancelled)
                          const logs = Array.isArray(nextForm.milestoneLogs) ? nextForm.milestoneLogs.map(m => ({ ...m })) : [];
                          const pct = (() => {
                            const m = String(nextMethod || '').toLowerCase();
                            if (m === 'credit card' || m === 'creditcard' || m === 'credit_card') return creditCardProcessingPercent;
                            if (m === 'check') return checkProcessingPercent;
                            return 0;
                          })();
                          for (let i = 0; i < logs.length; i++) {
                            const ml = logs[i];
                            const st = String(ml?.status || '');
                            // Recalculate for Scheduled and Invoiced (not yet paid), but not for Paid/Partial/Cancelled
                            if (st !== 'Scheduled' && st !== 'Invoiced') continue;
                            const baseComponents = (
                              Number(ml?.expectedRevenueBase || 0) +
                              Number(ml?.expectedTransport || 0) +
                              Number(ml?.expectedCommission || 0)
                            );
                            if (baseComponents > 0) {
                              const fee = Math.round((baseComponents * (pct / 100)) * 100) / 100;
                              const total = Math.round((baseComponents + fee) * 100) / 100;
                              logs[i].expectedProcessingFee = fee;
                              logs[i].expectedAmount = total;
                            }
                          }
                          setFormData({ ...nextForm, milestoneLogs: logs });
                        } catch (_) {
                          setFormData(nextForm);
                        }
                      }}
                    >
                      <MenuItem value="Check">Check</MenuItem>
                      <MenuItem value="Transfer">Transfer</MenuItem>
                      <MenuItem value="Credit Card">Credit Card</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <InputLabel>Scheduled AR Timing</InputLabel>
                    <Select
                      value={normalizeScheduledReceivableTiming(formData.scheduledReceivableTiming)}
                      label="Scheduled AR Timing"
                      onChange={(e) => setFormData((prev) => ({
                        ...prev,
                        scheduledReceivableTiming: normalizeScheduledReceivableTiming(e.target.value)
                      }))}
                    >
                      {scheduledReceivableTimingOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    Controls when Scheduled payments appear in Accounts Receivable.
                  </Typography>
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Signed Date"
                    type="date"
                    value={formData.signedDate}
                    onChange={(e) => setFormData((prev) => ({ ...prev, signedDate: e.target.value }))}
                    InputLabelProps={{ shrink: true }}
                    disabled={editingContract && !isSa}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Start Date"
                    type="date"
                    value={formData.startDate}
                    onChange={(e) => {
                      const newStartDate = e.target.value;
                      const prev = formData.startDate;
                      // Validate against end date if present
                      if (formData.endDate) {
                        const endTime = new Date(`${formData.endDate}T00:00:00`).getTime();
                        const nextTime = new Date(`${newStartDate}T00:00:00`).getTime();
                        if (endTime && nextTime && nextTime >= endTime) {
                          setSnackbar({ open: true, message: 'Start date must be before end date', severity: 'error' });
                          return;
                        }
                      }
                      // Recalculate milestones and totals immediately using core engine
                      const recalc = recalculateTotalsAndMilestones(
                        formData.paymentSchedule,
                        formData.partnerRoles || [],
                        { ...formData, startDate: newStartDate }
                      );
                      setFormData({
                        ...formData,
                        startDate: newStartDate,
                        milestoneLogs: recalc.milestoneLogs,
                        totalAmount: recalc.totalAmount,
                        totalTransportFees: recalc.totalTransportFees,
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={formData.startDateLocked || !formData.beneficiaryCompanyId}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="End Date"
                    type="date"
                    value={formData.endDate}
                    onChange={(e) => {
                      const newEndDate = e.target.value;
                      const prev = formData.endDate;
                      const prevTime = prev ? new Date(`${prev}T00:00:00`).getTime() : 0;
                      const nextTime = newEndDate ? new Date(`${newEndDate}T00:00:00`).getTime() : 0;
                      // Enforce end must be strictly after start
                      if (formData.startDate) {
                        const startTime = new Date(`${formData.startDate}T00:00:00`).getTime();
                        if (nextTime && startTime && nextTime <= startTime) {
                          setSnackbar({ open: true, message: 'End date must be after start date', severity: 'error' });
                          return;
                        }
                      }
                      // Recalculate milestones and totals immediately using core engine
                      const recalc = recalculateTotalsAndMilestones(
                        formData.paymentSchedule,
                        formData.partnerRoles || [],
                        { ...formData, endDate: newEndDate }
                      );
                      setFormData({
                        ...formData,
                        endDate: newEndDate,
                        milestoneLogs: recalc.milestoneLogs,
                        totalAmount: recalc.totalAmount,
                        totalTransportFees: recalc.totalTransportFees,
                      });
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={!formData.startDate}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={formData.notes}
                    onChange={(e) => setFormData((prev) => ({ ...prev, notes: e.target.value }))}
                  />
                </Grid>
           
              </Grid>
            </TabPanel>


            {/* Services Tab */}
            <TabPanel value={currentTab} index={1}>
              <Box sx={{ mb: 2, display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                <Autocomplete
                  sx={{ flex: 1, minWidth: 280 }}
                  size="small"
                  options={getAvailableServicesForDropdown()}
                  value={formData._selectedServiceOption || null}
                    onChange={(_, newValue) => setFormData((prev) => ({ ...prev, _selectedServiceId: newValue ? newValue.id : '', _selectedServiceOption: newValue || null }))}
                  filterOptions={(opts, { inputValue }) =>
                    filterServiceOptions(opts, inputValue, (option) => {
                      const isCost = typeof option?.internal_cost === 'number';
                      const vendorEntityId = option?.vendorEntityId || option?.vendorId || option?.entity_id || (option?.vendor && (option.vendor.id || option.vendor.entity_id)) || null;
                      const vendorName = (vendorEntityId && entities.find((e) => e.id === vendorEntityId)?.entityName) || option?.vendorEntityName || '';
                      const price = isCost
                        ? (typeof option.margin_percent === 'number' ? option.internal_cost * (1 + option.margin_percent / 100) : null)
                        : (typeof option?.unitPrice === 'number' ? option.unitPrice : null);
                      return [
                        option?.name,
                        option?.category,
                        option?.serviceType,
                        frequencySearchText(option?.frequency),
                        vendorName,
                        option?.description,
                        option?.execution_location,
                        price !== null ? `${formatCurrency(price, formData.currency || 'USD')} ${price.toFixed(2)}` : null,
                      ];
                    })
                  }
                  getOptionLabel={(option) => {
                    if (!option || typeof option !== 'object') return '';
                    const isCost = typeof option?.internal_cost === 'number';
                    const name = option?.name || '';
                    const category = option?.category || '';
                    const freqRaw = isCost ? (option?.frequency || '') : (option?.frequency || '');
                    const frequency = String(freqRaw).replaceAll('_', ' ');
                    const vendorName = (() => {
                      try {
                        const vendorEntityId = option?.vendorEntityId || option?.vendorId || option?.entity_id || (option?.vendor && (option.vendor.id || option.vendor.entity_id)) || null;
                        if (vendorEntityId) {
                          const ent = entities.find((e) => e.id === vendorEntityId);
                          return ent?.entityName || '';
                        }
                        return option?.vendorEntityName || '';
                      } catch (_) {
                        return '';
                      }
                    })();
                    const price = isCost
                      ? (typeof option.internal_cost === 'number' && typeof option.margin_percent === 'number'
                          ? option.internal_cost * (1 + option.margin_percent / 100)
                          : null)
                      : (typeof option?.unitPrice === 'number' ? option.unitPrice : null);
                    const base = [category, name, frequency, vendorName].filter(Boolean).join(' - ');
                    return price !== null ? `${base} - ${formatCurrency(price, formData.currency || 'USD')}` : base;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  renderInput={(params) => (
                    <TextField {...params} label="Add Service" fullWidth />
                  )}
                />
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  disabled={saving}
                  onClick={async () => {
                    const selected = formData._selectedServiceOption;
                    const svc = selected
                      || services.find((s) => s.id === formData._selectedServiceId)
                      || costs.find((c) => c.id === formData._selectedServiceId)
                      || (formData.services || []).find((s) => s.id === formData._selectedServiceId)
                      || (formData.proposalSnapshot?.servicesFromProposal || []).find((s) => s.id === formData._selectedServiceId);
                    if (!svc) {
                      setSnackbar({ open: true, message: 'Select a service', severity: 'warning' });
                      return;
                    }
                    const qty = 1;
                    const assigned = formData.beneficiaryCompanyId || '';
                    const isCost = typeof svc?.internal_cost === 'number';
                    const svcNormalized = isCost
                      ? convertCostToContractService(svc, qty, assigned)
                      : { ...svc, quantity: qty, assignedEntityId: assigned };
                    
                    // Build the new service with unique ID inline to enable immediate save
                    const uniqueId = `contract-service-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
                    const newService = {
                      ...svcNormalized,
                      id: uniqueId,
                      vendorEntityId: svcNormalized.vendorEntityId || svcNormalized.vendorId || svcNormalized.entity_id || (svcNormalized.vendor && (svcNormalized.vendor.id || svcNormalized.vendor.entity_id)) || null,
                      assignedEntityId: assigned,
                      subAccountId: null,
                      addedAt: new Date().toISOString(),
                      addedBy: currentUser.uid,
                      oneTimeApplied: !!svcNormalized.oneTimeApplied
                    };
                    
                    const updatedServices = [...(formData.services || []), newService];
                    const newSchedule = regeneratePaymentSchedule(updatedServices, formData.partnerRoles || [], {
                      currentSchedule: formData.paymentSchedule,
                      clientEntityId: formData.clientEntityId,
                    });
                    const baseForRecalc = { ...formData, services: updatedServices, paymentSchedule: newSchedule };
                    const recalculated = recalculateTotalsAndMilestones(newSchedule, formData.partnerRoles || [], baseForRecalc);
                    
                    const updatedFormData = {
                      ...formData,
                      services: updatedServices,
                      paymentSchedule: newSchedule,
                      totalAmount: recalculated.totalAmount,
                      totalTransportFees: recalculated.totalTransportFees,
                      milestoneLogs: recalculated.milestoneLogs,
                      serviceModifications: [...(formData.serviceModifications || []), {
                        id: Date.now(),
                        type: serviceModificationTypes.ADD,
                        serviceId: newService.id,
                        serviceName: newService.name,
                        timestamp: new Date().toISOString(),
                        userId: currentUser.uid,
                      }],
                      _selectedServiceId: '',
                      _selectedServiceOption: null,
                    };
                    
                    // Update local state immediately
                    setFormData(updatedFormData);
                    setJustAddedServiceId(newService.id);
                    
                    // Auto-save to Firebase if editing an existing contract (so oneTimeApplied propagates correctly)
                    if (editingContract?.id) {
                      try {
                        await updateContract(editingContract.id, updatedFormData);
                        setSnackbar({ open: true, message: 'Service added and saved to contract', severity: 'success' });
                      } catch (err) {
                        console.error('Auto-save failed after adding service:', err);
                        setSnackbar({ open: true, message: 'Service added locally but save failed - please click Save', severity: 'warning' });
                      }
                    } else {
                      setSnackbar({ open: true, message: 'Service added to contract', severity: 'success' });
                    }
                  }}
                >
                  {saving ? <CircularProgress size={20} /> : 'Add'}
                </Button>
              </Box>

              <Card variant="outlined" sx={{ userSelect: 'text' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1 }}>
                    <Typography variant="h6">Contract Services</Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<CopyIcon />}
                      onClick={handleCopyServicesText}
                      disabled={sortedServices.length === 0}
                    >
                      Copy Services Text
                    </Button>
                  </Box>
                  {(sortedServices.length === 0) ? (
                    <Typography color="text.secondary">No services added yet.</Typography>
                  ) : (
                    <>
                      {activeMonthlyServices.length > 0 && (
                        <>
                          <Typography variant="subtitle2" sx={{ mt: 1, mb: 1 }}>Monthly</Typography>
                          {activeMonthlyServices.map((svc, idx) => (
                            <React.Fragment key={`${svc.id}-${idx}`}>{renderServiceAccordion(svc)}</React.Fragment>
                          ))}
                        </>
                      )}
                      {activeOneTimeServices.length > 0 && (
                        <>
                          {activeMonthlyServices.length > 0 && <Divider sx={{ my: 2 }} />}
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>One-time</Typography>
                          {activeOneTimeServices.map((svc, idx) => (
                            <React.Fragment key={`${svc.id}-${idx}`}>{renderServiceAccordion(svc)}</React.Fragment>
                          ))}
                        </>
                      )}
                      {historyServices.length > 0 && (
                        <>
                          {(activeMonthlyServices.length + activeOneTimeServices.length) > 0 && <Divider sx={{ my: 2 }} />}
                          <Box
                            onClick={() => setServicesHistoryOpen((open) => !open)}
                            sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, cursor: 'pointer', userSelect: 'none' }}
                          >
                            <ExpandMoreIcon
                              fontSize="small"
                              sx={{ transform: servicesHistoryOpen ? 'none' : 'rotate(-90deg)', transition: 'transform 150ms' }}
                            />
                            <Typography variant="subtitle2">History</Typography>
                            <Typography variant="caption" color="text.secondary">({historyServices.length})</Typography>
                          </Box>
                          <Collapse in={servicesHistoryOpen} unmountOnExit>
                            {historyServices.map((svc, idx) => (
                              <React.Fragment key={`${svc.id}-${idx}`}>{renderServiceAccordion(svc)}</React.Fragment>
                            ))}
                          </Collapse>
                        </>
                      )}
                    </>
                  )}
                </CardContent>
              </Card>
            </TabPanel>
 
            {/* Partner Roles Tab */}
            <TabPanel value={currentTab} index={2}>
              <Card variant="outlined">
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h6">Partner Roles</Typography>

                  </Box>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Role</TableCell>
                        <TableCell>Entity</TableCell>
                        <TableCell>Percent</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(formData.partnerRoles || []).length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={4} align="center">
                            <Typography color="text.secondary">No partner roles. Select a proposal or add roles in your proposal to populate.</Typography>
                          </TableCell>
                        </TableRow>
                      ) : (
                        formData.partnerRoles.map((role, idx) => (
                          <TableRow key={`${role.positionId}-${idx}`} hover>
                            <TableCell>{role.positionName || role.positionId}</TableCell>
                            <TableCell>
                              <FormControl size="small" sx={{ minWidth: 220 }} disabled={String(formData.status || '') === 'Active'}>
                                <InputLabel>Entity</InputLabel>
                                <Select
                                  label="Entity"
                                  value={(role.positionId === 'company') ? (role.entityId || '') : (role.entityId ?? 'nobody')}
                                  onChange={(e) => handlePartnerRoleChange(idx, 'entityId', e.target.value)}
                                  disabled={String(formData.status || '') === 'Active'}
                                >
                                  <MenuItem value="">Unassigned</MenuItem>
                                  {role.positionId !== 'company' && (
                                    <MenuItem value="nobody">Nobody</MenuItem>
                                  )}
                                  {(role.positionId === 'company'
                                    ? entities.filter((e) => e.entityType === 'company' && e.accountType !== 'sub')
                                    : entities.filter((e) => isActivePartnerEntity(e))
                                  ).map((e) => (
                                    <MenuItem key={e.id} value={e.id}>{e.entityName}</MenuItem>
                                  ))}
                                </Select>
                              </FormControl>
                            </TableCell>
                            <TableCell>
                              <TextField
                                key={`percent-${role.positionId}-${role.entityId ?? (String(role.entityName||'').toLowerCase()==='nobody'?'nobody':'assigned')}`}
                                size="small"
                                type="text"
                                inputProps={{ inputMode: 'decimal', pattern: '^\\d*\\.?\\d*$' }}
                                value={((role.entityId == null) && String(role.entityName || '').toLowerCase() === 'nobody' && role.positionId !== 'company') ? 0 : role.percent}
                                onChange={(e) => handlePartnerRoleChange(idx, 'percent', e.target.value)}
                                disabled={String(formData.status || '') === 'Active' || ((role.entityId == null) && String(role.entityName || '').toLowerCase() === 'nobody' && role.positionId !== 'company')}
                              />
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </TabPanel>


            {/* Enhanced Milestones & Financial Integration Tab */}
            <TabPanel value={currentTab} index={3}>
              {/* Unified Financial Milestones & Integration Section */}
        

              {/* Unified Financial Summary Cards - Invoiced, Paid, Scheduled, Available */}
              <Grid container spacing={2} sx={{ mb: 4 }}>
                {(() => {
                  const milestones = Array.isArray(formData.milestoneLogs) ? formData.milestoneLogs : [];
                  let totalInvoiced = 0;
                  let totalPaid = 0;
                  let totalScheduled = 0;
                  let totalApplied = 0;

                  milestones.forEach((m) => {
                    const expected = Number(m.expectedAmount) || 0;
                    const actual = Number(m.actualAmount) || 0;
                    const applied = Number(m.appliedAmount) || 0;

                    // Paid portion
                    const paidPortion = (m.status === 'Paid')
                      ? (actual > 0 ? Math.min(actual, expected) : expected)
                      : (m.status === 'Partial' ? Math.min(actual, expected) : 0);
                    totalPaid += paidPortion;

                    // Invoiced (unpaid) portion
                    const invoicedPortion = (m.status === 'Invoiced')
                      ? expected
                      : (m.status === 'Partial' ? Math.max(0, expected - paidPortion) : 0);
                    totalInvoiced += invoicedPortion;

                    // Scheduled portion
                    if (m.status === 'Scheduled') {
                      totalScheduled += expected;
                    }

                    totalApplied += applied;
                  });

                  const available = Math.max(0, (totalPaid || 0) - (totalApplied || 0));

                  return (
                    <>
                      {/* Invoiced */}
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'warning.50', border: '1px solid', borderColor: 'warning.200' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="warning.main" fontWeight="bold">
                              {formatCurrency(totalInvoiced, formData.currency)}
                            </Typography>
                            <Typography variant="body2" color="warning.dark">
                              Invoiced
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      {/* Paid */}
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'success.50', border: '1px solid', borderColor: 'success.200' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="success.main" fontWeight="bold">
                              {formatCurrency(totalPaid, formData.currency)}
                            </Typography>
                            <Typography variant="body2" color="success.dark">
                              Paid
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      {/* Scheduled */}
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'info.50', border: '1px solid', borderColor: 'info.200' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="info.main" fontWeight="bold">
                              {formatCurrency(totalScheduled, formData.currency)}
                            </Typography>
                            <Typography variant="body2" color="info.dark">
                              Scheduled
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      {/* Available */}
                      <Grid item xs={6} md={3}>
                        <Card sx={{ bgcolor: 'primary.50', border: '2px solid', borderColor: 'primary.main' }}>
                          <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="h4" color="primary.main" fontWeight="bold">
                              {formatCurrency(Number(getEffectiveClientAvailable(formData.clientEntityId) || 0), 'USD')}
                            </Typography>
                            <Typography variant="body2" color="primary.dark">
                              Client Available
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grid>
                      

                    </>
                  );
                })()}
              </Grid>

              {/* Milestone Tracking Section */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">Invoices</Typography>
                {/* <Typography variant="body2" color="text.secondary">
                  Client Available: <strong>{new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(clientAvailableBalance)}</strong>
                </Typography> */}
                {/* <Button 
                  startIcon={<AddIcon />} 
                  onClick={() => {
                    if (!canEditPayments) return;
                    const nextIndex = (formData.milestoneLogs || []).length + 1;
                      const updated = [...formData.milestoneLogs, {
                      id: Date.now(),
                      milestone: String(nextIndex),
                      expectedDate: '',
                      actualDate: '',
                      expectedAmount: 0,
                        actualAmount: 0,
                        appliedAmount: 0,
                      status: 'Scheduled',
                      financialTriggers: []
                    }];
                    setFormData({
                      ...formData,
                      milestoneLogs: reindexMilestones(updated)
                    });
                  }}
                  variant="outlined"
                  size="small"
                  disabled={!canEditPayments}
                >
                  Add Invoice
                </Button>
                 */}
              </Box>

              {/* Milestones Table */}
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                      <TableRow>
                        <TableCell>Milestone</TableCell>
                        <TableCell>Expected</TableCell>
                        <TableCell>Invoice ID</TableCell>
                        <TableCell>Invoice Link</TableCell>
                        <TableCell>Invoice</TableCell>
                        <TableCell>Applied</TableCell>
                        <TableCell>Status</TableCell>
                        {/* <TableCell>Actions</TableCell> */}
                      </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.milestoneLogs.map((milestone, index) => (
                      <TableRow key={milestone.id}>
                        <TableCell>
                          <Typography variant="body2" fontWeight="bold">{index + 1}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{milestone.expectedDate || '—'}</Typography>
                        </TableCell>
                        
                        <TableCell>
                          {(() => {
                            const isLocked = ['Paid', 'Cancelled', 'Partial', 'Skip'].includes(milestone.status);
                            return (
                              <TextField
                                size="small"
                                placeholder="INV-0001"
                                value={milestone.invoiceId || ''}
                                onChange={(e) => {
                                  if (isLocked || !canEditInvoiceMeta) return;
                                  const newMilestones = reindexMilestones([...formData.milestoneLogs]);
                                  newMilestones[index].invoiceId = e.target.value;
                                  setFormData({ ...formData, milestoneLogs: newMilestones });
                                  queueInvoiceSave(milestone.id, 'invoiceId', e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (isLocked || !canEditInvoiceMeta) return;
                                  saveInvoiceMeta(milestone.id, 'invoiceId', e.target.value);
                                }}
                                variant="outlined"
                                disabled={isLocked || !canEditInvoiceMeta}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const isLocked = ['Paid', 'Cancelled', 'Partial', 'Skip'].includes(milestone.status);
                            return (
                              <TextField
                                size="small"
                                placeholder="https://..."
                                value={milestone.invoiceUrl || ''}
                                onChange={(e) => {
                                  if (isLocked || !canEditInvoiceMeta) return;
                                  const newMilestones = reindexMilestones([...formData.milestoneLogs]);
                                  newMilestones[index].invoiceUrl = e.target.value;
                                  setFormData({ ...formData, milestoneLogs: newMilestones });
                                  queueInvoiceSave(milestone.id, 'invoiceUrl', e.target.value);
                                }}
                                onBlur={(e) => {
                                  if (isLocked || !canEditInvoiceMeta) return;
                                  saveInvoiceMeta(milestone.id, 'invoiceUrl', e.target.value);
                                }}
                                variant="outlined"
                                disabled={isLocked || !canEditInvoiceMeta}
                              />
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const expectedAmt = Number(milestone.expectedAmount || 0);
                            const hasExplicitFee = typeof milestone.expectedProcessingFee === 'number';
                            const baseComponents = (
                              Number(milestone.expectedRevenueBase || 0) +
                              Number(milestone.expectedTransport || 0) +
                              Number(milestone.expectedCommission || 0)
                            );
                            const computedFee = !hasExplicitFee && baseComponents > 0
                              ? (() => {
                                  // ⚠️ FALLBACK: This should rarely/never trigger after milestones.js fix
                                  console.warn('[Contracts][Display] Using fallback processing fee calculation (expectedProcessingFee missing):', {
                                    milestoneId: milestone.id,
                                    status: milestone.status,
                                    expectedDate: milestone.expectedDate,
                                    hasExplicitFee,
                                    baseComponents,
                                    milestone
                                  });
                                  const method = String(formData.defaultPaymentMethod || '').toLowerCase();
                                  const pct = (method === 'credit card' || method === 'creditcard' || method === 'credit_card')
                                    ? creditCardProcessingPercent
                                    : (method === 'check' ? checkProcessingPercent : 0);
                                  return Math.round((baseComponents * (pct / 100)) * 100) / 100;
                                })()
                              : Math.round((Number(milestone.expectedProcessingFee || 0)) * 100) / 100;
                            const showFee = computedFee > 0;
                            return (
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography variant="body2" fontWeight="bold">{formatCurrency(expectedAmt, 'USD')}</Typography>
                                
                              </Box>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          {(() => {
                            const isLocked = ['Paid', 'Cancelled', 'Partial', 'Skip'].includes(milestone.status);
                            return (
                              <>
                              <TextField
                                size="small"
                                type="text"
                                value={String(milestone.appliedAmount ?? '')}
                                placeholder={Number(milestone.appliedAmount || 0).toFixed(2)}
                                onChange={(e) => {
                                  if (isLocked || !canEditPayments) return;
                                  const text = e.target.value;
                                  const sanitized = text.replace(/[^0-9.]/g, '');
                                  const next = sanitized;
                                  const newMilestones = reindexMilestones([...formData.milestoneLogs]);
                                  newMilestones[index].appliedAmount = next;
                                  setFormData({ ...formData, milestoneLogs: newMilestones });
                                }}
                                onBlur={() => {
                                  if (isLocked || !canEditPayments) return;
                                  const allMilestones = [...formData.milestoneLogs];
                                  const cur = allMilestones[index];
                                  const inputNum = Number(cur.appliedAmount);
                                  const normalized = Number.isFinite(inputNum) && inputNum >= 0 ? Math.round(inputNum * 100) / 100 : 0;
                                  const effectiveAvailPlusCurrent = Number(getEffectiveClientAvailable(formData.clientEntityId) || 0) + Number(milestone.appliedAmount || 0);
                                  const invoiceMax = Math.max(0, Number(milestone.actualAmount) || Number(milestone.expectedAmount) || 0);
                                  const maxForThis = Math.min(effectiveAvailPlusCurrent, invoiceMax);
                                  const finalApplied = Math.min(normalized, maxForThis);
                                  const newMilestones = reindexMilestones([...allMilestones]);
                                  newMilestones[index].appliedAmount = Number.isFinite(finalApplied) ? finalApplied : 0;
                                  setFormData({ ...formData, milestoneLogs: newMilestones });
                                }}
                                variant="outlined"
                                inputProps={{ inputMode: 'decimal', pattern: '^\\d*\\.?\\d*$' }}
                                disabled={isLocked || !canEditPayments}
                              />
                              </>
                            );
                          })()}
                        </TableCell>
                        
                        <TableCell>
                          <FormControl size="small" sx={{ minWidth: 100 }} disabled={!canEditPayments}>
                            <Select
                              value={milestone.status || 'Scheduled'}
                              onChange={async (e) => {
                                if (!canEditPayments) return;
                                const current = milestone.status;
                                const next = e.target.value;
                                const allowed = allowedMilestoneTransitions[current] || [];
                                if (current !== next && !allowed.includes(next)) {
                                  setSnackbar({ open: true, message: 'Invalid status transition', severity: 'error' });
                                  return;
                                }
                                
                                try {
                                  // If moving to Paid and applied is not set, auto-fill to expected
                                  const latestSavedMilestoneLogs = getLatestSavedMilestoneLogs(editingContract?.id);
                                  const previousMilestoneLogs = preserveInvoiceMetadata(
                                    Array.isArray(formData.milestoneLogs) ? formData.milestoneLogs : [],
                                    latestSavedMilestoneLogs
                                  );
                                  let milestoneLogsForApply = previousMilestoneLogs.map((m)=>({ ...m }));
                                  
                                  // 🔧 FIX: Ensure expectedCommission is set before status transitions
                                  // This backfills missing expectedCommission for new contracts or old milestones
                                  // ⚠️ CRITICAL: Backfill missing fields but preserve existing expectedAmount
                                  const milestoneIdx = milestoneLogsForApply.findIndex((m)=>m && m.id === milestone.id);
                                  if (milestoneIdx >= 0) {
                                    const m = milestoneLogsForApply[milestoneIdx];
                                    const hasMissingCriticalFields = (
                                      typeof m.expectedCommission !== 'number' || 
                                      typeof m.expectedRevenueBase !== 'number'
                                    );
                                    
                                    // Backfill if critical fields are missing (required by Invoiced transition)
                                    // But will preserve existing expectedAmount to avoid changing milestone amounts
                                    if (hasMissingCriticalFields) {
                                      console.warn('[Contracts][StatusChange] Backfilling missing milestone fields:', {
                                        milestoneId: milestone.id,
                                        hasExpectedCommission: typeof m.expectedCommission === 'number',
                                        hasExpectedRevenueBase: typeof m.expectedRevenueBase === 'number',
                                        milestone: m
                                      });
                                      
                                      // Calculate from contract data
                                      const partnerPercent = (Array.isArray(formData.partnerRoles) ? formData.partnerRoles : [])
                                        .reduce((sum, r) => sum + (Number(r?.percent) || 0), 0) / 100;
                                      
                                      const ym = String(m.expectedDate || '');
                                      const isMonthly = (freq) => String(freq || '').toLowerCase() === 'monthly';
                                      const services = Array.isArray(formData.services) ? formData.services : [];
                                      
                                      // ✅ FIX: Use isServiceActiveForMonth helper for consistent cancellation filtering
                                      const monthlyActive = services.filter((s) => 
                                        isMonthly(s?.frequency) && isServiceActiveForMonth(s, ym)
                                      );
                                      const monthlyRevenue = monthlyActive.reduce((sum, s) => sum + (Number(s?.amount) || 0), 0);
                                      const monthlyInternal = monthlyActive.reduce((sum, s) => 
                                        sum + ((Number(s?.internalUnitCost) || 0) * (Number(s?.quantity) || 0)), 0
                                      );
                                      
                                      // ⚠️ CRITICAL FIX: Determine which one-time services belong to THIS milestone
                                      // Use oneTimeAppliedMilestoneId to match services to the milestone they were invoiced on
                                      // This matches the logic in milestones.js getActiveServicesForMilestone()
                                      const currentMilestoneId = m.id;
                                      
                                      const oneTimeServicesForThisMilestone = services.filter((s) => {
                                        if (isMonthly(s?.frequency)) return false;
                                        
                                        // If service has explicit milestone ID, use that
                                        if (s?.oneTimeAppliedMilestoneId) {
                                          return s.oneTimeAppliedMilestoneId === currentMilestoneId;
                                        }
                                        
                                        // If service not yet applied, check if this is the appropriate milestone
                                        if (!s?.oneTimeApplied) {
                                          // Find the first invoiced/paid/partial milestone that should include this service
                                          // based on when the service was added
                                          const svcAddedAt = s?.addedAt ? new Date(s.addedAt) : null;
                                          
                                          for (let i = 0; i < milestoneLogsForApply.length; i++) {
                                            const ml = milestoneLogsForApply[i];
                                            if (!ml || ml.status === 'Cancelled' || ml.status === 'Skip') continue;
                                            
                                            // If no addedAt, service was there from start - use first non-cancelled milestone
                                            if (!svcAddedAt) return i === milestoneIdx;
                                            
                                            // Check if service was added before/during this milestone's month
                                            const mlDate = ml.expectedDate ? new Date(ml.expectedDate + '-01') : null;
                                            if (!mlDate) continue;
                                            
                                            if (svcAddedAt <= mlDate || svcAddedAt.toISOString().slice(0, 7) <= ml.expectedDate) {
                                              return i === milestoneIdx;
                                            }
                                          }
                                          return false;
                                        }
                                        
                                        return false;
                                      });
                                      
                                      const oneTimeRevenue = oneTimeServicesForThisMilestone.reduce((sum, s) => sum + (Number(s?.amount) || 0), 0);
                                      const oneTimeInternal = oneTimeServicesForThisMilestone.reduce((sum, s) => 
                                        sum + ((Number(s?.internalUnitCost) || 0) * (Number(s?.quantity) || 0)), 0
                                      );
                                      
                                      console.log('[Contracts][StatusChange][Backfill] One-time services for milestone:', {
                                        milestoneId: currentMilestoneId,
                                        milestoneStatus: m.status,
                                        expectedDate: m.expectedDate,
                                        oneTimeServicesCount: oneTimeServicesForThisMilestone.length,
                                        oneTimeServices: oneTimeServicesForThisMilestone.map(s => ({
                                          name: s?.name,
                                          oneTimeAppliedMilestoneId: s?.oneTimeAppliedMilestoneId,
                                          oneTimeApplied: s?.oneTimeApplied,
                                          addedAt: s?.addedAt,
                                          internalCost: Number(s?.internalUnitCost) || 0,
                                          quantity: Number(s?.quantity) || 0
                                        })),
                                        oneTimeRevenue,
                                        oneTimeInternal
                                      });
                                      
                                      const totalRevenue = monthlyRevenue + oneTimeRevenue;
                                      const totalInternal = monthlyInternal + oneTimeInternal;
                                      const commission = Math.round((totalInternal * partnerPercent) * 100) / 100;
                                      
                                      // Set missing values
                                      if (typeof m.expectedRevenueBase !== 'number') {
                                        milestoneLogsForApply[milestoneIdx].expectedRevenueBase = Math.round(totalRevenue * 100) / 100;
                                      }
                                      if (typeof m.expectedCommission !== 'number') {
                                        milestoneLogsForApply[milestoneIdx].expectedCommission = commission;
                                      }
                                      if (typeof m.expectedTransport !== 'number') {
                                        milestoneLogsForApply[milestoneIdx].expectedTransport = 0;
                                      }
                                      
                                      // ⚠️ CRITICAL: Preserve existing expectedAmount if it exists
                                      // Only recalculate if it's missing - milestone amounts should NEVER change
                                      const existingExpectedAmount = milestoneLogsForApply[milestoneIdx].expectedAmount;
                                      const shouldPreserveAmount = typeof existingExpectedAmount === 'number' && existingExpectedAmount > 0;
                                      
                                      if (shouldPreserveAmount) {
                                        console.log('[Contracts][StatusChange] Preserving existing expectedAmount (NOT recalculating):', {
                                          milestoneId: milestone.id,
                                          existingExpectedAmount,
                                          preservedOriginalValue: true
                                        });
                                      } else {
                                        // Only recalculate if expectedAmount is missing
                                        const baseComponents = (
                                          Number(milestoneLogsForApply[milestoneIdx].expectedRevenueBase || 0) +
                                          Number(milestoneLogsForApply[milestoneIdx].expectedTransport || 0) +
                                          Number(milestoneLogsForApply[milestoneIdx].expectedCommission || 0)
                                        );
                                        
                                        if (baseComponents > 0) {
                                          const method = String(formData.defaultPaymentMethod || 'Transfer').toLowerCase();
                                          const pct = (method === 'credit card' || method === 'creditcard' || method === 'credit_card')
                                            ? creditCardProcessingPercent
                                            : (method === 'check' ? checkProcessingPercent : 0);
                                          const fee = Math.round((baseComponents * (pct / 100)) * 100) / 100;
                                          const total = Math.round((baseComponents + fee) * 100) / 100;
                                          milestoneLogsForApply[milestoneIdx].expectedProcessingFee = fee;
                                          milestoneLogsForApply[milestoneIdx].expectedAmount = total;
                                        }
                                      }
                                      
                                      console.log('[Contracts][StatusChange] Backfilled milestone values:', {
                                        milestoneId: milestone.id,
                                        expectedRevenueBase: milestoneLogsForApply[milestoneIdx].expectedRevenueBase,
                                        expectedCommission: milestoneLogsForApply[milestoneIdx].expectedCommission,
                                        expectedTransport: milestoneLogsForApply[milestoneIdx].expectedTransport,
                                        expectedProcessingFee: milestoneLogsForApply[milestoneIdx].expectedProcessingFee,
                                        expectedAmount: milestoneLogsForApply[milestoneIdx].expectedAmount
                                      });
                                      
                                      // Update formData immediately so backfilled values are available
                                      const mergedBackfilledMilestones = preserveInvoiceMetadata(milestoneLogsForApply, latestSavedMilestoneLogs);
                                      milestoneLogsForApply = mergedBackfilledMilestones;
                                      setFormData(prev => ({
                                        ...prev,
                                        milestoneLogs: preserveInvoiceMetadata(mergedBackfilledMilestones, prev.milestoneLogs || [])
                                      }));
                                      
                                      // Persist backfilled values to database if contract is saved
                                      if (editingContract?.id) {
                                        try {
                                          await updateDoc(doc(db, 'contracts', editingContract.id), { 
                                            milestoneLogs: mergedBackfilledMilestones 
                                          });
                                          console.log('[Contracts][StatusChange] Persisted backfilled milestone values to database');
                                        } catch (err) {
                                          console.error('[Contracts][StatusChange] Failed to persist backfilled values:', err);
                                        }
                                      }
                                    }
                                  }
                                  
                                  if (next === 'Paid') {
                                    const idx = milestoneLogsForApply.findIndex((m)=>m && m.id === milestone.id);
                                    if (idx >= 0) {
                                      const expected = Number(milestoneLogsForApply[idx].expectedAmount || 0);
                                      const actual = Number(milestoneLogsForApply[idx].actualAmount || 0);
                                      const invoiceTotal = actual > 0 ? Math.min(actual, expected) : expected;
                                      let appliedAmount = Number(milestoneLogsForApply[idx].appliedAmount || 0);
                                      
                                      // 🔍 RACE CONDITION LOG: Track Paid status change validation
                                      console.log('[Contracts][StatusChange] PAID VALIDATION START:', {
                                        timestamp: new Date().toISOString(),
                                        contractId: editingContract?.id,
                                        milestoneId: milestone.id,
                                        currentStatus: milestone.status,
                                        nextStatus: next,
                                        expected,
                                        actual,
                                        invoiceTotal,
                                        appliedAmount,
                                        clientAvailBefore: getEffectiveClientAvailable(formData.clientEntityId)
                                      });
                                      
                                      if (appliedAmount + 1e-6 < invoiceTotal) {
                                        setSnackbar({ open: true, message: 'Cannot mark Paid: applied is less than amount', severity: 'warning' });
                                        return;
                                      }
                                      const wasNormalized = Math.abs(appliedAmount - invoiceTotal) > 0.01;
                                      if (wasNormalized) {
                                        console.log('[Contracts][StatusChange] NORMALIZING APPLIED AMOUNT:', {
                                          timestamp: new Date().toISOString(),
                                          contractId: editingContract?.id,
                                          milestoneId: milestone.id,
                                          appliedBefore: appliedAmount,
                                          invoiceTotal,
                                          delta: invoiceTotal - appliedAmount
                                        });
                                        milestoneLogsForApply[idx].appliedAmount = invoiceTotal;
                                        appliedAmount = invoiceTotal;
                                      }
                                      
                                      // Check available balance BEFORE applying to ensure exact coverage
                                      const availableBefore = Number(getEffectiveClientAvailable(formData.clientEntityId) || 0);
                                      if (availableBefore + 1e-6 < appliedAmount) {
                                        setSnackbar({ open: true, message: 'Insufficient client available balance to mark Paid', severity: 'warning' });
                                        return;
                                      }
                                      
                                      const availableAfter = Number(getEffectiveClientAvailable(formData.clientEntityId, milestoneLogsForApply) || 0);
                                      
                                      console.log('[Contracts][StatusChange] PAID VALIDATION COMPLETE:', {
                                        timestamp: new Date().toISOString(),
                                        contractId: editingContract?.id,
                                        milestoneId: milestone.id,
                                        wasNormalized,
                                        finalApplied: appliedAmount,
                                        availableBefore,
                                        availableAfter,
                                        willProceed: true
                                      });
                                    }
                                  }
                                  if (next === 'Partial') {
                                    const idx = milestoneLogsForApply.findIndex((m)=>m && m.id === milestone.id);
                                    if (idx >= 0) {
                                      const expected = Number(milestoneLogsForApply[idx].expectedAmount || 0);
                                      const appliedAmount = Number(milestoneLogsForApply[idx].appliedAmount || 0);
                                      if (!(appliedAmount > 0 && appliedAmount < expected)) {
                                        setSnackbar({ open: true, message: 'Partial requires applied > 0 and < invoice total', severity: 'warning' });
                                        return;
                                      }
                                      const availableAfter = Number(getEffectiveClientAvailable(formData.clientEntityId, milestoneLogsForApply) || 0);
                                      if (availableAfter + 1e-6 < 0) {
                                        setSnackbar({ open: true, message: 'Insufficient client available balance for Partial', severity: 'warning' });
                                        return;
                                      }
                                    }
                                  }
                                  const contractForApply = {
                                    id: editingContract?.id,
                                    beneficiaryCompanyId: formData.beneficiaryCompanyId,
                                    defaultPaymentMethod: formData.defaultPaymentMethod,
                                    clientEntityId: formData.clientEntityId,
                                    partnerRoles: formData.partnerRoles,
                                    services: formData.services,
                                    milestoneLogs: milestoneLogsForApply,
                                  };
                                  
                                  operationsInProgress.current++;
                                  setOperationInProgress(true);
                                  const updated = await applyMilestoneTransition({
                                    contract: contractForApply,
                                    milestoneId: milestone.id,
                                    nextStatus: next,
                                    entities,
                                    financeConfig,
                                      createdBy: currentUser?.uid || 'system',
                                    getEffectiveClientAvailable: (clientId) => Number(getEffectiveClientAvailable(clientId, milestoneLogsForApply) || 0),
                                    persistUpdate: async (updatedContract) => {
                                      if (editingContract?.id) {
                                      // If first time invoicing a Scheduled milestone, mark all one-time services as applied
                                      let servicesUpdate = null;
                                      if (next === 'Invoiced') {
                                        const currentMilestoneId = milestone.id;
                                        const hasUnappliedOneTime = (Array.isArray(formData.services) ? formData.services : []).some(
                                          (s) => normalizeFrequencyValue(s?.frequency) === 'one-time' && !s?.oneTimeApplied
                                        );
                                        if (hasUnappliedOneTime) {
                                          servicesUpdate = (Array.isArray(formData.services) ? formData.services : []).map((s) => {
                                            if (normalizeFrequencyValue(s?.frequency) === 'one-time' && !s?.oneTimeApplied) {
                                              return { ...s, oneTimeApplied: true, oneTimeAppliedMilestoneId: currentMilestoneId };
                                            }
                                            return s;
                                          });
                                        }
                                      }
                                      const mergedMilestonesForPersist = preserveInvoiceMetadata(
                                        preserveInvoiceMetadata(updatedContract.milestoneLogs, previousMilestoneLogs),
                                        getLatestSavedMilestoneLogs(editingContract.id)
                                      );
                                      const updates = servicesUpdate
                                        ? { milestoneLogs: mergedMilestonesForPersist, services: servicesUpdate }
                                        : { milestoneLogs: mergedMilestonesForPersist };
                                      await updateDoc(doc(db, 'contracts', editingContract.id), updates);
                                      }
                                    },
                                  });
                                // Reflect changes locally, including one-time service application when invoiced
                                let nextServices = formData.services;
                                if (next === 'Invoiced') {
                                  const currentMilestoneId = milestone.id;
                                  const hasUnappliedOneTime = (Array.isArray(formData.services) ? formData.services : []).some(
                                    (s) => normalizeFrequencyValue(s?.frequency) === 'one-time' && !s?.oneTimeApplied
                                  );
                                  if (hasUnappliedOneTime) {
                                    nextServices = (Array.isArray(formData.services) ? formData.services : []).map((s) => {
                                      if (normalizeFrequencyValue(s?.frequency) === 'one-time' && !s?.oneTimeApplied) {
                                        return { ...s, oneTimeApplied: true, oneTimeAppliedMilestoneId: currentMilestoneId };
                                      }
                                      return s;
                                    });
                                  }
                                }
                                const mergedMilestonesLocal = preserveInvoiceMetadata(
                                  preserveInvoiceMetadata(updated.milestoneLogs, previousMilestoneLogs),
                                  getLatestSavedMilestoneLogs(editingContract?.id)
                                );
                                setFormData({ ...formData, milestoneLogs: mergedMilestonesLocal, services: nextServices });
                                  setSnackbar({ open: true, message: `Status updated to ${next}`, severity: 'success' });
                                } catch (err) {
                                  setSnackbar({ open: true, message: String(err?.message || 'Failed to update status'), severity: 'warning' });
                                } finally {
                                  operationsInProgress.current--;
                                  if (operationsInProgress.current === 0) {
                                    setOperationInProgress(false);
                                  }
                                }
                              }}
                              variant="outlined"
                            >
                              <MenuItem value="Scheduled" disabled={milestone.status !== 'Scheduled'}>Scheduled</MenuItem>
                              <MenuItem value="Invoiced" disabled={!(milestone.status === 'Invoiced' || (allowedMilestoneTransitions[milestone.status] || []).includes('Invoiced'))}>Invoiced</MenuItem>
                              <MenuItem value="Paid" disabled={!(((allowedMilestoneTransitions[milestone.status] || []).includes('Paid')) || milestone.status === 'Paid') || !canEditPayments}>Paid</MenuItem>
                              <MenuItem value="Partial" disabled={!(((allowedMilestoneTransitions[milestone.status] || []).includes('Partial')) || milestone.status === 'Partial') || !canEditPayments}>Partial</MenuItem>
                              <MenuItem value="Cancelled" disabled={!(milestone.status === 'Cancelled' || (allowedMilestoneTransitions[milestone.status] || []).includes('Cancelled'))}>Cancelled</MenuItem>
                              <MenuItem value="Skip" disabled={!(milestone.status === 'Skip' || (allowedMilestoneTransitions[milestone.status] || []).includes('Skip'))}>Skip</MenuItem>
                            </Select>
                          </FormControl>
                        </TableCell>
                        
    
                      </TableRow>
                    ))}
                    {formData.milestoneLogs.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} sx={{ textAlign: 'center', py: 3 }}>
                          <Typography color="text.secondary">
                            No milestones added yet. Click "Add Milestone" to get started. When you complete milestones, records will be created automatically.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>


            </TabPanel>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} disabled={saving || operationInProgress}>Close</Button>
          <Button onClick={handleSave} variant="outlined" disabled={saving || operationInProgress}>
            {saving ? <CircularProgress size={24} /> : 'Save'}
          </Button>
          {!editingContract && (
            <Button onClick={handleSubmit} variant="contained" disabled={saving}>
              {saving ? <CircularProgress size={24} /> : 'Create'}
            </Button>
          )}
        </DialogActions>
      </Dialog>


      {/* Snackbar for notifications */}
      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>

      {/* Backdrop for loading */}
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading || saving}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </Container>
  );
}
